import { frameDisplayType, mergeInitialHistory } from './interactive-state';
import { flowButtonPayload, quickMessageText, conversationRowTitle, resolveServiceTitle } from '../widget/launcher-engagement.js';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import * as store from 'store';
import { defaultConfiguration } from '../widget/default-configuration';
import MessageArea from './message-area';
import PushManager from './push-manager';
import ApiService from '../services/api';
import WebSocketService from '../services/websocket';
import RealtimeService from '../services/realtime';
import nativeBridge, { openLink } from '../widget/native-bridge.js';
import { ensureMediaPermission } from '../widget/permission-bridge.js';
import PermissionGuidanceBar from '../widget/permission-guidance-bar.js';
import { VoiceRecorderSession, formatVoiceDuration } from './voice-recorder.js';
import VoicePlayer from './voice-player.js';
import LcIcon from '../widget/lc-icon.js';
// Lazy-load the audio/video call UI (Cloudflare RealtimeKit SDK is heavy and its
// mic/cam permission listeners can crash outside a session). Deferred via a
// separate async chunk — only fetched when a call actually starts (first render
// of <RealtimeCall/>). Chunk loads from output.publicPath (cdn.lane.chat/js/).
const RealtimeCall = React.lazy(() => import('./realtime-call-new'));

// 通话 UI 专用错误边界。没有它，RTK 懒加载 chunk 拉取失败（CDN 不通/部署窗口）
// 或 CallUI 渲染期异常会沿 React 树一路炸掉，把整个聊天 widget 卸载成白屏。
// 兜住后交给 onFatal 收尾（发 call_end 止铃 + 清通话状态 + 消息流提示）。
class CallErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    componentDidCatch(error) {
        console.error('[Call] call UI crashed, containing failure:', error);
        try { this.props.onFatal && this.props.onFatal(error); } catch (e) { /* 收尾失败也不再抛 */ }
    }
    render() {
        return this.state.failed ? null : this.props.children;
    }
}
import { t, locales, pickLocalized } from '../i18n';
import { getLang } from '../i18n';
import { debugLog } from '../debug';
import { resolveInitialChatId, shouldFallBackToDefaultChat } from './chat-navigation';
import { resolveEmptyStateIdentity, isIdentityAnswer, chatEmptyStateVisible } from './empty-state-identity';
import { isTrustedPostMessage, collectAllowedHostOrigins } from './postmessage-trust';
import { createEmbedBridge } from './embed-bridge';
import { resolveThemeMode, resolveEffectiveTheme } from '../widget/theme-color';
import {
    resolveConfiguredQuestions,
    isKnowledgeFallbackEnabled,
    resolveKnowledgePills,
    KB_PILL_MAX,
} from './suggest-pills';

// ============ 消息区滚动状态机的两个阈值（刻意分开，永远不要合并成一个）============
//
// STICK_TO_BOTTOM_PX —— 「用户是否严格吸在底部」。只有这个判定为真时，系统才被允许
//   把 scrollTop 改写到底部（内容高度补偿 / 新消息跟随）。必须极严格：8px 只覆盖
//   subpixel 取整和 1 行内的浮动误差。历史上这里用的是 50/100px 的宽松值，导致
//   "用户往上滚了一点点仍被判成贴底" → 图片加载、typing 气泡出现/消失、建议 pill
//   显隐等任何高度变化都会把人拽回底部，就是本次要修的 bug。
const STICK_TO_BOTTOM_PX = 8;
//
// JUMP_BUTTON_PX —— 「滚动到底部」浮动按钮的显隐阈值。纯 UI 观感（离底太近时按钮
//   闪现很吵），和上面的贴底判定没有任何关系，绝不可复用同一个常量。
const JUMP_BUTTON_PX = 120;
//
// 一次程序化"跟随到底"动画的预算窗口。窗口内的高度变化重新把目标顶到底部，而不是
// 做锚定补偿（否则会把正在进行的平滑滚动打断在半路）。
const AUTO_FOLLOW_WINDOW_MS = 800;

export default class Chat extends Component {
    constructor(props) {
        super(props);
        this.autoResponseState = 'pristine'; // pristine, set or canceled
        this.autoResponseTimer = 0;
        this.chat_id = false;
        this.client_id = false;
        // 深链目标（widget.html ?view=chat&chat_id=N → props.initialChatId，
        // 一路从 widget.js → chat-frame.js 原样透传下来）。_defaultChatId 是
        // 服务端 /v1/chat 在同一次响应里选好的默认会话——深链目标不属于本访客时
        // （loadInitialMessages 收到服务端 404）的优雅回退目标。见 _applyInitResponse
        // / loadInitialMessages。
        this._defaultChatId = null;
        this._initialChatIdConsumed = false;
        this.conf = defaultConfiguration;
        if (props.appId) {
            this.conf = { ...this.conf, app_id: props.appId };
        }
        store.enabled = false;
        // ChatFrame（widget.html fill/centered、iframe、原生 WebView 宿主）传的是
        // 真布尔值 isMobileBoolean；只认字符串 "true" 会让 `true == "true"` 判假
        // （JS 用 == 比较 boolean 和 string 时把 boolean 转数字，"true" 转数字是
        // NaN，永远不相等）——之前 this.isMobile 在这条路径下恒为 false，导致
        // .mobile-area/.pc-area 选错（键盘适配用的 handleVisualViewportResize
        // 也因此从未触发，键盘弹起时 composer 不跟手、消息不跟着回到底部）。
        // 两种写法都认，兼容可能存在的字符串来源。
        this.isMobile = props.isMobile === true || props.isMobile === "true";
        this.state = {
            chat_id: false,
            isMobile: props.isMobile ?? false,
            fromData: props.fromData ?? {},
            imagePreviewUrl: null,
            imageFile: null,
            uploading: false,
            uploadProgress: 0,
            showSendButton: false, // New state to control the visibility of the send button
            showEmojiPicker: false, // State to control emoji picker visibility
            showGiphy: false,
            showCallActions: false, // 通话按钮：折叠成一个，点击向右展开语音/视频/共享
            messages: [],
            textareaRows: 1,
            mockGifs: [],
            gifStatus: 'idle', // idle | loading | ready | error —— GIF 面板的加载/空/失败态
            gifLoadingMore: false, // 分页：正在拉下一页（底部转圈）
            gifHasMore: true, // 分页：服务端说这批取满了才可能还有下一页
            replyToMessage: null, // 新增：要回复的消息
            showReplyMenu: false, // 新增：是否显示回复菜单
            scrollToMessageId: null, // 新增：要滚动到的消息ID
            loadingHistory: false, // 新增：是否正在加载历史消息
            // 空态卡的身份三件套（头像/名称/欢迎语），与原生两端 chatEmptyStateOf
            // 同构。三态语义（别合并成一个字段，"还没定"和"定成没有"必须分得开）：
            //   emptySettled=false            → 还没定：画中性骨架，绝不画卡
            //   emptySettled=true, 有 identity → 画卡，且此后冻结不再变
            //   emptySettled=true, identity=null → 定成"没有卡"：这一格空着（终态）
            // 不变式见 empty-state-identity.js 顶部。
            emptyIdentity: null,
            emptySettled: false,
            hasMoreMessages: false, // 新增：是否还有更多消息（首次 load 回来按 data.has_more 设，避免空会话误显示"向上加载"）
            isAtBottom: true, // 新增：是否在底部
            farFromBottom: false, // 离底部超过阈值（~120px）→ 浮出"滚动到底部"按钮
            suggestPills: [], // 聊天空态建议问题 pill（商户配置 > 知识库标题回落；取不到不显示）
            unreadCount: 0, // 新增：未读消息数
            // 三个通知渠道的真实绑定状态（/v1/chat init 下发）——
            // 通知卡片据此把每一行画成「未开启 / 已开启」，不靠前端猜。
            bindingStatus: { webpush: false, telegram: false, email: false },
            // Realtime 通话相关状态
            showCallUI: false, // 是否显示通话界面
            incomingCall: null, // 来电信息
            callPeerAccepted: false, // 发起方收到 WS call_accepted（对端已接听，媒体可能还没通）
            // Toolbar 滚动状态
            showLeftArrow: false, // 是否显示左箭头
            showRightArrow: false, // 是否显示右箭头
            // 输入状态
            agentTyping: false, // 客服正在输入
            aiStream: null,     // AI 流式预览的临时气泡 { id, text }
            // 会话列表
            // initialViewMode 由 widget 在导航时一次性传入（home 的「查看全部对话」
            // → 'chatList'）。挂载即渲染目标子视图，不再"先画一帧会话再跳列表"。
            viewMode: props.initialViewMode === 'chatList' ? 'chatList' : 'chat', // 'chat' | 'chatList'
            chatList: [],
            chatListLoading: false,
            chatClosed: false, // 当前会话是否已关闭
            banned: false, // 访客被封禁（后端 banned 事件），禁用输入
            emailGateLocked: false, // required ask_email 未提交前锁定输入区（widget 端 gating）
            permissionGuidance: null, // 原生容器里麦克风/摄像头权限被拒 → {kind: 'microphone'|'camera'}，展示引导条；null = 不展示
            voiceRecording: null, // 正在录音 → {seconds}；null = 未在录
            voicePreview: null,   // 录完待发 → {blob, mimeType, seconds, url}；null = 无预览
            voiceSending: false,  // 语音上传/发送进行中（预览条的发送按钮转 loading）
            appConfig: null, // 面板下发的应用配置（state 化以触发渲染）
        };

        this.mainContainer = null; // 主容器ref
        this.chatContainer = null; // Initialize a ref here
        this._voiceRecorder = null; // 当前 VoiceRecorderSession 实例，非 state（不需要触发渲染）
        this.texContainerRef = React.createRef(); // Reference for the textarea
        this.textareaRef = React.createRef(); // Reference for the textarea
        this.emojiPickerRef = React.createRef(); // Create a ref
        this.emojiButtonRef = React.createRef(); // 表情切换按钮，click-outside 需排除它
        this.gifPickerRef = React.createRef(); // Create a ref
        this.hasInitialScrolled = false; // 标记是否已经完成首次滚动
        // 滚动判断激活闸：首次渲染完成 + 初始滚动落位之后才开始处理 scroll 事件。
        // 否则加载期容器高度不稳定（scrollHeight 未撑开），"距底部>120px"必然误判，
        // 「滚动到底部」按钮会在消息还在加载时就冒出来。
        this._scrollWatchReady = false;
        this.toolbarRef = React.createRef(); // toolbar滚动容器ref

        this.handleInputChange = this.handleInputChange.bind(this);
        this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
        this.handleEmojiSelect = this.handleEmojiSelect.bind(this);
        this.adjustTextareaHeight = this.adjustTextareaHeight.bind(this);
        this.handleGiphySearchTermChange = this.handleGiphySearchTermChange.bind(this);
        this.toggleGiphy = this.toggleGiphy.bind(this);
        this.handleReplyMessage = this.handleReplyMessage.bind(this);
        this.cancelReply = this.cancelReply.bind(this);
        this.scrollToReplyMessage = this.scrollToReplyMessage.bind(this);
        
        // 消息队列，用于存储断线时的消息
        this.messageQueueKey = props.messagesKey ? `${props.messagesKey}-queue` : 'lane-chat-messages-queue';
        this.messageQueue = [];
        this.isReconnecting = false;
        this.isConnecting = false;
        this.wsHandlersRegistered = false;
        
        // GIF搜索防抖计时器
        this.giphySearchDebounceTimer = null;
        
        // 标记是否应该自动滚动到底部
        this.shouldAutoScroll = true;
        
        // 保存滚动计时器，以便在需要时清除
        this.scrollTimers = [];
        
        // 标记是否是初始加载
        this.isInitialLoad = true;
        
        // 注意：消息队列现在由 WebSocketService 管理
        // this.messageQueue 只用于 UI 状态显示
    }

    clearScrollTimers = () => {
        this.scrollTimers.forEach(timer => clearTimeout(timer));
        this.scrollTimers = [];
    }

    // ==================== 消息区滚动状态机 ====================
    //
    // 只有三种力量允许改写 chatContainer.scrollTop，其它任何代码路径都不许直接碰：
    //
    //   A. 显式用户/视图意图 —— 点「回到底部」按钮、首屏落位、重开面板恢复位置、
    //      加载更多历史时的位置保持。这些走各自的专用函数。
    //   B. 自动跟随到底（_maybeAutoScroll）—— 闸门只认两件事：用户此刻严格贴底
    //      (_isStuckToBottom)，或者用户刚刚自己发了一条消息(_followOnce)。
    //   C. 高度补偿（_compensateForHeightChange）—— 用户在看历史时，内容高度变化
    //      （图片加载完、typing 气泡出现/消失、建议 pill 显隐、卡片撑开）必须做
    //      锚定补偿：把变化前的第一条可见消息重新摆回原来的视口位置。不是"什么都
    //      不做"——什么都不做在"高度变化发生在视口上方"时依然会让内容跳。
    //
    // 判定一律用 DOM 实测，不信任何缓存布尔值（缓存值总会在某条路径上过期）。

    // 严格贴底判定 —— 状态机里所有"能不能自动滚到底"的裁决唯一依据。
    _isStuckToBottom = () => {
        const c = this.chatContainer;
        if (!c) return false;
        // display:none 下 scrollTop/scrollHeight 读数不可靠，按"不贴底"处理更安全：
        // 不可见时本来也不该有任何自动滚动。
        if (c.offsetParent === null) return false;
        return (c.scrollHeight - c.scrollTop - c.clientHeight) <= STICK_TO_BOTTOM_PX;
    }

    // 用户刚自己发了消息 —— 一次性放行自动跟随（只烧一次）。
    _armFollowOnce = () => { this._followOnce = true; }

    _consumeFollowOnce = () => {
        if (!this._followOnce) return false;
        this._followOnce = false;
        return true;
    }

    // 自动跟随的唯一闸门。返回是否真的滚了。
    // opts.instant —— 布局补偿类调用用 instant，避免动画。
    _maybeAutoScroll = (opts = {}) => {
        if (!this.chatContainer) return false;
        const forced = this._consumeFollowOnce();
        if (!forced && !this._isStuckToBottom()) return false;
        this._beginAutoFollow();
        if (opts.instant) this.instantScrollToBottom();
        else this.scrollToBottom();
        return true;
    }

    _beginAutoFollow = () => { this._autoFollowUntil = Date.now() + AUTO_FOLLOW_WINDOW_MS; }

    _isAutoFollowing = () => Date.now() < (this._autoFollowUntil || 0);

    // 任何真实的用户滚动输入立刻作废跟随窗口 —— 用户抢方向盘时系统必须马上松手。
    _cancelAutoFollow = () => {
        this._autoFollowUntil = 0;
        this._followOnce = false;
    }

    // 记录"变化前第一条可见消息 + 它相对视口顶的偏移"。
    _captureScrollAnchor = () => {
        const c = this.chatContainer;
        if (!c || c.offsetParent === null) return null;
        const nodes = c.querySelectorAll('li.chat-msg');
        const top = c.scrollTop;
        for (let i = 0; i < nodes.length; i++) {
            const el = nodes[i];
            // 第一条底边越过视口顶的元素 = 第一条可见消息
            if (el.offsetTop + el.offsetHeight > top) {
                return { el, delta: el.offsetTop - top };
            }
        }
        return null;
    }

    // 把锚点消息摆回它原来的视口位置。元素已被卸载则放弃（不乱动 scrollTop）。
    _restoreScrollAnchor = (anchor) => {
        const c = this.chatContainer;
        if (!c || !anchor || !anchor.el || !anchor.el.isConnected) return;
        const want = anchor.el.offsetTop - anchor.delta;
        if (Math.abs(c.scrollTop - want) < 1) return;
        const prev = c.style.scrollBehavior;
        c.style.scrollBehavior = 'auto';
        c.scrollTop = want;
        c.style.scrollBehavior = prev || 'smooth';
    }

    // 任何内容高度变化（React 重渲染 / ResizeObserver）之后调用。
    // snapshot 由变化"之前"采集：{ stuck, anchor }。
    _compensateForHeightChange = (snapshot) => {
        const c = this.chatContainer;
        if (!c || !snapshot) return;
        if (c.offsetParent === null) return;
        // 首屏落位 / 重开面板恢复位置期间由那两条路径独占 scrollTop，别插手。
        if (this._restorePending) return;
        if (!this._scrollWatchReady) return;

        // 正在做程序化"跟随到底"动画：重新把目标顶到最新底部，保持动画连续。
        if (this._isAutoFollowing()) {
            c.scrollTop = c.scrollHeight;
            return;
        }
        if (snapshot.stuck) {
            // 变化前严格贴底 → 保持贴底（instant，补偿不该有动画）
            this.instantScrollToBottom();
            return;
        }
        // 用户在看历史 → 锚定补偿，视觉上一动不动
        this._restoreScrollAnchor(snapshot.anchor);
    }

    // ResizeObserver 用的"变化前"快照。RO 回调只能在高度已经变了之后才跑，那时实测
    // 的 stuck 已被污染（内容变高、scrollTop 不动 → 看起来就不贴底了），所以必须
    // 在变化发生前持续维护一份快照。用 rAF 节流，滚动事件很密。
    _refreshRoSnapshot = () => {
        if (this._roSnapshotRaf) return;
        this._roSnapshotRaf = requestAnimationFrame(() => {
            this._roSnapshotRaf = null;
            if (!this.chatContainer) return;
            this._roSnapshot = {
                stuck: this._isStuckToBottom(),
                anchor: this._captureScrollAnchor(),
            };
        });
    }

    // React 渲染前采样：typing 气泡、建议 pill、新气泡、卡片展开等所有由渲染引起的
    // 高度变化都会经过这里，是最高频的高度变化来源。
    getSnapshotBeforeUpdate() {
        if (!this.chatContainer) return null;
        if (this.chatContainer.offsetParent === null) return null;
        return { stuck: this._isStuckToBottom(), anchor: this._captureScrollAnchor() };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (snapshot) this._compensateForHeightChange(snapshot);
    }


    handleReplyMessage = (messageId, message) => {
        this.setState({
            replyToMessage: { id: messageId, ...message },
            showReplyMenu: true
        }, () => {
            // 延迟一下以确保DOM更新完成
            setTimeout(() => {
                this.adjustTextareaHeight();
            }, 50);
        });
        
        // 显示回复菜单后自动关闭
        setTimeout(() => {
            this.setState({ showReplyMenu: false });
        }, 2000);
    }
    
    cancelReply = () => {
        this.setState({ replyToMessage: null }, () => {
            // 延迟一下以确保DOM更新完成
            setTimeout(() => {
                this.adjustTextareaHeight();
            }, 50);
        });
    }

    // A launcher quick button pressed before the chat was ready. Returns false while it
    // should be retried. Flow buttons take the button click path and message buttons the
    // normal send path, so flows and AI treat them like any other visitor input.
    sendQuickAction = (button) => {
        if (this.state.banned) return true;
        if (!this.chat_id || !this._wsRegistered || !WebSocketService.isConnected) return false;
        if (this.state.chatClosed) {
            if (!this._quickActionNewChat) {
                this._quickActionNewChat = true;
                Promise.resolve(this.createNewChat()).finally(() => { this._quickActionNewChat = false; });
            }
            return false;
        }
        if (button.action === 'flow') this.handleButtonClick(flowButtonPayload(button));
        else this.sendMessage(quickMessageText(button));
        return true;
    }

    handleButtonClick = (button, messageId) => {
        // 服务端按"该访客当前打开的会话"落消息：已关闭的会话没有 open 记录，点击会静默新建
        // 一个会话，访客这边什么都看不到。封禁时消息直接被丢。与输入框同一套门控。
        if (this.state.chatClosed || this.state.banned) return;
        // 发送用户的选择作为文本消息
        const textMessage = {
            type: 'message',
            text: button.text,
            from: 'visitor',
            appId: this.props.appId,
            userId: this.client_id || this.fp_visitor,
            chatId: this.chat_id,
            clientId: this.client_id,
            visitorName: this.props.conf?.visitorName,
            timestamp: new Date().toISOString(),
            metadata: {
                button_click: {
                    button_id: button.id,
                    // Legacy flow buttons only carry {id,text} — fall back to
                    // id so the backend button_click trigger schema is satisfied
                    button_value: (button.value !== undefined && button.value !== null && button.value !== '') ? button.value : button.id,
                    button_text: button.text,
                    // 选的是哪条菜单，服务端据此回填已选状态（见 InteractiveStateResolver）。
                    // 只传真实消息 id，渲染层兜底的 'msg-3' 没有意义。
                    ...(Number.isFinite(Number(messageId)) ? { message_id: Number(messageId) } : {})
                }
            }
        };

        // 立即在本地显示用户的选择
        const tempId = 'temp-' + Date.now();
        // client_temp_id 让 ack 回显能精确认领这一条临时气泡（见 handleWebSocketMessage）。
        textMessage.metadata.client_temp_id = tempId;
        this.writeToMessages({
            ...textMessage,
            id: tempId,
            status: 'sending'
        });

        // 发送到服务器
        WebSocketService.send(textMessage);
    }

    // Embed 事件转 postback。embed iframe 通过 bridge 上报 event/notify/submit
    // (如投票完成 poll_vote)→ 这里转成一条**访客消息**发往服务端,走与 button_click
    // 完全相同的不可信访客输入通道(WebSocketService.send + metadata):
    //   - metadata.embed_event { source:'embed_event', name, data, embed_message_id }
    //     标记来源与目标 embed 消息 id;服务端据此校验(投票选项合法性/一人一票
    //     由服务端裁,不信客户端)并把权威完成态 embed_state 回写到该 embed 消息,
    //     后续拉历史时富化下发 → 组件渲染"已完成"只读态。
    //   - text 给一句人读摘要(坐席可见);data.label 优先,否则用事件名。
    // 不新开信任通道:和 handleButtonClick 一样是访客帧,服务端 sanitize/限流照旧。
    handleEmbedEvent = (entry, name, data) => {
        if (!name) return;
        const payload = (data && typeof data === 'object') ? data : {};
        const label = typeof payload.label === 'string' && payload.label ? payload.label : name;
        const embedMessageId = entry && (entry.embedMessageId != null ? entry.embedMessageId : null);
        const message = {
            type: 'message',
            text: String(label).slice(0, 200),
            from: 'visitor',
            appId: this.props.appId,
            userId: this.client_id || this.fp_visitor,
            chatId: this.chat_id,
            clientId: this.client_id,
            visitorName: this.props.conf?.visitorName,
            timestamp: new Date().toISOString(),
            metadata: {
                embed_event: {
                    source: 'embed_event',
                    name: String(name).slice(0, 64),
                    data: payload,
                    embed_message_id: embedMessageId,
                },
            },
        };
        const tempId = 'temp-' + Date.now();
        message.metadata.client_temp_id = tempId;
        this.writeToMessages({ ...message, id: tempId, status: 'sending' });
        WebSocketService.send(message);
    }

    // 当前生效主题(light|dark)—— 喂给 embed init/theme,让 embed 跟随暗色。
    _currentEmbedTheme = () => {
        const conf = this.getMessageAreaConf();
        try {
            const mode = resolveThemeMode({
                darkModeEnabled: conf.darkModeEnabled,
                explicitTheme: conf.explicitTheme,
                serverThemeMode: conf.themeMode,
            });
            const prefersDark = typeof window !== 'undefined' && window.matchMedia
                ? window.matchMedia('(prefers-color-scheme: dark)').matches : false;
            return resolveEffectiveTheme(mode, prefersDark);
        } catch (e) {
            return 'light';
        }
    }

    // 向在册 embed 广播 theme 帧。**去重**:同一 theme 值不重复下发(规范 §5.8),
    // 否则每次 appConfig 局部更新都会给每个 embed 注一次没有内容变化的帧。
    _broadcastEmbedTheme = () => {
        if (!this._embedBridge) return;
        const next = this._currentEmbedTheme();
        if (next === this._lastBroadcastEmbedTheme) return;
        this._lastBroadcastEmbedTheme = next;
        try { this._embedBridge.broadcastTheme(next); } catch (e) { /* best-effort */ }
    }

    // 系统 prefers-color-scheme 变化 → 同样要广播(规范 §5.8)。
    // 此前只有 applyAppConfig 那条路会广播,于是 auto 档下用户在会话中途切系统
    // 暗色,宿主容器跟着变了、embed 里的内容还停在亮色 —— 并排看非常明显。
    _handleSystemThemeChange = () => {
        this._broadcastEmbedTheme();
    }

    // 处理邮箱提交（用于 ask_email Flow 节点）
    handleEmailSubmit = async (email) => {
        // 发送邮箱作为消息
        const textMessage = {
            type: 'message',
            text: email,
            from: 'visitor',
            appId: this.props.appId,
            userId: this.client_id || this.fp_visitor,
            chatId: this.chat_id,
            clientId: this.client_id,
            visitorName: this.props.conf?.visitorName,
            timestamp: new Date().toISOString(),
            metadata: {
                email_submit: {
                    email: email
                }
            }
        };

        // 立即在本地显示
        const tempId = 'temp-' + Date.now();
        // client_temp_id 让 ack 回显能精确认领这一条临时气泡（见 handleWebSocketMessage）。
        textMessage.metadata.client_temp_id = tempId;
        this.writeToMessages({
            ...textMessage,
            id: tempId,
            status: 'sending'
        });

        // 发送到服务器
        WebSocketService.send(textMessage);

        // required ask_email 场景：提交合法邮箱后立即解锁输入区
        if (this.state.emailGateLocked) {
            this.setState({ emailGateLocked: false });
        }

        return Promise.resolve();
    }

    // required ask_email 未提交 → 锁定输入区。
    // 历史接口在访客已留邮箱时会给 ask_email 的 service_data 打 submitted:true，
    // 所以刷新后已提交过的访客不会被重复锁定。
    _computeEmailGate = (messages) => {
        if (!Array.isArray(messages)) return false;
        return messages.some(m =>
            m && m.service_data &&
            m.service_data.type === 'ask_email' &&
            m.service_data.required === true &&
            !m.service_data.submitted
        );
    }

    // "Powered by Lane.Chat" 链接（带 utm，href 与 openLink 走同一 URL）
    _poweredByHref = () => {
        return `https://lane.chat/?utm_source=widget&utm_medium=referral&utm_campaign=powered_by${(this.props.appId || this.appId) ? `&utm_content=${encodeURIComponent(this.props.appId || this.appId)}` : ''}`;
    }

    // 处理富消息表单提交（rich form 消息，POST {apiUrl}/forms/submit）
    handleFormSubmit = async ({ message_id, form_id, values }) => {
        try {
            await ApiService.submitForm({
                app_id: this.props.appId,
                chat_id: this.chat_id,
                visitor_id: this.client_id || this.fp_visitor,
                message_id,
                form_id,
                values,
            });
        } catch (error) {
            console.warn('[Chat] Form submit failed:', error);
        }
    }

    scrollToReplyMessage = () => {
        if (this.state.replyToMessage && this.state.replyToMessage.id) {
            // 设置要滚动到的消息ID
            this.setState({ scrollToMessageId: this.state.replyToMessage.id });
            
            // 延迟执行，让MessageArea组件有时间处理
            setTimeout(() => {
                this.setState({ scrollToMessageId: null });
            }, 100);
        }
    }
    
    handleImageLoad = () => {
        // 图片解码完成会撑高列表。走状态机：严格贴底才跟随，否则由 ResizeObserver
        // 的锚定补偿把用户的阅读位置钉住（这里绝不能自己动 scrollTop）。
        this._maybeAutoScroll({ instant: true });
    }
    
    handleInputChange = (e) => {
        this.setState({
            showSendButton: e.target.value.length > 0 || this.state.imageFile !== null
        });
        this.adjustTextareaHeight();
        // 向客服广播"访客正在输入"（节流：最多每 2s 发一次 true，停止输入 2.5s 后发 false）
        if (e.target.value.length > 0) this.notifyTyping();
    }

    // 发送访客输入状态到服务端 → 中转给该会话的客服
    sendTypingSignal = (isTyping) => {
        if (!this.chat_id) return;
        try {
            WebSocketService.send({ type: 'typing', chat_id: this.chat_id, typing: !!isTyping });
        } catch (e) { /* ws 未就绪，静默 */ }
    };

    notifyTyping = () => {
        const now = Date.now();
        if (!this._lastTypingSent || now - this._lastTypingSent > 2000) {
            this._lastTypingSent = now;
            this.sendTypingSignal(true);
        }
        if (this._typingStopTimer) clearTimeout(this._typingStopTimer);
        this._typingStopTimer = setTimeout(() => {
            this._lastTypingSent = 0;
            this.sendTypingSignal(false);
        }, 2500);
    };

    stopTyping = () => {
        if (this._typingStopTimer) { clearTimeout(this._typingStopTimer); this._typingStopTimer = null; }
        if (this._lastTypingSent) { this._lastTypingSent = 0; this.sendTypingSignal(false); }
    };

    toggleEmojiPicker = (e) => {
        // 阻止事件冒泡到 Widget 的 onClick
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        const willShow = !this.state.showEmojiPicker;
        this._syncComposerVar();   // 弹层下边界跟输入区实测高度走，开之前先量准
        debugLog('[Chat] toggleEmojiPicker called, willShow:', willShow);
        debugLog('[Chat] this.Picker:', this.Picker);
        debugLog('[Chat] this.emojiPortalContainer:', this.emojiPortalContainer);

        this.setState(
            { showGiphy: false, showEmojiPicker: willShow },
            () => {
                debugLog('[Chat] setState callback, showEmojiPicker:', this.state.showEmojiPicker);

                setTimeout(
                    ()=>{
                        this.adjustTextareaHeight();
                    },
                    100
                )
                setTimeout(
                    ()=>{
                        this.adjustTextareaHeight();
                    },
                    300
                )

                // Add/remove click outside listener for emoji picker.
                // 关键：监听目标必须在 widget 的【闭合 shadow】内部（mainContainer），
                // 而不是 document。因为 laneChatRoot 是 closed shadow，document 上的监听器
                // 只能看到 event.target 被重定向成 laneChatRoot 宿主、composedPath 也截断到宿主，
                // 于是 handleClickOutsideEmojiPicker 对【任何】点击都算作"外部"→ mousedown
                // 直接关掉面板，点 emoji 的 click 还没触发 onEmojiSelect 就被卸载 → 点哪都关、
                // 且插不进输入框。挂到 shadow 内的 mainContainer 后，点击 target 是真实节点，
                // emojiPick.contains / composedPath 才能正确判断"内部"。
                if (willShow) {
                    setTimeout(() => {
                        const tgt = this.mainContainer || document;
                        this._emojiOutsideTarget = tgt;
                        tgt.addEventListener('mousedown', this.handleClickOutsideEmojiPicker);
                    }, 100);
                } else {
                    (this._emojiOutsideTarget || document).removeEventListener('mousedown', this.handleClickOutsideEmojiPicker);
                }
            }
        );

    }

    toggleGiphy = (e) => {
        // 阻止事件冒泡到 Widget 的 onClick
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        this._syncComposerVar();   // 同上
        this.setState(
            { showEmojiPicker: false, showGiphy: !this.state.showGiphy },
            () => {
                setTimeout(() => { this.adjustTextareaHeight(); }, 100);
                setTimeout(() => { this.adjustTextareaHeight(); }, 300);
                // 打开面板且尚无 GIF → 拉 trending 作为默认展示（空关键词=trending）
                if (this.state.showGiphy && (!this.state.mockGifs || this.state.mockGifs.length === 0)) {
                    this.sendGifSearch('');
                }
            }
        );
    }

    // 通话按钮组：折叠→点击向右展开语音/视频/共享；再点折叠。
    toggleCallActions = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (e && e.preventDefault) e.preventDefault();
        this.setState({ showCallActions: !this.state.showCallActions });
    }

    // 选中某个通话动作后先折叠回去，再发起，避免展开态残留
    runCallAction = (fn) => {
        this.setState({ showCallActions: false });
        if (typeof fn === 'function') fn();
    }

    // 统一发起 GIF 请求（kw 为空 = trending 默认展示）。
    // 结果走 WS 回推，没有 HTTP 状态码可看 —— 用一个超时把「一直没回」翻译成
    // 可见的失败态，否则面板永远停在骨架/空白上，看起来像挂了。
    sendGifSearch = (kw, offset = 0) => {
        const keyword = (kw || '').trim();
        // 记住这一轮的关键词：翻页请求回来时要拿它对账，否则「翻到第 3 页时改了
        // 搜索词」的旧结果会追加到新列表尾巴上。
        this._gifKeyword = keyword;
        if (offset > 0) {
            // 翻页：列表原地不动，只在底部转圈
            if (this._gifMoreInFlight || !this.state.gifHasMore) return;
            this._gifMoreInFlight = true;
            this.setState({ gifLoadingMore: true });
            clearTimeout(this._gifMoreTimeout);
            this._gifMoreTimeout = setTimeout(() => {
                // 下一页没回来就把转圈收掉，滚动到底还能再试一次，不至于卡死
                this._gifMoreInFlight = false;
                if (this.state.gifLoadingMore) this.setState({ gifLoadingMore: false });
            }, 8000);
        } else {
            // 换关键词/重试：把翻页锁一起清掉，否则上一轮的在途请求会把新列表锁死
            this._gifMoreInFlight = false;
            clearTimeout(this._gifMoreTimeout);
            this.setState({ gifStatus: 'loading', gifLoadingMore: false, gifHasMore: true });
            clearTimeout(this._gifTimeout);
            this._gifTimeout = setTimeout(() => {
                if (this.state.gifStatus === 'loading') this.setState({ gifStatus: 'error' });
            }, 8000);
        }
        try {
            WebSocketService.send({
                kw: keyword,
                offset: offset,
                type: 'gif',
                from: 'visitor',
                attachment: '',
                appId: this.props.appId,
                userId: this.client_id || this.fp_visitor,
                chatId: this.chat_id,
                clientId: this.client_id,
                visitorName: this.props.conf?.visitorName,
            });
        } catch (e) { /* ws 未就绪，静默 */ }
    }

    handleEmojiSelect = (event) => {
        if(event && event.native){
            this.input.value += event.native;
            this.handleInputChange({ target: this.input });
            this.adjustTextareaHeight();
        }
    }

    // Get position for emoji picker portal (inside laneChatRoot but outside Shadow DOM)
    getEmojiPickerPosition = () => {
        // Portal 在 laneChatRoot 内部，使用相对定位
        // Emoji picker 会覆盖整个 chat 区域
        return {
            // 不设置 inline style，让 CSS 控制
        };
    }

    // Handle clicks outside emoji picker to close it
    handleClickOutsideEmojiPicker = (event) => {
        const emojiPick = this.emojiPickerRef.current;
        if (!emojiPick) return;

        // Use composedPath() to see through Shadow DOM boundaries
        const path = event.composedPath ? event.composedPath() : [event.target];
        const emojiBtn = this.emojiButtonRef.current;
        // 表情按钮本身不算"外部"：否则移动端 tap 时 mousedown 会先关闭，
        // 紧接着 click 又 toggle 回开，表现为"点了不关"。让按钮的 click 独占 toggle。
        const isOnEmojiButton = emojiBtn && (path.some(el => el === emojiBtn) || emojiBtn.contains(event.target));
        const isInsideEmojiPicker = path.some(el => el === emojiPick) || emojiPick.contains(event.target);

        if (!isInsideEmojiPicker && !isOnEmojiButton) {
            this.setState({ showEmojiPicker: false });
            (this._emojiOutsideTarget || document).removeEventListener('mousedown', this.handleClickOutsideEmojiPicker);
        }
    }

    handleGifSelect = (gif) => {
        this.setState({ showGiphy: false });
        this.sendGifMessage(gif);
    };

    // 更新toolbar箭头显示状态
    updateToolbarArrows = () => {
        const toolbar = this.toolbarRef.current;
        if (!toolbar) return;

        const scrollLeft = toolbar.scrollLeft;
        const scrollWidth = toolbar.scrollWidth;
        const clientWidth = toolbar.clientWidth;

        // 判断是否可以向左滚动（不在最左边）
        const canScrollLeft = scrollLeft > 0;

        // 判断是否可以向右滚动（不在最右边）
        const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1; // -1 容错

        this.setState({
            showLeftArrow: canScrollLeft,
            showRightArrow: canScrollRight
        });
    };

    // 滚动toolbar
    scrollToolbar = (direction) => {
        const toolbar = this.toolbarRef.current;
        if (toolbar) {
            const scrollAmount = 200; // 每次滚动200px
            const currentScroll = toolbar.scrollLeft;
            const targetScroll = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            toolbar.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });

            // 滚动后更新箭头状态
            setTimeout(() => {
                this.updateToolbarArrows();
            }, 300); // 等待滚动动画完成
        }
    };

    sendGifMessage = (gif) => {
        // 如果当前正在回复某条消息，把 reply_to_id 一起带上 + 发送后清空 reply 状态
        const replyTo = this.state.replyToMessage;
        const messageData = {
            type: 'message',
            attachment: gif,
            text: '',
            from: 'visitor',
            appId: this.props.appId,
            userId: this.client_id || this.fp_visitor,
            chatId: this.chat_id,
            clientId: this.client_id,
            visitorName: this.props.conf.visitorName,
            ...(replyTo && replyTo.id ? {
                reply_to_id: replyTo.id,
                message_type: 'reply',
            } : {}),
        };

        // 使用WebSocketService发送消息
        const sent = WebSocketService.send(messageData);

        // 不论 send 成功与否（失败会进队列重发），UI 立刻清掉 reply 提示
        if (replyTo) {
            this.setState({ replyToMessage: null });
        }

        if (!sent) {
            // 连接断开，WebSocketService 已加入队列
            debugLog('WebSocket not ready, GIF message queued by WebSocketService');

            if (!this.isReconnecting) {
                this.connectWebSocket();
            }

            // 给用户反馈
            this.writeToMessages({
                text: t('gif.sending'),
                from: 'system',
                type: 'temp-status',
                id: 'temp-' + Date.now()
            });
        }

        this.input.value = '';
        this.setState({ showSendButton: false, uploading: false, uploadProgress: 0, imagePreviewUrl: null, imageFile: null });
    };

    handleGiphySearchTermChange = (term) => {
        // 清除之前的计时器
        if (this.giphySearchDebounceTimer) {
            clearTimeout(this.giphySearchDebounceTimer);
        }

        // 设置新的防抖计时器。清空搜索框 = 回到 trending，不再原地卡着上一次
        // 的结果（sendGifSearch('') 就是 trending）。
        this.giphySearchDebounceTimer = setTimeout(() => {
            debugLog('Searching GIFs for:', term);
            this.sendGifSearch((term || '').trim());
        }, 500);
    };
    
    /**
     * 通知渠道卡片的唯一出口。
     *
     * 2026-08 之前这里是三条各自为政的支路：push 弹 PushManager.showPushCard()
     * 的红框卡、telegram 弹 BindingMessage、email 弹 EmailBinding —— 三种视觉、
     * 三套状态、互相不认识，而且都是「再往对话流里塞一条新消息」。
     * 现在统一成一张 NotificationCard（service_data.type=request_binding），
     * 授权与状态都在那张卡里原地完成。
     */
    showNotificationCard = (methods) => {
        this.writeToMessages({
            from: 'system',
            text: '',
            type: 'service',
            service_data: {
                type: 'request_binding',
                binding_methods: methods,
                allow_multiple: methods.length > 1,
            },
            time: new Date(),
        });
    }

    handleServiceAction = (action, data) => {
        switch (action) {
            case 'show_push_binding':
                this.showNotificationCard(['webpush']);
                break;

            case 'show_telegram_binding':
                this.showNotificationCard(['telegram']);
                break;

            case 'show_email_binding':
                this.showNotificationCard(['email']);
                break;
                
            case 'open_game':
                // 打开游戏iframe
                if (data && window.parent !== window) {
                    // 如果在iframe中，向父窗口发送消息
                    window.parent.postMessage({
                        type: 'open_game',
                        url: data
                    }, '*');
                }
                break;
                
            default:
                debugLog('Unknown service action:', action);
        }
    }
    
    // generateTelegramBinding / showEmailBinding 已删除（2026-08 合并）。
    // 它们各自往对话流塞一张风格不同的绑定卡；现在 Telegram 深链生成与邮箱验证
    // 都在 NotificationCard 内部完成，状态原地更新，不再产生额外消息。

    async fetchChat() {
        try {
            const fpPromise = FingerprintJS.load();
            const fp = await fpPromise;
            const result = await fp.get();
            this.fp_visitor = result.visitorId;
            // 使用新的API服务初始化聊天
            // 新签名: (appId, visitorUuid, fingerprint, fromData, legacyToken)
            // 这里没有 uuid（widget.js 已经在 preInit 时建立过身份），传 null 让后端按 fp 兜底
            const data = await ApiService.initChat(this.conf.app_id, null, result.visitorId, this.state.fromData);
            return data;
        } catch (error) {
            console.error("Error fetching data: ", error);
            throw error;
        }
    }

    // ==================== Cookie-based visitor token ====================
    _getVisitorToken() {
        const name = `lane_vt_${this.conf.app_id || this.props.appId || 'default'}`;
        try {
            for (let c of document.cookie.split(';')) {
                c = c.trim();
                if (c.startsWith(name + '=')) return c.substring(name.length + 1);
            }
        } catch {}
        return null;
    }

    _setVisitorToken(token) {
        const name = `lane_vt_${this.conf.app_id || this.props.appId || 'default'}`;
        const expires = new Date(Date.now() + 365 * 86400000).toUTCString();
        document.cookie = `${name}=${token}; expires=${expires}; path=/; SameSite=Lax`;
    }

    // Keep chat_id in localStorage for fast same-session revisits
    _getCachedChatId() {
        try {
            return localStorage.getItem(`lane_chatid_${this.conf.app_id || this.props.appId || 'default'}`) || null;
        } catch { return null; }
    }

    _setCachedChatId(chatId) {
        try {
            if (chatId) localStorage.setItem(`lane_chatid_${this.conf.app_id || this.props.appId || 'default'}`, String(chatId));
        } catch {}
    }

    _applyInitResponse(responseData) {
        if (responseData?.chatId) {
            // 记住服务端这次挑的默认会话——不管深链目标最后用不用得上，都要有
            // 一个"合法"的回退候选（loadInitialMessages 深链 404 时用）。
            this._defaultChatId = responseData.chatId;
            // 深链目标只在挂载后第一次 _applyInitResponse 生效一次：componentDidMount
            // 的三条路径（preInitData 直用 / preInitPromise 等到再用 / 独立兜底 init）
            // 互斥，只有一条会跑，但同一条路径内部有重试 catch 分支也会再调一次
            // _applyInitResponse——一次性开关避免深链目标被反复"抢焦点"，且不影响
            // 之后用户手动切会话（switchToChat 之类）或 preInit 失败重试的正常行为。
            this.chat_id = resolveInitialChatId({
                initialChatId: this.props.initialChatId,
                alreadyConsumed: this._initialChatIdConsumed,
                defaultChatId: responseData.chatId,
            });
            this._initialChatIdConsumed = true;
            this.setState({ chat_id: this.chat_id });
            this._setCachedChatId(this.chat_id);
        }
        if (responseData?.clientId) {
            this.client_id = responseData.clientId;
        }
        // 访客已留过的邮箱 —— email 绑定选项据此走"一键确认"分支
        if (responseData?.clientEmail) {
            this.clientEmail = responseData.clientEmail;
        }
        if (responseData?.bindingStatus) {
            this.setState({
                bindingStatus: {
                    webpush: !!responseData.bindingStatus.webpush,
                    telegram: !!responseData.bindingStatus.telegram,
                    email: !!responseData.bindingStatus.email,
                },
            });
        }
        if (responseData?.visitor_token) {
            this._setVisitorToken(responseData.visitor_token);
            this.fp_visitor = responseData.visitor_token;
        }
        // 会话就绪总线事件 —— visitor_uuid 是服务端权威 id(widget.js preInitClient 里
        // 同样用 visitor_uuid || visitor_token 这个 fallback 顺序,这里保持一致)。
        if (responseData?.visitor_uuid || responseData?.visitor_token) {
            this._postToWidget({
                type: 'lane_session_loaded',
                visitor_uuid: responseData.visitor_uuid || responseData.visitor_token || '',
            });
        }
        if (responseData?.appConfig) {
            debugLog('Received app config:', responseData.appConfig);
            this.applyAppConfig(responseData.appConfig);

            if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                    type: 'appConfigLoaded',
                    appConfig: responseData.appConfig
                }, '*');
            }

            // welcome_message 已由服务端作为真实消息写入 DB，无需前端临时插入
        } else {
            // 服务端答了，但这一答里没有 appConfig（异常响应 / 精简宿主）——身份
            // 不会再有第二次机会，闸门就此定成"没有卡"。少了这条，空态会永远停在
            // 骨架：这条路径既不 throw 也拿不到身份，下面那些 catch 兜不住它。
            this._settleEmptyIdentity('unavailable');
        }
    }

    async _initWithFingerprint() {
        // 首次访问：直接调用 API init，服务端生成 visitor_token，不依赖 FingerprintJS
        try {
            // 后台加载 FingerprintJS，仅用于设备识别（封禁等），不阻塞初始化
            import('@fingerprintjs/fingerprintjs').then(async ({ default: FingerprintJS }) => {
                this.FingerprintJS = FingerprintJS;
                const fp = await this.loadFingerprint();
                if (fp) {
                    debugLog('[Chat] Device fingerprint loaded:', fp);
                    this.fp_device = fp;
                }
            }).catch(e => console.warn('[Chat] FP load failed:', e));

            const visitorToken = this._getVisitorToken();
            const chatData = await ApiService.initChat(this.conf.app_id, null, null, this.from_data, visitorToken);
            const responseData = chatData?.data || chatData;
            this._applyInitResponse(responseData);

            // Parallel: load messages + connect WS
            this.loadInitialMessages();
            this.connectWebSocket();
        } catch (error) {
            console.error('Failed to initialize chat:', error);
            // 初始化明确失败（ApiService 30s 内必 resolve/reject）→ 空态定成
            // "没有卡"，骨架收走，这一格空着。见 empty-state-identity.js。
            this._settleEmptyIdentity('unavailable');
            // Still try to connect WS
            this.connectWebSocket();
        }
    }

    // 底部输入区（toolbar + 输入框 + Powered by）的真实高度 → CSS 变量。
    // emoji / GIF 弹层就是靠它把下边界收在输入区之上。以前写死 116/124px，
    // 输入框长高、通话按钮换行、Powered by 显隐都会让弹层压住输入区（实测差 5px）。
    _syncComposerVar = () => {
        const host = this.mainContainer;
        const el = this.composerEl;
        if (!host || !el) return;
        host.style.setProperty('--lc-composer-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    }

    _bindComposer = (el) => {
        if (this._composerRo) { this._composerRo.disconnect(); this._composerRo = null; }
        this.composerEl = el;
        if (!el) return;
        if (window.ResizeObserver) {
            // border-box：默认的 content-box 不认 padding 变化
            this._composerRo = new ResizeObserver(this._syncComposerVar);
            this._composerRo.observe(el, { box: 'border-box' });
        }
        // 挂载瞬间量到的是面板开场动画中途的高度，补两拍；真正要准的时刻
        // （打开弹层前）另有 _syncComposerVar 兜底。
        this._syncComposerVar();
        requestAnimationFrame(this._syncComposerVar);
        setTimeout(this._syncComposerVar, 400);
    }

    async componentDidMount() {
        this._isMounted = true;
        // 确保初始状态下自动滚动是开启的
        this.shouldAutoScroll = true;

        // 等待 mainContainer 渲染后创建 emoji portal
        setTimeout(() => {
            if (this.mainContainer) {
                this.emojiPortalContainer = document.createElement('div');
                this.emojiPortalContainer.id = 'chatEmojiPortal';
                this.emojiPortalContainer.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    pointer-events: none;
                    z-index: 9999;
                `;

                // 添加样式
                const style = document.createElement('style');
                style.textContent = `
                    #chatEmojiPortal .emoji-pick {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        /* 颜色专项:原为写死 rgba(255,255,255,.98),暗色下整块 emoji
                           面板保持亮白。--lc-emoji-bg-rgb 在暗色下由
                           darkPaletteToCssVars 写成 surface,面板随主题走。 */
                        background: rgba(var(--lc-emoji-bg-rgb, 255, 255, 255), 0.98);
                        backdrop-filter: blur(10px);
                        z-index: 9999;
                        pointer-events: all;
                        display: flex;
                        flex-direction: column;
                    }

                    #chatEmojiPortal em-emoji-picker {
                        width: 100%;
                        height: 100%;
                    }
                `;
                this.emojiPortalContainer.appendChild(style);
                this.mainContainer.appendChild(this.emojiPortalContainer);

                debugLog('[Chat] Emoji portal created in mainContainer');
            } else {
                console.warn('[Chat] mainContainer not available');
            }
        }, 100);

        this.from_data = this.state.fromData ?? {};

        // Load UI dependencies in parallel (non-blocking)
        const depsPromise = Promise.all([
            import('store'),
            import('../widget/default-configuration'),
            import('@emoji-mart/data/sets/13.1/native.json'),
            import('@emoji-mart/react'),
            import('./giphy')
        ]).then(([{ default: store }, { default: defaultConfiguration }, { default: data }, { default: Picker }, { default: GiphySearch }]) => {
            this.store = store;
            this.defaultConfiguration = defaultConfiguration;
            this.data = data;
            this.Picker = Picker;
            this.GiphySearch = GiphySearch;
        });

        // ====== 使用 Widget 预初始化的数据（JWT 和 client 已在 widget 加载时获取）======
        if (this.props.preInitData) {
            debugLog('[Chat] Using pre-initialized data from Widget');
            this._applyInitResponse(this.props.preInitData);
            this.loadInitialMessages();
            this.connectWebSocket();
        } else if (this.props.preInitPromise) {
            // 用户快速点击 - preInit 还在进行中，等待它完成
            debugLog('[Chat] Waiting for pre-init promise to resolve...');
            try {
                await this.props.preInitPromise;
                // promise 完成后，Widget._preInitData 已设置，但 props 不会自动更新
                // preInitClient 已经把 JWT 设置到 ApiService 中了，直接用
                if (this.props.preInitData) {
                    // props 可能已通过 re-render 更新
                    this._applyInitResponse(this.props.preInitData);
                } else {
                    // 直接调用 API 获取最新数据（JWT 已就绪，不会重复创建 client）
                    const vt = this._getVisitorToken();
                    const chatData = await ApiService.initChat(this.conf.app_id, null, null, this.from_data, vt);
                    const responseData = chatData?.data || chatData;
                    this._applyInitResponse(responseData);
                }
                this.loadInitialMessages();
                this.connectWebSocket();
            } catch (error) {
                console.error('[Chat] Pre-init promise failed, fallback init:', error);
                // 这条兜底 init 自己再抛（网络本来就断了，这是高发场景）时，异常会
                // 一路冒出 componentDidMount 变成 unhandled rejection —— 后面的
                // WS 连接不会跑，空态闸门也永远等不到答案（永久骨架）。所以它必须
                // 自带 catch：失败同样是"明确事件"，把闸门定成"没有卡"。
                try {
                    const visitorToken = this._getVisitorToken();
                    const chatData = await ApiService.initChat(this.conf.app_id, null, null, this.from_data, visitorToken);
                    const responseData = chatData?.data || chatData;
                    this._applyInitResponse(responseData);
                    this.loadInitialMessages();
                } catch (retryError) {
                    console.error('[Chat] Fallback init failed:', retryError);
                    this._settleEmptyIdentity('unavailable');
                }
                this.connectWebSocket();
            }
        } else {
            // 回退：如果没有预初始化数据（独立模式），自行 init
            debugLog('[Chat] No pre-init data, doing init directly');
            const visitorToken = this._getVisitorToken();
            try {
                const chatData = await ApiService.initChat(this.conf.app_id, null, null, this.from_data, visitorToken);
                const responseData = chatData?.data || chatData;
                this._applyInitResponse(responseData);
                this.loadInitialMessages();
                this.connectWebSocket();
            } catch (error) {
                console.error('[Chat] Init failed:', error);
                this._settleEmptyIdentity('unavailable');
                this.connectWebSocket();
            }
        }

        // 后台加载 FingerprintJS，仅用于设备识别
        import('@fingerprintjs/fingerprintjs').then(async ({ default: FingerprintJS }) => {
            this.FingerprintJS = FingerprintJS;
            const fp = await this.loadFingerprint();
            if (fp) this.fp_device = fp;
        }).catch(e => console.warn('[Chat] FP load failed:', e));

        // Wait for UI deps to finish loading
        await depsPromise;

        // 初始化textarea高度
        setTimeout(() => {
            this.adjustTextareaHeight();
        }, 100);

        // 只在首次加载且有消息时滚动到底部
        if (!this.hasInitialScrolled && this.state.messages && this.state.messages.length > 0) {
            setTimeout(() => {
                // 容器在 display:none 里（widget 预挂载连 WS 但面板没开）时
                // scrollTop 赋值是 no-op —— 此时绝不能烧掉一次性标记，否则
                // 刷新后首次打开面板会停在会话最顶部。等面板真正打开时由
                // widget.js 的收敛点调用 restoreScrollOnOpen 补滚。
                if (!this.chatContainer || this.chatContainer.offsetParent === null) return;
                this._beginAutoFollow();
                this.scrollToBottom();
                this.hasInitialScrolled = true; // 标记已完成首次滚动
            }, 500);
        }

        // 带初始子视图挂载（home 的「查看全部对话」直达列表）：列表数据要一起拉，
        // 否则挂载即是列表视图却空白。放在 init 之后，此时 JWT 已就绪。
        if (this.props.initialViewMode === 'chatList') this.loadChatList();

        // 设置全局引用
        window.laneChatInstance = this;

        // 监听页面可见性变化
        this.setupVisibilityTracking();

        // 监听来自Service Worker的消息
        this.setupServiceWorkerListener();

        // 添加滚动监听器
        if (this.chatContainer) {
            this.chatContainer.addEventListener('scroll', this.handleScroll);
            // 真实用户滚动输入 → 立刻交还控制权（作废跟随窗口）
            this.chatContainer.addEventListener('wheel', this._cancelAutoFollow, { passive: true });
            this.chatContainer.addEventListener('touchstart', this._cancelAutoFollow, { passive: true });
        }

        // 异步内容高度补偿（非渲染驱动的那一半：图片/视频解码完成、字体回流、富媒体
        // 卡片自撑高——这些不经过 React 重渲染，getSnapshotBeforeUpdate 抓不到）。
        //
        // 判定走状态机：严格贴底才跟随到底，否则做锚定补偿把第一条可见消息摆回原位。
        // 旧实现用宽松的 shouldAutoScroll（离底 100px 内都算贴底），用户往上滚一点点
        // 后任何一次图片加载都会把他拽回底部 —— 那正是本次修的 bug。
        if (this.chatContainer && typeof ResizeObserver !== 'undefined') {
            try {
                this._contentRO = new ResizeObserver(() => {
                    if (!this.chatContainer) return;
                    // RO 回调发生在高度已经变了之后，此刻实测的 stuck 已被污染，
                    // 所以用上一次 RO 结束时缓存下来的"变化前"快照。
                    const snapshot = this._roSnapshot || {
                        stuck: this._isStuckToBottom(),
                        anchor: this._captureScrollAnchor(),
                    };
                    this._compensateForHeightChange(snapshot);
                    this._refreshRoSnapshot();
                });
                const content = this.chatContainer.querySelector('.chat');
                if (content) this._contentRO.observe(content);
                // 每次滚动停下来都重新采样"当前即是变化前"的状态
                this.chatContainer.addEventListener('scroll', this._refreshRoSnapshot, { passive: true });
                this._refreshRoSnapshot();
            } catch (e) { /* RO 不可用就退回旧行为（onImageLoad 补滚仍在） */ }
        }

        // 移动端输入法适配 —— 这里**只**负责"键盘弹起时滚到最新消息"。
        //
        // 面板贴合可视区（高度 / 位置）不归这里管：那要改的是外层
        // position:fixed 的包裹层，在 widget.js 的 _updateKeyboardViewport。
        // 早先这里去改自己持有的那层子 div，子 div 缩了、外面那层白底容器没缩，
        // 露出的白边就是用户看到的"输入框离键盘很远"。改错了元素。
        if (this.isMobile && window.visualViewport) {
            // 判据基准必须是**记住的初始高度**，不能拿当前 innerHeight 比：
            // Android WebView(adjustResize) 下 window 和 visualViewport 一起缩，
            // 比值恒为 1，键盘永远判不出"打开" —— 原生 SDK 里点输入框消息区不动、
            // 看到的还是上面的旧消息，就是这么来的。
            // 取见过的最大值，免得把"地址栏收起"误判成键盘。
            this._baseViewportH = Math.max(
                window.visualViewport.height || 0,
                window.innerHeight || 0
            );

            this.handleVisualViewportResize = () => {
                if (!this.chatContainer) return;
                const h = (window.visualViewport.height) || window.innerHeight || 0;
                if (h > (this._baseViewportH || 0)) this._baseViewportH = h;
                const isKeyboardOpen = this._baseViewportH > 0 && h < this._baseViewportH * 0.75;
                if (!isKeyboardOpen) return;

                // 键盘打开时跟随到底部露出最新消息 —— 但仅当用户本来就贴底。
                // 正在翻历史时点输入框不该把他拽走（容器变矮由锚定补偿兜住）。
                requestAnimationFrame(() => {
                    this._maybeAutoScroll({ instant: true });
                });
            };

            window.visualViewport.addEventListener('resize', this.handleVisualViewportResize);
            window.visualViewport.addEventListener('scroll', this.handleVisualViewportResize);
            // 某些 Android 机型只派发 window resize，不派发 visualViewport resize
            window.addEventListener('resize', this.handleVisualViewportResize);
        }

        // 初始化 toolbar 箭头状态
        setTimeout(() => {
            this.updateToolbarArrows();
        }, 100);

        // 监听 toolbar 滚动事件
        if (this.toolbarRef.current) {
            this.toolbarRef.current.addEventListener('scroll', this.updateToolbarArrows);
        }

        // 监听窗口大小变化
        this.handleWindowResize = () => {
            this.updateToolbarArrows();
        };
        window.addEventListener('resize', this.handleWindowResize);

        // Chat 首屏挂载完成 —— 通知 native 容器（普通网页里 bridge 未激活时是 no-op）
        try { nativeBridge.event('ready', {}); } catch (e) { /* bridge 永不阻塞挂载 */ }
    }

    // 空态身份闸门的唯一开关点（见 empty-state-identity.js）。单调：只翻一次，
    // 之后所有调用都是 no-op —— 这就是"一次画对，而不是画三次"的执行体。
    //
    // 用实例字段而不是 state 做去重：applyAppConfig / init 失败可能在同一批
    // setState 里连着来，读 this.state 会读到还没提交的旧值，重复解析一次身份
    // （值可能已经变了）就又出现第二个标题。
    //
    // @param reason 'server'      服务端就身份给了明确答案 → 解析一次并冻结，画卡
    //        'unavailable' 这次初始化明确失败 / 服务端没给身份 → 定成"没有卡"。
    //        不拿兜底文案顶上（那是又猜一次，且与真配置到达后的值不同 = 变脸），
    //        也不停在骨架（那是永远转圈）。这一格就此空着，静态终态，与原生两端
    //        "No config → draw nothing" 一致。
    _settleEmptyIdentity = (reason) => {
        if (this._emptyIdentitySettled) return;
        this._emptyIdentitySettled = true;
        const identity = reason === 'server'
            ? resolveEmptyStateIdentity({
                // The untouched default is translated here; a merchant's title passes through.
                appConfig: this.appConfig && this.appConfig.service_title_is_default
                    ? { ...this.appConfig, service_title: t('brand.customer_service') }
                    : this.appConfig,
                conf: this.props.conf,
                defaultTitle: t('brand.service_center'),
                defaultWelcome: t('brand.default_welcome'),
            })
            : null;
        debugLog('[Chat] empty-state identity settled:', reason, identity);
        if (this._isMounted === false) return;
        this.setState({ emptyIdentity: identity, emptySettled: true });
    }

    // 应用来自Widget的应用配置
    applyAppConfig = (config) => {

        // 归一化 show_agent_avatar：后端可能下发 0/1/'0'/'1'/boolean
        if (config && config.show_agent_avatar !== undefined) {
            config = {
                ...config,
                show_agent_avatar: !(config.show_agent_avatar === false || config.show_agent_avatar === 0 || config.show_agent_avatar === '0'),
            };
        }

        // 欢迎语不再单独存一份 state.welcomeMessage：它与 appConfig.welcome_message
        // 同源同值，两个副本各自触发一次重绘，正是空态"变脸"的重绘源之一。空态卡
        // 现在从冻结的 emptyIdentity 读（见下方闸门），这份副本已无读者。

        // 存储配置供后续使用 —— 同时写入 state 触发渲染，
        // 让 MessageArea 能拿到面板配置（show_agent_avatar 等）。
        // 过滤 undefined 的 key，避免后到的局部配置清掉先前的值
        const merged = { ...this.appConfig };
        Object.keys(config || {}).forEach((key) => {
            if (config[key] !== undefined) merged[key] = config[key];
        });
        this.appConfig = merged;
        // 身份闸门：服务端这次就 service_title / welcome_message 明确说过话了 →
        // 空态卡可以画了，并且就按此刻这份 merged + props.conf 冻结下来。
        // 放在 setState 之前、用已经同步合并好的 this.appConfig 解析，避免依赖
        // setState 的提交时机。
        if (isIdentityAnswer(config)) this._settleEmptyIdentity('server');
        this.setState({ appConfig: { ...merged } }, () => {
            // Powered-by badge visibility depends on config → bar height may change.
            requestAnimationFrame(this.updateChatBottomPadding);
            // 配置可能改了主题(暗色开关/模式)→ 通知在册 embed 跟随暗色。
            this._broadcastEmbedTheme();
        });
    }

    // MessageArea 的 conf：面板下发配置作为默认值，嵌入站 props.conf 优先覆盖。
    // props.conf 里 undefined 的 key 不覆盖面板值。
    getMessageAreaConf = () => {
        const merged = { ...(this.state.appConfig || {}) };
        const propConf = this.props.conf || {};
        Object.keys(propConf).forEach((key) => {
            if (propConf[key] !== undefined) merged[key] = propConf[key];
        });
        return merged;
    }
    
    // ========== 会话列表方法 ==========

    loadChatList = async () => {
        if (!this.props.appId || !this.fp_visitor) return;
        this.setState({ chatListLoading: true });
        try {
            const res = await ApiService.getWidgetChats(this.props.appId, this.fp_visitor);
            if (res.code === 0) {
                this.setState({ chatList: res.data || [] });
            }
        } catch (e) {
            console.error('[Chat] loadChatList error:', e);
        } finally {
            this.setState({ chatListLoading: false });
        }
    }

    // 会话列表 ↔ 会话 也走方向感知过渡：列表在上层（pop 从左入），会话在下层
    // （push 从右入）。动画结束后清掉 _navAnim，避免面板关/开（display 切换）重播。
    _setNavAnim = (which) => {
        this.setState({ navAnim: which });
        if (this._navAnimTimer) clearTimeout(this._navAnimTimer);
        this._navAnimTimer = setTimeout(() => {
            this._navAnimTimer = null;
            if (this._isMounted !== false) this.setState({ navAnim: null });
        }, 350);
    }

    _navAnimStyle = (which, name) => {
        if (this.state.navAnim !== which) return {};
        let reduced = false;
        try { reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
        if (reduced) return {};
        return { animation: `${name} var(--lc-dur-base) var(--lc-ease-out)` };
    }

    // 子视图变化必须告诉外层 widget —— 返回导航的"来路"记录在 widget 的
    // chat-navigation 状态机里，而"从列表点进某条会话"这一步只发生在这里。
    // 外层没挂回调（独立部署的 chat 页）时静默跳过。
    _notifyViewMode = (mode) => {
        try { this.props.onViewModeChange && this.props.onViewModeChange(mode); } catch (e) { /* 导航回调失败不影响本视图 */ }
    }

    showChatList = () => {
        this.setState({ viewMode: 'chatList' });
        this._setNavAnim('list');
        this._notifyViewMode('chatList');
        this.loadChatList();
    }

    hideChatList = () => {
        this.setState({ viewMode: 'chat' });
        this._setNavAnim('chat');
        this._notifyViewMode('chat');
    }

    switchToChat = async (chatId) => {
        this.chat_id = chatId;
        this.isInitialLoad = true;
        // 换到新会话(含"新建会话")→ 重新上报一次面板状态,服务端据此为新 chat
        // 触发 first_visit。面板此刻必然开着(用户就是在面板里点的)。
        this._flushPanelState();
        this.setState({
            viewMode: 'chat',
            messages: [],
            chatClosed: false,
            hasMoreMessages: true,
        });
        this._setNavAnim('chat');
        this._notifyViewMode('chat');
        // reload messages for the new chat
        this.loadInitialMessages();
        // notify server of active chat change via WS
        if (WebSocketService.isConnected) {
            WebSocketService.send({ type: 'switch_chat', chat_id: chatId });
        }
    }

    closeChatById = async (chatId) => {
        if (!this.props.appId || !this.fp_visitor) return;
        try {
            const res = await ApiService.widgetCloseChat(this.props.appId, this.fp_visitor, chatId);
            if (res.code === 0) {
                // Refresh list
                this.loadChatList();
                // If the closed chat is the current one
                if (chatId === this.chat_id) {
                    this.setState({ chatClosed: true });
                }
            }
        } catch (e) {
            console.error('[Chat] closeChatById error:', e);
        }
    }

    createNewChat = async () => {
        if (!this.props.appId || !this.fp_visitor) return;
        try {
            const res = await ApiService.widgetCreateChat(this.props.appId, this.fp_visitor);
            if (res.code === 0 && res.data) {
                this.switchToChat(res.data.chatId);
            }
        } catch (e) {
            console.error('[Chat] createNewChat error:', e);
        }
    }

    formatChatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return t('chat.yesterday');
        } else if (diffDays < 7) {
            return t('chat.days_ago', { days: diffDays });
        } else {
            return date.toLocaleDateString();
        }
    }

    renderChatList = () => {
        const { chatList, chatListLoading } = this.state;
        const mainColor = this.props.conf?.mainColor || '#2563eb';

        return (
            <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--lc-surface, #fff)',
                // 与 home 同一套定案：外层不滚、只做尺寸容器；滚动全部交给下面的列表区。
                // 外层若留 overflow:visible，子项一旦超出就会溢到面板外面（面板本身是
                // 圆角 + 阴影的浮层，溢出的行会画在圆角之外）。
                overflow: 'hidden',
            }}>
                {/* Chat list header —— 顶部固定条（返回 + 新对话），不滚、不被压缩 */}
                <div style={{
                    // flex:0 0 auto：高度完全由内容决定。默认的 flex-shrink:1 会让这条
                    // 在极矮面板（横屏手机）下被挤扁，按钮跟着变形。
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--lc-space-3) var(--lc-space-4)',
                    borderBottom: 'var(--lc-hairline)',
                    background: 'var(--lc-bg, #fafbfc)',
                }}>
                    <button
                        onClick={this.hideChatList}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)',
                            color: 'var(--lc-text-2, #64748b)', fontSize: '14px', padding: 'var(--lc-space-1) 0',
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        {t('chat.back_to_chat')}
                    </button>
                    <button
                        onClick={this.createNewChat}
                        style={{
                            background: mainColor, border: 'none', cursor: 'pointer',
                            color: '#fff', fontSize: '13px', padding: '6px 14px',
                            borderRadius: 'var(--lc-radius-sm)', fontWeight: '500',
                            transition: 'opacity var(--lc-dur-base)',
                        }}
                    >
                        {t('chat.new_chat')}
                    </button>
                </div>

                {/* Chat list body —— 独立滚动区，吃掉 header 之下的全部剩余高度 */}
                <div style={{
                    flex: '1 1 auto',
                    // minHeight:0 是 flex 里 overflow 能生效的前提（默认 min-height:auto
                    // 会让它被内容顶高）。overflow-y:auto 目前已把自动最小尺寸归零，
                    // 但那是隐式依赖 —— 显式写出来，和 home 一致。
                    minHeight: 0,
                    overflowY: 'auto',
                    // 滚到头别把宿主页面一起带着滚（widget 是浮层）
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    // 顶部齐平、底部留呼吸位 —— 与知识库列表(KnowledgeList 的
                    // `list` 样式)同一条规则,不是新发明的。
                    // 原来上下各 space-2:行是**通栏**的,激活行还带 3px 左侧条,
                    // 顶部那 8px 让"当前会话"的高亮和左条够不到 header 的分隔线,
                    // 第一行看起来是浮着的。
                    padding: '0 0 var(--lc-space-3)',
                }}>
                    {chatListLoading ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--lc-text-3, #94a3b8)', fontSize: '14px' }}>
                            {t('common.loading')}
                        </div>
                    ) : chatList.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--lc-text-3, #94a3b8)', fontSize: '14px' }}>
                            {t('chat.no_chats')}
                        </div>
                    ) : (
                        chatList.map(chat => this.renderChatListItem(chat))
                    )}
                </div>
            </div>
        );
    }

    renderChatListItem = (chat) => {
        const isActive = chat.id === this.chat_id;
        const isOpen = chat.status === 'open';
        const mainColor = this.props.conf?.mainColor || '#2563eb';

        return (
            <div
                key={chat.id}
                onClick={() => this.switchToChat(chat.id)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'var(--lc-space-3) var(--lc-space-4)',
                    cursor: 'pointer',
                    background: isActive ? 'var(--lc-bg, #f8fafc)' : 'transparent',
                    borderLeft: isActive ? `3px solid ${mainColor}` : '3px solid transparent',
                    transition: 'all var(--lc-dur-fast) var(--lc-ease-out)',
                }}
            >
                {/* Status dot */}
                <div style={{
                    width: '8px', height: '8px', borderRadius: 'var(--lc-radius-full)', flexShrink: 0,
                    background: isOpen ? '#22c55e' : '#94a3b8',
                    marginRight: 'var(--lc-space-3)',
                    boxShadow: isOpen ? '0 0 0 3px rgba(34, 197, 94, 0.15)' : 'none',
                }} />

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: '2px',
                    }}>
                        <span style={{
                            fontSize: '14px', fontWeight: isActive ? '600' : '500',
                            color: 'var(--lc-text, #1e293b)',
                            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {conversationRowTitle(chat.last_message, t('chat.conversation_fallback'))}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--lc-text-3, #94a3b8)', flexShrink: 0, marginLeft: 'var(--lc-space-3)' }}>
                            {this.formatChatTime(chat.last_message_time)}
                        </span>
                    </div>
                    <div style={{
                        fontSize: '13px', color: 'var(--lc-text-2, #64748b)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {chat.last_message
                            ? resolveServiceTitle(this.state.appConfig, t('brand.customer_service'))
                            : t('chat.no_messages')}
                    </div>
                </div>

                {/* Close button for open chats */}
                {isOpen && (
                    <button
                        onClick={(e) => { e.stopPropagation(); this.closeChatById(chat.id); }}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--lc-text-3, #94a3b8)', padding: 'var(--lc-space-1)', marginLeft: 'var(--lc-space-3)',
                            borderRadius: 'var(--lc-radius-xs)', transition: 'color var(--lc-dur-base)',
                        }}
                        title={t('chat.close_session')}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    </button>
                )}
            </div>
        );
    }

    // 受信宿主(外层 widget)判定 —— 与 embed bridge 同一套 postMessage 安全纪律:
    // 双向校验 origin + source。APPLY_APP_CONFIG 会把任意 config 灌进 widget 状态,
    // 此前**无任何 origin/source 校验**(spec 红线 3),同页任意脚本 / 被嵌入的第三方
    // 内容都能伪造一帧改配色/文案/开关。这里把它收进受校验的入口:
    //   - source:配置只可能来自外层 widget(本 iframe 的 window.parent;
    //     ChatFrame 直渲同窗时退化为 window 自身)——别的 window 一律拒。
    //   - origin:必须落在允许集合(本源 + laneChatAllowedOrigins + server url origin)。
    _isTrustedHostMessage = (event) => {
        const expectedSource = (window.parent && window.parent !== window) ? window.parent : window;
        return isTrustedPostMessage(event, {
            expectedSource,
            allowedOrigins: collectAllowedHostOrigins(window),
        });
    }

    // Embed 交互 bridge(widget ↔ embed iframe 内容)。分发器持 source→entry 注册表,
    // 双向 origin+source 校验 + 能力白名单(见 embed-bridge.js)。embed 上报的
    // event/submit 一律当**不可信访客输入**:onNotify 复用现有访客 postback 通道
    // (handleEmbedEvent → WebSocketService.send,metadata 标 source=embed_event),
    // 不因"是我们的 embed"就信任。openUrl 走宿主 linkPolicy(native 经 SDK bridge)。
    _ensureEmbedBridge = () => {
        if (this._embedBridge) return this._embedBridge;
        this._embedBridge = createEmbedBridge({
            onNotify: (entry, name, data) => this.handleEmbedEvent(entry, name, data),
            onOpenUrl: (url) => openLink(url, { source: 'embed' }),
        });
        // auto 档跟随系统:addEventListener('change') 是现代写法,老 Safari 只有
        // addListener,两条都试一次(失败静默 —— 拿不到监听只是回退成"不跟随",
        // 不该让整个 bridge 挂掉)。
        try {
            const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
            if (mq) {
                if (mq.addEventListener) mq.addEventListener('change', this._handleSystemThemeChange);
                else if (mq.addListener) mq.addListener(this._handleSystemThemeChange);
                this._embedThemeMq = mq;
            }
        } catch (e) { /* best-effort */ }
        return this._embedBridge;
    }

    setupServiceWorkerListener = () => {
        this._ensureEmbedBridge();
        // 监听来自Service Worker和Widget的postMessage
        window.addEventListener('message', (event) => {
            debugLog('[Chat] Received message:', event.data);
            debugLog('[Chat] Message origin:', event.origin);

            // Embed 交互 bridge:embed iframe → widget 的受控白名单帧,自带 origin+source
            // 双向校验。非本协议帧 / 未注册来源会被它内部静默丢弃,不影响下面的分支。
            if (this._embedBridge) this._embedBridge.handleMessage(event);

            // 处理应用配置更新
            if (event.data && event.data.type === 'APPLY_APP_CONFIG') {
                // 安全修复(spec 红线 3):APPLY_APP_CONFIG 此前无 origin/source 校验。
                if (!this._isTrustedHostMessage(event)) {
                    debugLog('[Chat] APPLY_APP_CONFIG rejected: untrusted origin/source', event.origin);
                    return;
                }
                debugLog('[Chat] Applying app config:', event.data.config);
                // 走统一入口：合并 + state 化，自动触发重新渲染
                this.applyAppConfig(event.data.config || {});
                return;
            }

            // 验证消息来源（来自Service Worker的消息origin可能是null或当前域）
            if (event.data && event.data.type === 'navigate-to-chat') {
                debugLog('[Chat] Processing navigate-to-chat message');
                const { chatId, clientId } = event.data;
                
                // 如果提供了chatId和clientId，导航到特定的聊天
                if (chatId && clientId) {
                    debugLog('[Chat] Current chat:', this.chat_id, 'Current client:', this.client_id);
                    debugLog('[Chat] Target chat:', chatId, 'Target client:', clientId);
                    
                    // 检查当前是否是同一个聊天
                    if (this.chat_id !== chatId || this.client_id !== clientId) {
                        debugLog('[Chat] Different chat, need to switch');
                    } else {
                        debugLog('[Chat] Same chat, just scrolling to bottom');
                    }
                    
                    // 用户点了系统通知 —— 显式意图，滚动到底部显示最新消息
                    this._beginAutoFollow();
                    this.scrollToBottom();

                    // 可选：闪烁或高亮显示新消息
                    this.highlightNewMessages();
                    
                    // 如果窗口没有焦点，尝试获取焦点
                    if (!document.hasFocus()) {
                        window.focus();
                    }
                }
            }
        });
        
        // 还要监听Service Worker的控制状态
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                debugLog('[Chat] Received SW message via navigator.serviceWorker:', event.data);
                // 处理相同的逻辑
                if (event.data && event.data.type === 'navigate-to-chat') {
                    const { chatId, clientId } = event.data;
                    if (chatId && clientId) {
                        this._beginAutoFollow();
                        this.scrollToBottom();
                        this.highlightNewMessages();
                        if (!document.hasFocus()) {
                            window.focus();
                        }
                    }
                }
            });
        }
    }
    
    highlightNewMessages = () => {
        // 可选：添加视觉提示表示有新消息
        const chatContainer = this.chatContainer;
        if (chatContainer) {
            chatContainer.classList.add('new-message-highlight');
            setTimeout(() => {
                chatContainer.classList.remove('new-message-highlight');
            }, 1000);
        }
    }
    
    removeTempStatusMessages = () => {
        // 移除所有临时状态消息和连接状态消息
        this.setState(prevState => ({
            messages: prevState.messages.filter(msg => 
                msg.type !== 'temp-status' && 
                msg.type !== 'connection-status'
            )
        }));
    }
    
    setupVisibilityTracking = () => {
        // 页面可见性API
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('focus', this.handleWindowFocus);
        window.addEventListener('blur', this.handleWindowBlur);
        // 网络从离线恢复：WS 重连退避耗尽后不会自愈，靠 online 事件补一次主动重连。
        window.addEventListener('online', this.handleNetworkOnline);

        // 初始状态
        this.isPageVisible = !document.hidden;
        this.isWindowFocused = document.hasFocus();

        // 定期向服务器报告状态
        this.visibilityReportInterval = setInterval(() => {
            this.reportVisibilityStatus();
        }, 30000);
    }

    handleVisibilityChange = () => {
        this.isPageVisible = !document.hidden;
        this.reportVisibilityStatus();
        // 切回前台是最常见的"网络其实早就恢复了，但退避已经耗尽"的时机，补一次探测。
        if (this.isPageVisible) this._maybeForceReconnect();
    }
    
    handleWindowFocus = () => {
        this.isWindowFocused = true;
        this.reportVisibilityStatus();
    }
    
    handleWindowBlur = () => {
        this.isWindowFocused = false;
        this.reportVisibilityStatus();
    }
    
    reportVisibilityStatus = () => {
        if (WebSocketService.isConnected && this.client_id) {
            WebSocketService.send({
                type: 'visibility_status',
                client_id: this.client_id,
                is_visible: this.isPageVisible && this.isWindowFocused,
                timestamp: new Date().toISOString()
            });
        }
    }
    
    componentWillUnmount() {
        this._isMounted = false;
        if (this._paceTimer) { clearTimeout(this._paceTimer); this._paceTimer = null; }
        this._pacing = false;
        if (this._persistScrollTimer) { clearTimeout(this._persistScrollTimer); this._persistScrollTimer = null; }
        // 清理事件监听器
        if (this._embedThemeMq) {
            try {
                if (this._embedThemeMq.removeEventListener) this._embedThemeMq.removeEventListener('change', this._handleSystemThemeChange);
                else if (this._embedThemeMq.removeListener) this._embedThemeMq.removeListener(this._handleSystemThemeChange);
            } catch (e) { /* best-effort */ }
            this._embedThemeMq = null;
        }
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('focus', this.handleWindowFocus);
        window.removeEventListener('blur', this.handleWindowBlur);
        window.removeEventListener('online', this.handleNetworkOnline);
        (this._emojiOutsideTarget || document).removeEventListener('mousedown', this.handleClickOutsideEmojiPicker);

        if (this.visibilityReportInterval) {
            clearInterval(this.visibilityReportInterval);
        }

        // 清理GIF搜索防抖计时器 / 结果超时
        if (this.giphySearchDebounceTimer) {
            clearTimeout(this.giphySearchDebounceTimer);
        }
        clearTimeout(this._gifTimeout);
        clearTimeout(this._gifMoreTimeout);
        if (this._composerRo) { this._composerRo.disconnect(); this._composerRo = null; }

        // 清理滚动计时器
        this.clearScrollTimers();

        // Remove emoji picker portal container
        if (this.emojiPortalContainer && this.emojiPortalContainer.parentNode) {
            this.emojiPortalContainer.parentNode.removeChild(this.emojiPortalContainer);
        }

        // 移除滚动监听器
        if (this.chatContainer) {
            this.chatContainer.removeEventListener('scroll', this.handleScroll);
            this.chatContainer.removeEventListener('scroll', this._refreshRoSnapshot);
            this.chatContainer.removeEventListener('wheel', this._cancelAutoFollow);
            this.chatContainer.removeEventListener('touchstart', this._cancelAutoFollow);
        }

        // 断开内容高度观察
        if (this._contentRO) {
            try { this._contentRO.disconnect(); } catch (e) { /* ignore */ }
            this._contentRO = null;
        }
        if (this._roSnapshotRaf) { cancelAnimationFrame(this._roSnapshotRaf); this._roSnapshotRaf = null; }
        this._roSnapshot = null;

        // 清理视口监听器
        if (this.isMobile && window.visualViewport && this.handleVisualViewportResize) {
            window.visualViewport.removeEventListener('resize', this.handleVisualViewportResize);
            window.visualViewport.removeEventListener('scroll', this.handleVisualViewportResize);
            window.removeEventListener('resize', this.handleVisualViewportResize);
        }

        // 清理 toolbar 监听器
        if (this.toolbarRef.current) {
            this.toolbarRef.current.removeEventListener('scroll', this.updateToolbarArrows);
        }
        if (this.handleWindowResize) {
            window.removeEventListener('resize', this.handleWindowResize);
        }

        // 报告页面关闭
        if (WebSocketService.isConnected && this.client_id) {
            WebSocketService.send({
                type: 'visibility_status',
                client_id: this.client_id,
                is_visible: false,
                is_closing: true,
                timestamp: new Date().toISOString()
            });
        }
        
        // 清理WebSocket连接
        if (WebSocketService.isConnected) {
            WebSocketService.disconnect();
        }
    }
    
    handleScroll = () => {
        if (!this.chatContainer) return;
        // 初始滚动还没落位前不处理（加载期的 scroll 事件全是布局噪音）
        if (!this._scrollWatchReady) return;

        const { scrollTop, scrollHeight, clientHeight } = this.chatContainer;
        
        // 检查是否滚动到顶部（距离顶部小于100px时触发）
        if (scrollTop < 100 && !this.state.loadingHistory && this.state.hasMoreMessages) {
            this.loadMoreMessages();
        }
        
        // 严格贴底判定 —— 这是「系统能不能自动把你带到底部」的唯一依据。
        // 注意：这里和下面那个「按钮显隐」阈值是两件事，必须用两个常量。
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const isAtBottom = distanceFromBottom <= STICK_TO_BOTTOM_PX;

        // shouldAutoScroll 只是这次实测的缓存（sessionStorage 记忆要用），真正的
        // 裁决走 _isStuckToBottom() 实测，不依赖这个可能过期的布尔值。
        this.shouldAutoScroll = isAtBottom;

        // 如果用户主动向上滚动，清除所有待执行的滚动计时器
        if (!this.shouldAutoScroll) {
            this.clearScrollTimers();
        }

        // 如果滚动到底部，清除未读计数
        if (isAtBottom && this.state.unreadCount > 0) {
            this.setState({ unreadCount: 0 });
        }

        if (this.state.isAtBottom !== isAtBottom) {
            this.setState({ isAtBottom });
        }

        // 「滚动到底部」浮动按钮的显隐阈值 —— 纯 UI 观感，离底 >120px 才浮出，
        // 避免正常阅读最后几条时闪现。和贴底判定无关，不要合并常量。
        const farFromBottom = distanceFromBottom > JUMP_BUTTON_PX;
        if (this.state.farFromBottom !== farFromBottom) {
            this.setState({ farFromBottom });
        }

        // 会话滚动位置记忆（sessionStorage per chat)：重开面板 / 刷新后首开时
        // 由 restoreScrollOnOpen 恢复。写入做 250ms 节流，滚动事件很密。
        this._schedulePersistScrollPos();
    }

    // ---------- 聊天空态建议 pill ----------
    // 数据源两条，判定逻辑全在 ./suggest-pills（纯函数，有单测），这里只负责
    // 取配置 + 发请求 + setState：
    //
    //   1. 商户配置 widget_config.suggested_questions —— 三态（未配置 / 显式空数组
    //      = 关闭 / 非空 = 接管），见 resolveConfiguredQuestions 的注释。配置态
    //      **完全接管**，一个字节的知识库请求都不发。
    //   2. 未配置时回落知识库文章标题（服务端按 sort_order 排序，取前 4 条，
    //      >60 字符的丢弃）。这条路要先过 knowledge_enabled —— 商户关掉知识库后
    //      访客不该还看到 KB 派生的按钮（此前漏判）。
    //
    // 一条都取不到就不显示（绝不硬编码假问题）。点击即以访客身份发送。
    loadSuggestPills = async () => {
        try {
            if (this._suggestPillsLoaded) return;
            this._suggestPillsLoaded = true;

            // this.appConfig 由 _applyInitResponse → applyAppConfig 同步写入，而
            // loadSuggestPills 只被 loadInitialMessages 调用（永远在 init 之后），
            // 所以这里读到的就是服务端那份。真取不到（边缘态）时 widget_config
            // 为 undefined → 判定回落 KB，即老行为，不会更差。
            const appConfig = this.appConfig || {};

            const configured = resolveConfiguredQuestions(appConfig.widget_config, pickLocalized);
            if (configured !== null) {
                // 注意判 `!== null` 不是判真假：`[]` 是"商户明确关掉了"，必须
                // 在这里 return，绝不能掉进下面的知识库回落。
                if (configured.length && this._isMounted !== false) {
                    this.setState({ suggestPills: configured });
                }
                return;
            }

            if (!isKnowledgeFallbackEnabled(appConfig)) return;

            const appId = this.props.appId;
            if (!appId) return;
            // limit=KB_PILL_MAX*2：只要前几条，留一倍余量给">60 字符被丢弃"的标题。
            // （原来这里传的是 status=active —— 后端 KnowledgeController::index 根本
            //  不读这个参数，状态固定筛 'ready'，是个纯装饰的死参数。）
            const resp = await fetch(`${ApiService.baseURL}/knowledge?app_id=${encodeURIComponent(appId)}&limit=${KB_PILL_MAX * 2}`);
            if (!resp.ok) return;
            const result = await resp.json();
            if (result && result.code === 0) {
                const pills = resolveKnowledgePills(result.data);
                if (pills.length && this._isMounted !== false) {
                    this.setState({ suggestPills: pills });
                }
            }
        } catch (e) { /* 静默：pill 是锦上添花，绝不打扰主流程 */ }
    }

    handleSuggestPillClick = (text) => {
        // 发送后 messages 里出现 visitor 消息，渲染条件自动隐藏整组 pill
        this.sendMessage(text);
    }

    // 「滚动到底部」按钮点击：平滑滚底（reduced-motion 下 instant），清计数
    handleJumpToBottom = () => {
        let reduced = false;
        try { reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
        // 显式用户意图 —— 开跟随窗口，让途中的高度变化重新顶到底部而不是锚定回去
        this._beginAutoFollow();
        if (reduced) this.instantScrollToBottom();
        else this.scrollToBottom();
        this.setState({ unreadCount: 0 });
    }

    // ---------- 滚动位置记忆（sessionStorage，per chat） ----------

    _scrollPosKey = () => {
        if (!this.chat_id) return null;
        return `lane_scroll_${this.props.appId || 'app'}_${this.chat_id}`;
    }

    // 最后一条有真实数字 id 的消息 —— 用来判断"重开时有没有新消息"
    _lastRealMessageId = () => {
        const msgs = this.state.messages || [];
        for (let i = msgs.length - 1; i >= 0; i--) {
            const id = msgs[i] && msgs[i].id;
            if (id != null && Number.isFinite(Number(id))) return Number(id);
        }
        return null;
    }

    _schedulePersistScrollPos = () => {
        if (this._persistScrollTimer) return;
        this._persistScrollTimer = setTimeout(() => {
            this._persistScrollTimer = null;
            this._persistScrollPos();
        }, 250);
    }

    _persistScrollPos = () => {
        const key = this._scrollPosKey();
        if (!key || !this.chatContainer) return;
        // 容器不可见时 scrollTop 读数不可靠（display:none 下为 0），别覆盖好数据
        if (this.chatContainer.offsetParent === null) return;
        try {
            sessionStorage.setItem(key, JSON.stringify({
                top: this.chatContainer.scrollTop,
                atBottom: !!this.shouldAutoScroll,
                last: this._lastRealMessageId(),
            }));
        } catch (e) { /* storage full / disabled — best effort */ }
    }

    // 面板从关闭变打开（或视图切回 chat）时由 widget.js 收敛点调用：
    //   1. 有记忆且用户当时不在底部、且没有新消息 → 恢复原位置
    //   2. 有新消息（最后一条消息 id 变了）→ 滚到底部
    //   3. 无记忆 → 滚到底部（最新消息）
    restoreScrollOnOpen = () => {
        const c = this.chatContainer;
        if (!c || c.offsetParent === null) return; // 还不可见，无从恢复
        let saved = null;
        try {
            const key = this._scrollPosKey();
            if (key) saved = JSON.parse(sessionStorage.getItem(key));
        } catch (e) { /* ignore */ }

        const lastId = this._lastRealMessageId();
        const canRestore = saved
            && typeof saved.top === 'number'
            && !saved.atBottom
            && saved.last != null && lastId != null
            && Number(saved.last) === Number(lastId);

        // 恢复期间由本函数独占 scrollTop —— 高度补偿路径看到这个标记就完全让开，
        // 否则"恢复到历史位置"会被补偿逻辑当成异常位移又拉回去。
        this._restorePending = true;
        c.style.scrollBehavior = 'auto';
        if (canRestore) {
            c.scrollTop = saved.top;
            this.shouldAutoScroll = false;
        } else {
            c.scrollTop = c.scrollHeight;
            this.shouldAutoScroll = true;
        }
        this.hasInitialScrolled = true;
        requestAnimationFrame(() => {
            if (!this.chatContainer) { this._restorePending = false; return; }
            // 再补一次，防止刚变可见那一帧 scrollHeight 还没稳定
            if (canRestore) this.chatContainer.scrollTop = saved.top;
            else this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
            this.chatContainer.style.scrollBehavior = 'smooth';
            // 落位完成 → 激活滚动判断（jump 按钮等）+ 交还 scrollTop 所有权
            requestAnimationFrame(() => {
                this._restorePending = false;
                this._scrollWatchReady = true;
                this._refreshRoSnapshot();
            });
        });
    }

    // 桌面端打开面板后自动聚焦输入框（widget.js 收敛点调用；移动端不调用，
    // 避免键盘弹跳）。禁用态（封禁/邮箱门）focus 是 no-op，无需分支。
    focusComposer = () => {
        try {
            if (this.input && !this.input.disabled) this.input.focus({ preventScroll: true });
        } catch (e) { /* ignore */ }
    }

    // 输入框 placeholder：商户自定义值优先；等于内置默认文案（任一语言）时改走
    // t()，这样访客用 header 语言切换器换语言后 placeholder 能跟着变。
    _composerPlaceholder = () => {
        // 商户配的文案可能是 {lang:text} 多语言 map（访客文案的统一存法）。
        // 之前这里把 conf.placeholderText **原样**返回 —— 配了 map 的 app，
        // 首帧会把整段 JSON 画进 placeholder，等配置二次下发才变正常。
        // pickLocalized 会自己判断是不是 JSON map、按当前语言取、回落 en，
        // 纯字符串原样返回，所以两种存法都安全。home-view.js 用的是同一个。
        const raw = this.props.conf && this.props.conf.placeholderText;
        const configured = pickLocalized(raw);
        if (configured
            && configured !== locales.zh['input.placeholder']
            && configured !== locales.en['input.placeholder']) {
            return configured;
        }
        return t('input.placeholder');
    }
    
        // 把后端返回的 agents 字典展开到每条消息上：
        //   后端响应 { messages: [{agent_key, ...}], agents: {key: {name, avatar, is_ai}} }
        //   → 展平为 message.agent_name / agent_avatar，保持渲染层零改动
        _enrichWithAgentInfo = (messages, agents) => {
            if (!Array.isArray(messages)) return messages;
            const dict = (agents && typeof agents === 'object') ? agents : {};
            return messages.map(m => {
                if (!m || !m.agent_key) return m;
                const info = dict[m.agent_key];
                if (!info) return m;
                return {
                    ...m,
                    agent_name: info.name || m.agent_name,
                    agent_avatar: info.avatar || m.agent_avatar || null,
                    is_ai: info.is_ai || m.is_ai,
                };
            });
        }

        // Re-sync (MERGE, not replace) persisted messages into state, dedup by id.
        // Backstops the proactive-message race: the widget now connects the WS on
        // mount and the first_visit flow dispatches fast — so its messages can be
        // persisted before/around WS register, i.e. missed by live frames AND by
        // the initial (too-early, empty) load. Calling this on register + a beat
        // later guarantees the welcome shows without re-introducing server blocking.
        // register 后的兜底 reload：first_visit / 主动消息可能在 register 前后才落库,
        // 同时错过初始 load(那时空)和 live 帧(那时未 register)。这里合并补齐,并且——
        // 关键——把"新鲜的主动气泡"(admin/agent/ai + 带 typing_ms/delay_ms + 刚落库)
        // 交给 _enqueuePaced 走 typing→出泡,而不是瞬间 merge。这样即便欢迎语只能靠
        // 兜底 load 到达,也照样有"正在输入"的过场,和 live 路径体验一致。旧消息(重连
        // 补历史)仍瞬间 merge,不会诡异地重放 typing。_pacedSeen 保证与 live 帧不重复。
        resyncMessages = async () => {
            if (!this.chat_id) return;
            try {
                const data = await ApiService.loadMessages(this.chat_id, this.fp_visitor, { limit: 20 });
                if (!data || !data.success || !Array.isArray(data.messages) || !data.messages.length) return;
                const incoming = this._enrichWithAgentInfo(data.messages, data.agents);
                this._pacedSeen = this._pacedSeen || new Set();
                const now = Date.now();
                const instant = [];
                incoming.forEach(m => {
                    if (m.id == null) return;
                    const already = this.state.messages.some(x => x.id === m.id) || this._pacedSeen.has(m.id);
                    if (already) return;
                    const md = m.metadata || {};
                    const isProactive = (m.from === 'admin' || m.from === 'agent' || m.from === 'ai')
                        && (parseInt(md.typing_ms, 10) > 0 || parseInt(md.delay_ms, 10) > 0);
                    const tsRaw = m.timestamp || m.time || m.created_at;
                    const ageMs = tsRaw ? (now - new Date(tsRaw).getTime()) : Infinity;
                    // 只对"刚刚(20s 内)落库"的主动消息做 typing 过场,避免给离线期间
                    // 早已发出的历史消息重放 typing。时钟偏差用宽松窗口兜底。
                    if (isProactive && ageMs >= -5000 && ageMs < 20000) {
                        this._enqueuePaced(m);
                    } else {
                        instant.push(m);
                    }
                });
                if (instant.length) {
                    this.setState(prev => {
                        const seen = new Set(prev.messages.map(x => x.id));
                        const merged = prev.messages.concat(instant.filter(x => !seen.has(x.id)));
                        // 本地待发消息 id 形如 'temp-172...'，Number()是 NaN，旧写法 `||0`
                        // 会让它们全部排到最前面（NaN||0 恒等于 0，小于任何真实历史消息
                        // id）——待发气泡就被顶到聊天记录最开头。改成：两条都有真实数字
                        // id 才按 id 排；否则没有数字 id 的一律排在后面，且互相之间保持
                        // 原有相对顺序（现代引擎的 Array#sort 是稳定排序）。
                        merged.sort((a, b) => {
                            const aNum = Number(a.id);
                            const bNum = Number(b.id);
                            const aValid = a.id != null && Number.isFinite(aNum);
                            const bValid = b.id != null && Number.isFinite(bNum);
                            if (aValid && bValid) return aNum - bNum;
                            if (aValid && !bValid) return -1;
                            if (!aValid && bValid) return 1;
                            return 0;
                        });
                        return { messages: merged, emailGateLocked: this._computeEmailGate(data.messages) };
                    }, () => { this._maybeAutoScroll({ instant: true }); });
                } else {
                    this.setState({ emailGateLocked: this._computeEmailGate(data.messages) });
                }
            } catch (e) { /* best-effort */ }
        };

        loadInitialMessages = async () => {
        if (!this.chat_id) {
            return;
        }

        this.setState({ loadingHistory: true });

        try {
            // 使用新的API服务加载初始消息
            const data = await ApiService.loadMessages(this.chat_id, this.fp_visitor, { limit: 20 });

            if (data.success && data.messages) {
                // 换了会话时旧消息不属于这里，只有同一会话才合并实时到达的消息。
                const sameChat = this._historyLoadedChatId === undefined
                    || String(this._historyLoadedChatId) === String(this.chat_id);
                this._historyLoadedChatId = this.chat_id;
                const history = this._enrichWithAgentInfo(data.messages, data.agents);
                this.setState(prev => ({
                    messages: sameChat
                        ? mergeInitialHistory(prev.messages, history)
                        : history,
                    hasMoreMessages: data.has_more,
                    emailGateLocked: this._computeEmailGate(data.messages)
                }), () => {
                    // 初始加载完成后滚动到底部（显式的首屏落位意图）
                    if (this.isInitialLoad) {
                        this.isInitialLoad = false;
                        this.shouldAutoScroll = true;
                        this._beginAutoFollow();
                        this.instantScrollToBottom();
                    }
                    // 初始滚动落位后（再等一帧布局稳定）才激活滚动判断
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                        this._scrollWatchReady = true;
                        this._refreshRoSnapshot();
                    }));
                });
                // 访客从没发过话 → 拉建议问题 pill（有历史对话就不打扰）
                if (!data.messages.some(m => m && m.from === 'visitor')) {
                    this.loadSuggestPills();
                }
            } else if (shouldFallBackToDefaultChat({ currentChatId: this.chat_id, defaultChatId: this._defaultChatId })) {
                // 深链目标（?chat_id=N）不属于本访客 / 已不可见——服务端
                // ApiController::loadMessages 按 client_id 归属校验，查不到就
                // 返回 {code:404}（不带 success 字段，上面 data.success 判 false）。
                // 优雅降级：回退到 /v1/chat 一开始就选好的默认会话，重新拉一次。
                // 只会触发这一次——重试落地后 chat_id === _defaultChatId，这个
                // 分支条件不再成立，不会死循环。
                debugLog('[Chat] initialChatId not accessible, falling back to default chat:', this.chat_id, '→', this._defaultChatId);
                this.chat_id = this._defaultChatId;
                this.setState({ chat_id: this.chat_id, messages: [] });
                this._setCachedChatId(this.chat_id);
                await this.loadInitialMessages();
            } else if (data.error === 'Chat not found') {
                // 聊天不存在，没有历史消息
                debugLog('No chat history found');
                this.setState({
                    messages: [],
                    hasMoreMessages: false
                });
                this.loadSuggestPills();
            }
        } catch (error) {
            console.error('Error loading initial messages:', error);
            // 出错时也设置为没有更多消息
            this.setState({ hasMoreMessages: false });
        } finally {
            this.setState({ loadingHistory: false });
            // 空会话 / 出错路径也要放行滚动判断（成功路径在滚动落位后已置 true）
            requestAnimationFrame(() => requestAnimationFrame(() => { this._scrollWatchReady = true; }));
        }
    }
    
        loadMoreMessages = async () => {
        // 注意：register消息在初始连接时已经返回了历史消息
        // 这个方法仅用于用户向上滚动时加载更多历史消息
        
        if (!this.chat_id || this.state.loadingHistory || !this.state.hasMoreMessages) {
            return;
        }
        
        // 标记不需要自动滚动
        this.shouldAutoScroll = false;
        
        this.setState({ loadingHistory: true });
        
        try {
            // 获取第一条消息的ID作为before_id
            const firstMessage = this.state.messages[0];
            const beforeId = firstMessage ? firstMessage.id : null;
            
            // 使用新的API服务加载更多历史消息
            const data = await ApiService.loadMessages(this.chat_id, this.fp_visitor, {
                beforeId: beforeId,
                limit: 50
            });
            
            if (data.success && data.messages.length > 0) {
                // 记录当前滚动位置
                const scrollHeight = this.chatContainer.scrollHeight;
                const scrollTop = this.chatContainer.scrollTop;

                // 在消息列表前面添加新消息（按 agents 字典 inject agent_name/avatar）
                const enriched = this._enrichWithAgentInfo(data.messages, data.agents);
                this.setState(prevState => ({
                    messages: [...enriched, ...prevState.messages],
                    hasMoreMessages: data.has_more
                }), () => {
                    // 保持滚动位置
                    const newScrollHeight = this.chatContainer.scrollHeight;
                    const scrollDiff = newScrollHeight - scrollHeight;
                    this.chatContainer.scrollTop = scrollTop + scrollDiff;
                });
            } else {
                this.setState({ hasMoreMessages: false });
            }
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            this.setState({ loadingHistory: false });
        }
    }
    async loadFingerprint() {
        const fpPromise = this.FingerprintJS.load();
        const fp = await fpPromise;
        const result = await fp.get();
        return result.visitorId;
    }


    connectWebSocket = async () => {
        try {
            // 防止重复连接
            if (this.isConnecting) {
                debugLog('WebSocket connection already in progress');
                return;
            }
            this.isConnecting = true;

            // 使用新的WebSocket服务 - 使用 fp_visitor 作为 userId
            const userId = this.client_id || this.fp_visitor || this.props.userId;

            // 只在第一次连接时注册事件处理器
            if (!this.wsHandlersRegistered) {
                // 注册消息处理器
                WebSocketService.on('connected', this.handleConnected);
                WebSocketService.on('register', this.handleRegisterResponse);
                WebSocketService.on('message', this.handleWebSocketMessage);
                WebSocketService.on('new_message', this.handleNewMessage);
                WebSocketService.on('typing', this.handleTypingStatus);
                WebSocketService.on('ai_stream', this.handleAiStream);
                WebSocketService.on('teaser', this.handleTeaserPush);
                WebSocketService.on('panel_open_ack', this.handlePanelOpenAck);
                WebSocketService.on('agent_status', this.handleAgentStatus);
                WebSocketService.on('gif_search_results', this.handleGifSearchResults);
                // 失败帧。此前只注册了成功帧,于是 app/平台都没配 Giphy key 时
                // (服务端立刻回 gif_error)访客要盯着转圈**干等满 8 秒**才看到
                // 错误——那 8 秒是本地兜底超时,不是网络在等。工作台一直处理了
                // 这个帧,所以同一个失败在客服侧是秒级的,两边体感完全不同。
                // 服务端附带的 message 是给商户看的配置诊断
                // ("GIF search is not configured for this app"),不原样甩给访客;
                // 这里只把状态推进到 error,复用已有的失败态 + 重试按钮。
                WebSocketService.on('gif_error', this.handleGifError);
                WebSocketService.on('call_initiated', this.handleCallInitiated);
                WebSocketService.on('call_request', this.handleIncomingCall);
                WebSocketService.on('call_accepted', this.handleCallAccepted);
                WebSocketService.on('call_rejected', this.handleCallRejected);
                WebSocketService.on('call_ended', this.handleCallEnded);
                WebSocketService.on('call_busy', this.handleCallBusy);
                WebSocketService.on('call_cancelled', this.handleCallCancelled);
                WebSocketService.on('chat_closed', this.handleChatClosed);
                WebSocketService.on('tts_play', this.handleTtsPlay);
                WebSocketService.on('banned', this.handleBanned);
                // 服务端对失败请求统一回 {type:'error'}（拨号被功能开关挡住、token
                // 生成失败……）。之前没人监听这个类型，websocket.js 找不到 handler
                // 直接丢弃——访客点了拨号毫无反应，是"通话不稳定"观感的一大来源。
                WebSocketService.on('error', this.handleServerError);

                // 注册连接状态变化处理器
                WebSocketService.onConnectionChange(this.handleConnectionChange);

                this.wsHandlersRegistered = true;
                debugLog('WebSocket handlers registered');
            }

            await WebSocketService.connect(userId, this.props.appId);

            debugLog('WebSocket connected');
            this.isConnecting = false;
            this.isReconnecting = false;

            // 不需要手动处理队列，WebSocketService 会自动处理
            // 只需要清理临时状态消息
            if (this.messageQueue.length > 0) {
                debugLog(`${this.messageQueue.length} messages will be sent by WebSocketService`);
                this.messageQueue = [];

                setTimeout(() => {
                    this.removeTempStatusMessages();
                }, 1000);
            }
        } catch (error) {
            console.error('Failed to connect WebSocket:', error);
            this.isConnecting = false;
            this.isReconnecting = true;
            setTimeout(() => this.connectWebSocket(), 5000);
        }
    };

    handleConnectionChange = (status) => {
        if (status === 'disconnected') {
            debugLog('WebSocket disconnected');
            this.isReconnecting = true;
            // message-area.js 早就有 type:'connection-status' 的渲染态（黄色警告条），
            // 但从来没有任何代码创建过这种消息——断线时界面上完全没有提示。这里接上。
            this._showConnectionStatus(t('status.connection_lost'));
        } else if (status === 'connected') {
            debugLog('WebSocket reconnected');
            this.isReconnecting = false;
            this.removeTempStatusMessages();
        }
    };

    // 插入/覆盖一条 connection-status 提示（固定 id，防止连续 disconnected 事件堆出多条）。
    _showConnectionStatus = (text) => {
        this.setState(prevState => ({
            messages: [
                ...prevState.messages.filter(m => m.type !== 'connection-status'),
                { id: 'connection-status', type: 'connection-status', text, from: 'system', time: new Date() },
            ],
        }));
    };

    // WS 指数退避 5 次耗尽后就不会再自己重试了。切回前台 / 网络恢复是最常见的
    // 恢复时机，这里补两个主动重连钩子，不然访客只能刷新页面才能恢复聊天。
    _maybeForceReconnect = () => {
        if (WebSocketService.isConnected) return;
        debugLog('[Chat] Active recovery hook firing — forcing WS reconnect');
        WebSocketService.forceReconnect().catch(err => console.warn('[Chat] Force reconnect failed:', err));
    };

    handleNetworkOnline = () => {
        this._maybeForceReconnect();
    };

    handleConnected = (data) => {
        debugLog('Received connected message:', data);

        // 发送注册消息 - 使用 fp_visitor 作为主要标识
        const userId = this.client_id || this.fp_visitor || this.props.userId;
        debugLog('Preparing to send register:', {
            appId: this.props.appId,
            userId: userId,
            fp_visitor: this.fp_visitor,
            propsUserId: this.props.userId,
            client_id: this.client_id
        });

        if (this.props.appId && userId) {
            debugLog('Sending register message');
            // 获取当前页面 URL（如果在 iframe 中，尝试获取父页面 URL）
            let currentPageUrl = window.location.href;
            try {
                if (window.parent !== window) {
                    currentPageUrl = window.parent.location.href;
                }
            } catch (e) {
                // 跨域无法访问父窗口，使用 referrer
                currentPageUrl = document.referrer || window.location.href;
            }

            WebSocketService.send({
                type: 'register',
                appId: this.props.appId,
                userId: userId,
                fp: this.fp_visitor,
                from_url: currentPageUrl,
                from_data: this.state.fromData,
                chatId: this.chat_id || ''
            });
        } else {
            debugLog('Cannot send register - missing appId or userId');
        }
    };

    handleRegisterResponse = (data) => {
        debugLog('Register response received:', data);

        const prevChatId = this.chat_id;

        if (data.chat_id) {
            this.chat_id = data.chat_id;
            this.setState({ chat_id: data.chat_id });
        }

        if (data.client_id) {
            this.client_id = data.client_id;
        }

        // 更新缓存（WS register 的值是最权威的）
        if (this.fp_visitor) {
            this._setVisitorToken(this.fp_visitor);
        }
        if (this.chat_id) {
            this._setCachedChatId(this.chat_id);
        }

        // 初始化推送管理器
        if (data.client_id && !this.pushManager) {
            this.pushManager = new PushManager(this.props.appId, data.client_id, this.fp_visitor);
            this.pushManager.init();
            window.laneChatPushManager = this.pushManager;
        }

        // 如果 chat_id 变化了（新会话或缓存过期），重新加载消息
        // 如果之前没有 chat_id（首次无缓存，initChat 也失败了），也需要加载
        if (data.chat_id && (!prevChatId || String(prevChatId) !== String(data.chat_id))) {
            this.loadInitialMessages();
        }

        // 一次性对账(保险,不是主修复)。主动消息的送达已在服务端根治:first_visit 现在
        // 由 WS handleRegister 在连接注册进 $userConnections 之后才 fire,flow 广播时连接
        // 必在 → live 帧稳定送达并带 typing 节奏。这里只留一次 merge-by-id 的对账,兜
        // 网络抖动/丢帧,且因为是按 id 合并,漏一条也能补(不像之前"有任意主动消息就跳过"
        // 的启发式 gate——那会在只漏其中一条时永远补不回来)。
        // 告诉外层 widget 这次注册有没有预跑 teaser。没有就绝不可能有 teaser 落 Redis,
        // widget 直接不轮询 /v1/widget/teaser(回访场景以前会白轮询 3 次)。
        this._postToWidget({ type: 'lane_first_visit', fired: !!data.teaser_pending });
        this._wsRegistered = true;

        // 连接/会话就绪后补发 panel_open:面板已经开过(inline 模式、或用户在连上
        // 之前就点开了、或刚换了会话)但当时没发成的,这里补上。按 chat_id 去重。
        this._flushPanelState();
    };

    // 上报面板状态。这是「状态」不是「事件」—— 重复上报完全安全,服务端按 chat
    // 自己判定要不要触发 first_visit(DB 里每个 chat 只触发一次)。
    // 客户端刻意不做「发过没有」的记账:之前那样做,面板开着时点「新建会话」就不再
    // 触发欢迎语了,而且每多一个场景就多一个漏掉的分支。
    notifyPanelOpen = () => this.reportPanelState(true);

    reportPanelState = (isOpen) => {
        this._panelOpen = !!isOpen;
        this._flushPanelState();
    };

    // 连接/会话没就绪时先不发,register 回来会补;换会话后也会再调一次。
    _flushPanelState = () => {
        if (this._panelOpen === undefined) return;
        if (!this.chat_id || !WebSocketService.isConnected) return;
        WebSocketService.send({
            type: 'panel_state',
            chat_id: this.chat_id,
            open: !!this._panelOpen,
        });
    };

    // 服务端确认 first_visit 是否真的触发了。触发了才安排那一次 merge-by-id 对账
    // (兜网络丢帧);没触发就 0 次多余请求。
    handlePanelOpenAck = (data) => {
        if (data && data.first_visit_fired) {
            setTimeout(() => { if (this._isMounted) this.resyncMessages(); }, 3000);
        }
    };

    handleWebSocketMessage = (data) => {
        debugLog('Received message:', data);

        if (data.type === 'message') {
            const newMessage = {
                id: data.id || Date.now(),
                text: data.text,
                from: data.from || 'agent',
                timestamp: data.timestamp || new Date().toISOString(),
                time: data.timestamp || new Date().toISOString(),
                attachment: data.attachment,
                reply_to: data.reply_to,
                metadata: data.metadata,
                type: frameDisplayType(data),
                message_type: data.message_type || 'text',
                service_data: data.service_data,
                agent_name: data.agent_name,
                agent_avatar: data.agent_avatar,
                is_ai: !!data.is_ai
            };

            // 如果是visitor发送的消息，检查是否有临时消息需要替换
            if (newMessage.from === 'visitor') {
                this.setState(prevState => {
                    // 之前这里无差别 filter 掉所有 status:'sending' 的消息——连续发 A、B
                    // 两条时，A 的 ack 先到会把 A、B 的临时气泡一起清掉再只塞回 A，B 就从
                    // 界面消失了（B 的 ack 若再丢，永久消失且无提示）。改成精确定位"这条
                    // ack 对应的那一条"再删：优先用 sendMessage/handleButtonClick/
                    // handleEmailSubmit 塞进 metadata.client_temp_id、若被服务端原样存回
                    // 并广播回来的值做精确匹配；拿不到就退化为"文本/附件相同的最早一条
                    // sending"（旧协议兜底），且只删这一条，不再清空其它 sending 消息。
                    const clientTempId = newMessage.metadata && newMessage.metadata.client_temp_id;
                    let targetIndex = -1;
                    if (clientTempId) {
                        targetIndex = prevState.messages.findIndex(m => m.status === 'sending' && m.id === clientTempId);
                    }
                    if (targetIndex === -1) {
                        targetIndex = prevState.messages.findIndex(m =>
                            m.status === 'sending' &&
                            m.text === newMessage.text &&
                            (m.attachment || null) === (newMessage.attachment || null)
                        );
                    }
                    const filteredMessages = targetIndex === -1
                        ? prevState.messages
                        : prevState.messages.filter((_, i) => i !== targetIndex);

                    // 检查是否已存在相同的消息（避免重复）
                    const exists = filteredMessages.some(msg => msg.id === newMessage.id);

                    if (!exists) {
                        return { messages: [...filteredMessages, newMessage] };
                    }

                    return { messages: filteredMessages };
                }, () => {
                    // 访客自己那条消息的服务端 ack —— 走同一个闸门（发送时已 arm 过
                    // _followOnce，所以刚发完必跟随；单纯的 ack 回填则只在贴底时跟随）
                    this._maybeAutoScroll();
                });
            } else {
                // agent/AI/flow 消息。客户端节奏(typing→出泡)统一走 _enqueuePaced,
                // 无论消息来自 live WS 帧还是 resync 兜底 load 都是同一条路径、同一套去重。
                this._enqueuePaced(newMessage);
            }
        }
    };

    // 统一的"节奏化入队"：任何 agent/AI/flow 气泡都从这里进——live WS 帧和 resync
    // 兜底 load 共用,靠 _pacedSeen 按 id 去重(两条路都可能送到同一条 proactive 消息,
    // 只能出泡一次)。metadata.typing_ms / delay_ms 由服务端(SendMessageAction /
    // AIAutoReply)透传：widget 先显示"正在输入"这么久再渲染气泡,取代服务端 usleep
    // (会阻塞 shard)。上限 5s 兜底,绝不会因坏配置无限挂着。
    _enqueuePaced = (newMessage) => {
        this._pacedSeen = this._pacedSeen || new Set();
        const id = newMessage.id;
        if (id != null && this._pacedSeen.has(id)) return;
        if (id != null) this._pacedSeen.add(id);

        // 面板关着时,把这条消息抛给外层 widget 当 teaser 式气泡提示(客服回复/主动消息
        // 都适用)。widget 自己判断面板开没开——它才是那个状态的所有者。
        this._postToWidget({
            type: 'lane_msg_preview',
            text: (newMessage.text || '').toString().slice(0, 300),
            message_type: newMessage.message_type || 'text',
            agent_name: (newMessage.agent && newMessage.agent.name) || null,
            avatar_url: (newMessage.agent && newMessage.agent.avatar) || null,
        });

        // 总线事件 message_received —— 覆盖 live WS 帧和 resync 兜底 load 两条路径
        // (都汇聚到这个函数),widget.js 据此累加未读徽标 + 转发给 laneChat.on() 订阅者。
        this._postToWidget({
            type: 'lane_msg_received',
            message_type: newMessage.message_type || 'text',
            excerpt: (newMessage.text || '').toString().slice(0, 80),
            is_ai: !!newMessage.is_ai,
        });

        const md = newMessage.metadata || {};
        const typingMs = Math.min(5000, parseInt(md.typing_ms, 10) || 0);
        const delayMs  = Math.min(5000, parseInt(md.delay_ms, 10) || 0);
        const showTyping = (this.props.conf?.show_typing !== false) && typingMs > 0;
        const wait = typingMs || delayMs;

        const commit = () => {
            const locksComposer = !!(newMessage.service_data &&
                newMessage.service_data.type === 'ask_email' &&
                newMessage.service_data.required === true &&
                !newMessage.service_data.submitted);
            let appended = false;
            this.setState(prevState => {
                if (prevState.messages.some(m => m.id === newMessage.id)) return {};
                appended = true;
                return {
                    messages: [...prevState.messages, newMessage],
                    // 真消息一落地就清掉流式预览气泡。
                    //
                    // 不能只靠服务端的 end 帧：它和真消息是两条独立的帧，end 晚到
                    // 或丢了，预览就会和真消息并排显示 —— 看起来像同一条回复发了
                    // 两次，而且要挂到看门狗超时才消失（刷新页面又是好的，因为库里
                    // 本来就只有一条）。在这里清是唯一无时序依赖的位置。
                    ...(prevState.aiStream ? { aiStream: null } : {}),
                    ...(locksComposer ? { emailGateLocked: true } : {})
                };
            }, () => {
                // 客服/AI 新消息到达：贴底才跟随；用户正在看历史就绝不动 scrollTop，
                // 只把「回到底部」按钮升级成未读计数胶囊。
                if (this._maybeAutoScroll()) return;
                if (appended) this.setState(prev => ({ unreadCount: prev.unreadCount + 1 }));
            });
        };

        // 前面还有气泡在排队时，没有 typing_ms 的消息（按钮菜单就是）也要排在它们后面，
        // 否则菜单先落地，欢迎语随后才出现在菜单下方。
        if (wait > 0 || this._pacing || (this._pacedQueue && this._pacedQueue.length)) {
            // 顺序队列：多条 flow 气泡按序、逐条"typing→出泡"，不并发抢 agentTyping。
            (this._pacedQueue = this._pacedQueue || []).push({ commit, wait, showTyping });
            this._drainPacedQueue();
        } else {
            commit();
        }
    };

    // 顺序消费 paced 队列：逐条 显示 typing → 等 wait → 出泡。避免多条 flow
    // 消息并发导致 agentTyping 抖动/乱序。仅一个循环在跑（_pacing 互斥）。
    _drainPacedQueue = () => {
        if (this._pacing || !this._pacedQueue || !this._pacedQueue.length) return;
        this._pacing = true;
        const step = () => {
            if (!this._isMounted) { this._pacing = false; return; }
            const item = this._pacedQueue.shift();
            if (!item) { this._pacing = false; return; }
            if (item.showTyping) {
                // 只改状态，不碰 scrollTop —— typing 气泡的高度变化由
                // getSnapshotBeforeUpdate/componentDidUpdate 统一补偿（贴底则跟随，
                // 看历史则锚定不动）。以前这里直接 scrollToBottom 是最高频的"被拽回
                // 底部"来源：typing 每来一条消息就出现+消失一次。
                this.setState({ agentTyping: true });
            }
            this._paceTimer = setTimeout(() => {
                if (!this._isMounted) { this._pacing = false; return; }
                item.commit();
                // 队列空了才收起 typing；还有就留着让下一条接续
                if (!this._pacedQueue.length) this.setState({ agentTyping: false });
                step();
            }, item.wait);
        };
        step();
    };

    // 处理来自HTTP API的新消息（ChatController发送的 new_message 格式）
    handleNewMessage = (data) => {
        debugLog('Received new_message:', data);
        const msg = data.message;
        if (!msg) return;

        // 转换为 handleWebSocketMessage 期望的格式
        this.handleWebSocketMessage({
            type: 'message',
            id: msg.id,
            text: msg.text,
            from: msg.from_user,
            timestamp: msg.created_at,
            attachment: msg.attachment,
            reply_to: msg.reply_to,
            metadata: msg.metadata,
            message_type: msg.message_type || 'text',
            agent_name: msg.sender?.nickname || msg.sender?.username,
            agent_avatar: msg.sender?.avatar
        });
    };

    handleChatClosed = (data) => {
        debugLog('[Chat] Chat closed event:', data);
        if (data.chat_id && String(data.chat_id) === String(this.chat_id)) {
            // Add system message
            const systemMsg = {
                id: 'sys_closed_' + Date.now(),
                // 借用 call.session_ended 是历史遗留:同一事件却用了通话模块的
                // 文案键,改天有人改通话措辞会连带改这里。改用会话自己的键。
                text: t('chat.closed_notice'),
                from: 'system',
                type: 'system',
                timestamp: new Date().toISOString(),
                time: new Date().toISOString(),
            };
            // Add rating card if not already rated
            const ratingMsg = {
                id: 'rating_' + Date.now(),
                text: t('call.rate_prompt'),
                from: 'system',
                type: 'rating',
                chat_id: this.chat_id,
                timestamp: new Date().toISOString(),
                time: new Date().toISOString(),
            };
            this.setState(prevState => ({
                messages: [...prevState.messages, systemMsg, ratingMsg],
                chatClosed: true,
            }), () => {
                this._maybeAutoScroll();
            });
        }
        // Also update chat list if visible
        if (this.state.viewMode === 'chatList') {
            this.loadChatList();
        }
    }

    // 访客被封禁（Websocket.php banned 事件）：禁用输入区并插入一条系统提示。
    // 后端只在访客尝试发消息时下发，刚发出去的临时消息并未送达，一并清掉 sending 态。
    handleBanned = (data) => {
        debugLog('[Chat] Banned event:', data);
        if (this.state.banned) return; // 只提示一次
        const systemMsg = {
            id: 'sys_banned_' + Date.now(),
            text: t('chat.banned_notice'),
            from: 'system',
            type: 'system',
            timestamp: new Date().toISOString(),
            time: new Date().toISOString(),
        };
        this.setState(prevState => ({
            banned: true,
            messages: [
                ...prevState.messages.filter(msg => msg.status !== 'sending'),
                systemMsg,
            ],
        }), () => {
            this._maybeAutoScroll();
        });
    };

    handleTypingStatus = (data) => {
        // 同上：typing 气泡的出现/消失是纯高度变化，交给渲染补偿路径，
        // 这里一行 scrollTop 都不许写。
        this.setState({ agentTyping: !!data.typing });
        // 3秒后自动清除输入状态（防止未收到停止事件）
        if (this.typingTimer) clearTimeout(this.typingTimer);
        if (data.typing) {
            this.typingTimer = setTimeout(() => {
                this.setState({ agentTyping: false });
            }, 3000);
        }
    };

    /**
     * AI 流式预览。
     *
     * 走的是 typing 同一条 WS 通道（队列 worker → Channel IPC → WS），所以不
     * 需要 SSE。这里渲染的是一个**临时**气泡：服务端只在生成结束后落库一次，
     * 真消息随后通过普通的 message 帧到达，把这个临时气泡替换掉。
     *
     * 两个必须防住的情况：
     *   · end 帧没到（worker 崩了 / IPC 丢帧）→ 看门狗兜底清掉，否则半截
     *     文字会永远挂在会话里，看起来像 AI 说了一半就跑了。
     *   · 刷新页面 → 组件重挂，state 天然为空，历史接口返回的是落库后的完整
     *     消息，不会出现半截。
     */
    handleAiStream = (data) => {
        const phase = data && data.phase;

        // reset：服务端发现这一轮是工具调用，刚才那点文字不是最终答案 ——
        // 清掉预览、把"正在输入"放回来，等下一轮。
        if (phase === 'reset') {
            this._clearAiStreamWatchdog();
            this.setState({ aiStream: null, agentTyping: true });
            return;
        }

        if (phase === 'end') {
            this._clearAiStreamWatchdog();
            if (this.state.aiStream) this.setState({ aiStream: null });
            return;
        }

        if (phase === 'start') {
            // 有字了就别再转圈，否则"正在输入"的点和正在生成的文字会同时出现
            if (this.typingTimer) { clearTimeout(this.typingTimer); this.typingTimer = null; }
            // 预览气泡的头像/名字来自 start 帧（服务端用与落地真消息**同一**来源算好：
            // Websocket::resolveDisplayName + avatarUrl('')）。两阶段同源，交接瞬间头像不再跳。
            this.setState({ agentTyping: false, aiStream: {
                id: data.stream_id,
                text: '',
                agent_name: data.agent_name || null,
                agent_avatar: data.agent_avatar || null,
            } });
            this._armAiStreamWatchdog();
            return;
        }

        if (phase === 'delta') {
            const chunk = String(data.delta || '');
            if (!chunk) return;
            this.setState((st) => {
                // start 帧丢了也要能自愈：直接用这条 delta 起一个气泡
                const cur = st.aiStream && (!data.stream_id || st.aiStream.id === data.stream_id)
                    ? st.aiStream
                    : { id: data.stream_id, text: '' };
                return { agentTyping: false, aiStream: {
                    id: cur.id,
                    text: cur.text + chunk,
                    // start 帧带来的头像/名字要跨 delta 保持（delta 帧本身不带，省流量）
                    agent_name: cur.agent_name || data.agent_name || null,
                    agent_avatar: cur.agent_avatar || data.agent_avatar || null,
                } };
            });
            this._armAiStreamWatchdog();
        }
    };

    /**
     * 消息列表 + 流式临时气泡。
     *
     * 复用 MessageArea 既有的气泡渲染（头像 / 客服名 / 对齐 / markdown），
     * 不另写一套预览 UI —— 那必然会和真气泡长得不一样，替换的瞬间会跳。
     *
     * id 用固定的 '__ai_stream__'：MessageArea 按 id 做 key，稳定的 key 才能
     * 让文字增量走 DOM 更新而不是每帧重建整个气泡。
     */
    _messagesWithStream() {
        const st = this.state.aiStream;
        if (!st || !st.text) return this.state.messages;
        return this.state.messages.concat([{
            id: '__ai_stream__',
            text: st.text,
            from: 'admin',
            message_type: 'text',
            timestamp: new Date().toISOString(),
            is_ai: true,
            // 与落地真消息同源的头像/名字（来自 start 帧），消除交接瞬间的头像跳变
            agent_name: st.agent_name || null,
            agent_avatar: st.agent_avatar || null,
            _streaming: true,   // MessageArea 据此不显示时间戳、不挂长按菜单
        }]);
    }

    /**
     * 45s 没有新增量就认为流断了，清掉临时气泡。
     * 这只是兜底 —— 正常情况下预览由「真消息落地」或 end 帧清掉，两者哪个先到都行。
     */
    _armAiStreamWatchdog() {
        this._clearAiStreamWatchdog();
        this._aiStreamWatchdog = setTimeout(() => {
            this._aiStreamWatchdog = null;
            if (this.state.aiStream) this.setState({ aiStream: null });
        }, 45000);
    }

    _clearAiStreamWatchdog() {
        if (this._aiStreamWatchdog) { clearTimeout(this._aiStreamWatchdog); this._aiStreamWatchdog = null; }
    }

    handleAgentStatus = (data) => {
        // 可以在这里更新客服在线状态
    };

    // Flow-pushed teaser (pre-open nudge) arrives over WS now instead of the old
    // ~1.5s poll. The teaser UI lives in the OUTER widget (widget.js), so this
    // iframe forwards the payload up via postMessage — widget.js's handleMessage
    // picks up 'lane_teaser' and calls presentTeaser (which itself no-ops if the
    // panel is already open / dismissed). Harmless if not in an iframe.
    handleTeaserPush = (data) => {
        const payload = data && (data.teaser || data.payload);
        if (!payload || !payload.message) return;
        this._postToWidget({ type: 'lane_teaser', teaser: payload });
    };

    // 给外层 widget.js 发消息。注意:ChatFrame 是**直接** render <Chat/>(同一个 window,
    // 不是 iframe),所以不能只在 window.parent !== window 时才发——那样消息永远发不出去
    // (teaser WS push / first_visit 信号都曾因此静默失效)。同窗就 post 给自己,
    // widget.js 的 message 监听照样收得到,且 event.origin === location.origin 天然过白名单。
    _postToWidget = (payload) => {
        try {
            const target = (window.parent && window.parent !== window) ? window.parent : window;
            target.postMessage(payload, '*');
        } catch (e) { /* best-effort */ }
    };

    // GIF 网格滚到底部附近时由子组件回调；也用于「首屏没填满就再拉一页」。
    loadMoreGifs = () => {
        if (this.state.gifStatus !== 'ready') return;
        if (this.state.gifLoadingMore || !this.state.gifHasMore) return;
        // setState 是异步的，同一帧里连着两次滚动回调都会看到 gifLoadingMore=false，
        // 用实例标志立刻上锁，避免重复发同一页。
        if (this._gifMoreInFlight) return;
        this.sendGifSearch(this._gifKeyword || '', (this.state.mockGifs || []).length);
    };

    handleGifError = (payload) => {

        // 清掉本地兜底超时:服务端已经答了,再等它没有意义。

        clearTimeout(this._gifTimeout);

        clearTimeout(this._gifMoreTimeout);

        this._gifMoreInFlight = false;

        if (payload && payload.message) {

            // 只进日志,不进 UI——见注册处对 message 归属的说明。

            console.warn('[Lane.Chat] gif search failed:', payload.message);

        }

        this.setState({ gifStatus: 'error', gifLoadingMore: false });

    };


    handleGifSearchResults = (data) => {
        debugLog('Received GIF search results:', data);
        const offset = Number(data && data.offset) || 0;
        const isPage = offset > 0;
        if (isPage) {
            clearTimeout(this._gifMoreTimeout);
            this._gifMoreInFlight = false;
        } else {
            clearTimeout(this._gifTimeout);
        }
        // 关键词对不上 = 用户已经改了搜索词，这批是上一轮的回声，丢掉。
        // （服务端把 keyword 原样回带；老服务端没这个字段就不做对账。）
        if (typeof data.keyword === 'string' && typeof this._gifKeyword === 'string'
            && data.keyword !== this._gifKeyword) {
            if (isPage) this.setState({ gifLoadingMore: false });
            return;
        }
        if (!Array.isArray(data.data)) {
            this.setState(isPage ? { gifLoadingMore: false } : { gifStatus: 'error' });
            return;
        }
        const batch = data.data;
        // has_more 由服务端按「这批是否取满」给；老服务端没有就退回看空数组。
        const hasMore = (typeof data.has_more === 'boolean') ? data.has_more : batch.length > 0;
        if (!isPage) {
            this.setState({ mockGifs: batch, gifStatus: 'ready', gifLoadingMore: false, gifHasMore: hasMore });
            return;
        }
        this.setState((prev) => {
            // Giphy 翻页偶尔会重发同一条，去重免得 React key 撞车
            const seen = new Set((prev.mockGifs || []).map(g => g.id));
            const added = batch.filter(g => g && !seen.has(g.id));
            return {
                mockGifs: (prev.mockGifs || []).concat(added),
                gifLoadingMore: false,
                gifHasMore: hasMore && added.length > 0,
            };
        });
    };

    // 服务端请求级错误（type:'error'）：进消息流当系统提示，不再静默丢弃。
    // error 文本可能是服务端的英文/中文提示（如功能开关关闭），原样展示。
    handleServerError = (data) => {
        console.warn('[Chat] Server error frame:', data);
        const text = (data && data.error) ? String(data.error) : t('status.connection_lost');
        this.writeToMessages({
            id: 'server-error-' + Date.now(),
            from: 'system',
            type: 'text',
            text,
            time: new Date()
        });
    };

    // ========== Realtime 通话相关方法 ==========

    // 处理通话发起成功（发起方收到自己的 token）
    handleCallInitiated = (data) => {
        debugLog('Call initiated:', data);
        const { auth_token, meeting_id, call_session_id, call_type } = data;

        // 保存 authToken 和会话信息
        this.currentCallData = {
            authToken: auth_token,
            meetingId: meeting_id,
            sessionId: call_session_id,
            callType: call_type,
            isInitiator: true  // 标记为发起方
        };

        // 显示通话界面
        this.setState({ showCallUI: true, callPeerAccepted: false });
    };

    handleIncomingCall = (data) => {
        debugLog('Incoming call:', data);
        const { call_type, from_client_id, from_name, meeting_id } = data;

        this.setState({
            incomingCall: {
                type: call_type,
                fromClientId: from_client_id,
                fromName: from_name || t('chat.reply_agent'),
                meetingId: meeting_id
            }
        });

        // 显示来电通知消息
        let callTypeName = t('call.type_call');
        if (call_type === 'audio') callTypeName = t('chat.voice_call');
        else if (call_type === 'video') callTypeName = t('chat.video_call');
        else if (call_type === 'screen' || call_type === 'screen_share') callTypeName = t('chat.screen_share');
        else if (call_type === 'screen_request') callTypeName = t('call.type_screen_request');

        this.writeToMessages({
            id: 'call-' + Date.now(),
            from: 'system',
            type: 'call_notification',
            text: t('call.invite', { name: from_name || t('chat.reply_agent'), type: callTypeName }),
            call_data: {
                type: call_type,
                fromClientId: from_client_id,
                fromName: from_name
            },
            time: new Date()
        });
    };

    handleCallAccepted = (data) => {
        debugLog('Call accepted:', data);
        const { auth_token, meeting_id, call_session_id } = data;

        // 如果包含 auth_token，说明是自己接受后收到的响应
        if (auth_token) {
            this.currentCallData = {
                authToken: auth_token,
                meetingId: meeting_id,
                sessionId: call_session_id,
                callType: this.state.incomingCall?.type || 'audio',
                isInitiator: false  // 标记为接受方
            };

            this.setState({ showCallUI: true, incomingCall: null });
        } else {
            // 否则是对方接受了我们发起的通话。除了消息流提示，还要把"已接听"
            // 信令喂给通话 UI 状态机——不然媒体房间对不上时，访客会一直看着
            // "等待对方接听"，而对端其实早就接了（真实事故的字面症状）。
            if (this.currentCallData && this.currentCallData.isInitiator) {
                this.setState({ callPeerAccepted: true });
            }
            this.writeToMessages({
                id: 'call-accepted-' + Date.now(),
                from: 'system',
                type: 'text',
                text: t('call.connected'),
                time: new Date()
            });
        }
    };

    handleCallRejected = (data) => {
        debugLog('Call rejected:', data);

        // 清理通话状态（可能已经在等待界面）
        this.currentCallData = null;
        this.setState({
            showCallUI: false,
            incomingCall: null
        });

        this.writeToMessages({
            id: 'call-rejected-' + Date.now(),
            from: 'system',
            type: 'text',
            text: t('call.declined'),
            time: new Date()
        });
    };

    handleCallEnded = (data) => {
        debugLog('Call ended:', data);

        // 清理通话状态
        this.currentCallData = null;
        this.setState({
            showCallUI: false,
            incomingCall: null
        });

        this.writeToMessages({
            id: 'call-ended-' + Date.now(),
            from: 'system',
            type: 'text',
            text: t('call.ended'),
            time: new Date()
        });
    };

    // 通话 UI 崩溃（chunk 拉取失败/渲染异常）时的收尾：通知服务端结束通话
    // （否则对端会一直振铃到 90s 超时/独自留在房间里），清本地状态，给访客一条
    // 看得见的失败提示。由 CallErrorBoundary.componentDidCatch 调用。
    handleCallUiFatal = () => {
        const data = this.currentCallData;
        try {
            WebSocketService.send({
                type: 'call_end',
                chat_id: this.chat_id,
                session_id: data ? data.sessionId : undefined,
                client_id: this.client_id
            });
        } catch (e) { /* WS 不可用时也要继续清 UI */ }
        this.currentCallData = null;
        this.setState({ showCallUI: false, incomingCall: null, callPeerAccepted: false });
        this.writeToMessages({
            id: 'call-fatal-' + Date.now(),
            from: 'system',
            type: 'text',
            text: t('call.connect_failed'),
            time: new Date()
        });
    };

    handleCallBusy = (data) => {
        debugLog('Call busy:', data);
        // 清除来电通知
        this.setState({ incomingCall: null });

        // 可选：显示提示消息
        if (data.message) {
            this.writeToMessages({
                id: 'call-busy-' + Date.now(),
                from: 'system',
                type: 'text',
                text: data.message,
                time: new Date()
            });
        }
    };

    handleCallCancelled = (data) => {
        debugLog('Call cancelled:', data);
        // 清除来电通知
        this.setState({ incomingCall: null });

        // 显示提示消息
        this.writeToMessages({
            id: 'call-cancelled-' + Date.now(),
            from: 'system',
            type: 'text',
            text: t('call.cancelled'),
            time: new Date()
        });
    };

    /**
     * Handle TTS audio from agent — play it locally so the visitor hears it
     */
    handleTtsPlay = (data) => {
        const url = data?.data?.url;
        if (!url) return;
        debugLog('[TTS] Playing agent TTS audio:', url);
        try {
            const audio = new Audio(url);
            audio.volume = 1.0;
            audio.play().catch(e => console.warn('[TTS] Audio play failed:', e));
        } catch (e) {
            console.error('[TTS] Failed to create audio:', e);
        }
    };

    startAudioCall = async () => {
        if (!this.chat_id || !this.client_id) {
            console.warn('Chat ID or Client ID not available yet');
            alert(t('call.error_not_ready'));
            return;
        }
        // 原生容器里先问一次系统权限——被拒就别再去振铃对方了，直接引导条。
        // 无 bridge / 网页端：ensureMediaPermission 恒返回 'proceed'，行为不变。
        if (await ensureMediaPermission('microphone') === 'blocked') {
            this.setState({ permissionGuidance: { kind: 'microphone' } });
            return;
        }
        this.setState({ permissionGuidance: null });
        try {
            // 通过 WebSocket 发送通话请求
            WebSocketService.send({
                type: 'call_request',
                chat_id: this.chat_id,
                client_id: this.client_id,
                app_id: this.props.appId,
                call_type: 'audio'
            });

            debugLog('Audio call request sent');
        } catch (error) {
            console.error('Failed to start audio call:', error);
            alert(t('call.error_start_audio_failed'));
        }
    };

    startVideoCall = async () => {
        if (!this.chat_id || !this.client_id) {
            console.warn('Chat ID or Client ID not available yet');
            alert(t('call.error_not_ready'));
            return;
        }
        // 视频通话要两个权限都过：先问麦克风，再问摄像头，谁先被拒就引导谁
        // （两个都拒的情况下，麦克风先展示——跟 startAudioCall 的引导条一致，
        // 用户按提示去设置页开完麦克风回来，再点一次会接着问到摄像头）。
        if (await ensureMediaPermission('microphone') === 'blocked') {
            this.setState({ permissionGuidance: { kind: 'microphone' } });
            return;
        }
        if (await ensureMediaPermission('camera') === 'blocked') {
            this.setState({ permissionGuidance: { kind: 'camera' } });
            return;
        }
        this.setState({ permissionGuidance: null });
        try {
            // 通过 WebSocket 发送通话请求
            WebSocketService.send({
                type: 'call_request',
                chat_id: this.chat_id,
                client_id: this.client_id,
                app_id: this.props.appId,
                call_type: 'video'
            });

            debugLog('Video call request sent');
        } catch (error) {
            console.error('Failed to start video call:', error);
            alert(t('call.error_start_video_failed'));
        }
    };

    startScreenShare = async () => {
        if (!this.chat_id || !this.client_id) {
            console.warn('Chat ID or Client ID not available yet');
            alert(t('call.error_not_ready'));
            return;
        }

        // 检查浏览器是否支持屏幕共享
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
            alert(t('call.error_screenshare_unsupported'));
            return;
        }

        try {
            // 通过 WebSocket 发送通话请求 (客户端只能共享自己的屏幕)
            WebSocketService.send({
                type: 'call_request',
                chat_id: this.chat_id,
                client_id: this.client_id,
                app_id: this.props.appId,
                call_type: 'screen_share'  // 客户端只支持共享自己的屏幕
            });

            debugLog('Screen share request sent');
        } catch (error) {
            console.error('Failed to start screen share:', error);
            alert(t('call.error_start_screenshare_failed'));
        }
    };

    acceptCall = async () => {
        const { incomingCall } = this.state;
        if (!incomingCall) return;

        // 接听方也要过一遍权限预检——发起方在 startAudioCall/startVideoCall 已经
        // 查过自己这端，但对方振铃时接听方从没被问过。被拒就展示引导条、保留来电
        // 让访客可以去设置页开权限后再点一次接听，不自动挂断（跟发起方 blocked
        // 时不发 call_request 是同一个"不确定就不强推进"的原则）。
        if (incomingCall.type === 'audio' || incomingCall.type === 'video') {
            if (await ensureMediaPermission('microphone') === 'blocked') {
                this.setState({ permissionGuidance: { kind: 'microphone' } });
                return;
            }
            if (incomingCall.type === 'video' && await ensureMediaPermission('camera') === 'blocked') {
                this.setState({ permissionGuidance: { kind: 'camera' } });
                return;
            }
            this.setState({ permissionGuidance: null });
        }

        // 检查是否为需要共享屏幕的呼叫类型
        if (incomingCall.type === 'screen_request') {
            // 检查浏览器是否支持屏幕共享
            const isScreenShareSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
            if (!isScreenShareSupported) {
                alert(t('call.error_screenshare_unsupported_incoming'));
                this.setState({ incomingCall: null });
                return;
            }

            // 检查是否为移动设备
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobileDevice) {
                alert(t('call.error_screenshare_mobile_incoming'));
                this.setState({ incomingCall: null });
                return;
            }
        }

        try {
            // 通过 WebSocket 发送接受响应
            WebSocketService.send({
                type: 'call_response',
                action: 'accept',
                chat_id: this.chat_id,
                client_id: this.client_id,
                app_id: this.props.appId,
                meeting_id: incomingCall.meetingId,
                call_type: incomingCall.type
            });

            debugLog('Call accepted, waiting for auth token...');
        } catch (error) {
            console.error('Failed to accept call:', error);
            this.setState({ incomingCall: null });
        }
    };

    rejectCall = () => {
        const { incomingCall } = this.state;
        if (!incomingCall) return;

        // 通知对方拒绝通话
        WebSocketService.send({
            type: 'call_response',
            action: 'reject',
            chat_id: this.chat_id,
            client_id: this.client_id,
            meeting_id: incomingCall.meetingId
        });

        this.setState({ incomingCall: null });
    };

    // ========== composer 语音消息录制 ==========
    // 交互选了"点击开始/点击停止"（tap-to-toggle），不是"按住说话"（hold-to-talk）：
    //   1. widget 跑在 iframe/WebView 里，长按手势会跟移动端浏览器自己的"长按选词/
    //      呼出上下文菜单"打架，要做对需要额外吞事件；tap 没有这个冲突。
    //   2. 这个 composer 目前所有交互（通话按钮、表情、GIF）都是点击语义，没有
    //      任何按住类交互先例——插入一个新模式反而不一致。
    //   3. 桌面端（这条 widget 相当比例的访问来源）鼠标"按住"没有自然的对应操作，
    //      要做对得处理 mousedown/mouseleave/mouseup 一整套边界，量级不小。
    // Telegram Desktop / WhatsApp Web 的桌面 composer 同样是点击式，不是按住式。

    startVoiceRecording = async () => {
        if (this.state.banned || this.state.emailGateLocked) return;
        if (this.state.voiceRecording || this.state.voicePreview) return; // 已经在录/已有待发预览

        if (typeof MediaRecorder === 'undefined') {
            this.writeToMessages({ from: 'system', type: 'system', text: t('voice.unsupported'), time: new Date() });
            return;
        }

        // 复用通话那条预检：bridge active 就先问系统权限，blocked 直接引导条，
        // 不再往下走（这是权限三件套的第一个真实消费场景之外，语音是第二个）。
        if (await ensureMediaPermission('microphone') === 'blocked') {
            this.setState({ permissionGuidance: { kind: 'microphone' } });
            return;
        }
        this.setState({ permissionGuidance: null });

        const session = new VoiceRecorderSession({
            onTick: (seconds) => {
                // session 可能已经被 cancelVoiceRecording 换掉/清空，tick 到达时
                // 如果不是当前这个 session 了就别再更新 state（避免旧 timer 的
                // 最后一次回调在取消之后又把 voiceRecording 点亮回来）。
                if (this._voiceRecorder !== session) return;
                this.setState({ voiceRecording: { seconds } });
            },
            onStop: ({ blob, mimeType, seconds }) => {
                if (this._voiceRecorder !== session) return;
                this._voiceRecorder = null;
                const url = URL.createObjectURL(blob);
                this.setState({ voiceRecording: null, voicePreview: { blob, mimeType, seconds, url } });
            },
            onError: (reason) => {
                if (this._voiceRecorder === session) this._voiceRecorder = null;
                this.setState({ voiceRecording: null });
                if (reason === 'denied') {
                    // 纯 web 场景：ensureMediaPermission 放行了(没有 bridge)，
                    // 但浏览器原生权限框本身被拒绝——同一条引导条兜底。
                    this.setState({ permissionGuidance: { kind: 'microphone' } });
                } else {
                    this.writeToMessages({ from: 'system', type: 'system', text: t('voice.unsupported'), time: new Date() });
                }
            },
        });
        this._voiceRecorder = session;
        const started = await session.start();
        if (started) {
            this.setState({ voiceRecording: { seconds: 0 } });
        }
    };

    // 点击"停止" → 正常结束，转入预览态（onStop 回调里 setState voicePreview）。
    stopVoiceRecording = () => {
        if (this._voiceRecorder) this._voiceRecorder.stop();
    };

    // 点击"取消"（录音中）→ 直接放弃，不进预览。
    cancelVoiceRecording = () => {
        if (this._voiceRecorder) {
            this._voiceRecorder.cancel();
            this._voiceRecorder = null;
        }
        this.setState({ voiceRecording: null });
    };

    // 预览态点击"丢弃" → 放弃这条录音，回到正常 composer。
    discardVoicePreview = () => {
        const preview = this.state.voicePreview;
        if (preview && preview.url) {
            try { URL.revokeObjectURL(preview.url); } catch (e) {}
        }
        this.setState({ voicePreview: null });
    };

    // 预览态点击"发送" → 走既有上传管线(kind='voice')，成功后走既有 sendMessage
    // 统一发送入口（跟图片附件同一条路，attachment 就是上传返回的 URL 字符串）。
    sendVoicePreview = async () => {
        const preview = this.state.voicePreview;
        if (!preview || this.state.voiceSending) return;
        this.setState({ voiceSending: true });
        try {
            const ext = preview.mimeType && preview.mimeType.includes('mp4') ? 'mp4'
                : (preview.mimeType && preview.mimeType.includes('ogg') ? 'ogg' : 'webm');
            const file = new File([preview.blob], `voice-${Date.now()}.${ext}`, { type: preview.mimeType || 'audio/webm' });
            const uploadResult = await ApiService.uploadFile(file, this.props.appId, this.state.chat_id, null, 'voice');
            if (uploadResult.code !== 0) {
                throw new Error(uploadResult.msg || 'Upload failed');
            }
            await this.sendMessage('', uploadResult.data.url);
        } catch (error) {
            console.error('Voice upload failed:', error);
            this.writeToMessages({ from: 'system', type: 'system', text: t('voice.upload_failed'), time: new Date() });
        } finally {
            if (preview.url) {
                try { URL.revokeObjectURL(preview.url); } catch (e) {}
            }
            this.setState({ voiceSending: false, voicePreview: null });
        }
    };

    // 之前只判断"有没有选文件"——选了张 20MB 大图或非图片文件，点发送后观感就是
    // "按了没反应"（没有校验、没有反馈）。这里补 type/size 校验，不过关就给一条
    // 系统提示消息（和 chat.banned_notice 等既有系统提示同一套渲染，不额外加 UI）。
    MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB，与常见上传网关限制对齐

    handleImageChange = (e) => {
        e.preventDefault();

        let file = e.target.files?.[0];
        if (!file) return;

        if (!file.type || !file.type.startsWith('image/')) {
            this.writeToMessages({ from: 'system', type: 'system', text: t('chat.image_invalid_type'), time: new Date() });
            e.target.value = '';
            return;
        }
        if (file.size > this.MAX_IMAGE_SIZE_BYTES) {
            this.writeToMessages({ from: 'system', type: 'system', text: t('chat.image_too_large'), time: new Date() });
            e.target.value = '';
            return;
        }

        let reader = new FileReader();

        reader.onloadend = () => {
            this.setState({
                imagePreviewUrl: reader.result,
                imageFile: file,
                showSendButton: true
            });
        }

        reader.readAsDataURL(file);
    }

    handleDeleteImage = () => {
        this.setState({
            imagePreviewUrl: null,
            imageFile: null,
            showSendButton: this.input.value.length > 0
        });
    }

    // No-op: the input bar is now a normal flex-flow item at the bottom (see
    // .pc-area/.mobile-area CSS), so chat-container(flex:1) naturally sits above
    // it and the message list keeps its normal CSS padding — no measured
    // bottom-padding hack needed. Kept as a stub so existing call sites are safe.
    updateChatBottomPadding = () => {};

    adjustTextareaHeight = () => {
        const textarea = this.textareaRef.current;
        const texContainerRef = this.texContainerRef.current;

        if (!textarea) return;

        // 真实测量：先塌到 auto 拿 scrollHeight，再夹在 [minH, maxH]。
        // 不再按字符数粗估折行（CJK/等宽/emoji 都算不准，导致高度跳变或裁字）。
        const lineHeight = 18;
        const maxRows = 5;                  // 自动增高上限（原 10 行太高，挤压消息区）
        const minH = 38;                   // 与 CSS min-height 对齐 → 空态 1 行
        const maxH = lineHeight * maxRows; // 5 行封顶

        textarea.style.height = 'auto';
        const contentH = textarea.scrollHeight;
        const heightRow = Math.max(minH, Math.min(contentH, maxH));
        textarea.style.height = `${heightRow}px`;
        textarea.style.overflowY = contentH > maxH ? 'auto' : 'hidden';
        // Re-sync message-list bottom clearance to the (now-updated) bar height.
        requestAnimationFrame(this.updateChatBottomPadding);
        // NOTE: the textarea uses a fixed rows={1}; height is controlled purely
        // here via scrollHeight. Do NOT feed a computed `rows` back into the
        // textarea — that inflated its intrinsic height and created a monotonic
        // growth feedback loop (a few chars → very tall, never shrank).

        // 更新主容器高度，让chat-container自然占据剩余空间
        /*
        const mainContainer = this.mainContainer;
        if (mainContainer) {
            // const bottomHeight = calculateBottomHeight();
            let bottomHeight = this.isMobile ? 130 : 130;
            // 主容器高度 = 100% - pc-area实际高度
            mainContainer.style.height = `calc(100% - ${bottomHeight}px)`;

            debugLog('Main container height adjusted:', `calc(100% - ${bottomHeight}px)`, 'isMobile:', this.isMobile);
        }*/

        // chat-container高度由CSS padding-bottom控制（chat-frame.js），不再用JS设置height

        // emoji / GIF 弹层的高度不再由这里算：它们 inset:0 铺满 .emoji-pick /
        // .giphy，而那两层的下边界已经由 chat-frame 按 composer 高度收好。
        // 旧代码用 `calc(100% - textareaH - 50/96)` 这种魔数，面板尺寸模型
        // 改成 min(700, 100vh-48) 之后必然对不上（矮视口下弹层被压成一条）。
    }

    instantScrollToBottom = () => {
        if (this.chatContainer) {
            this.chatContainer.style.scrollBehavior = 'auto';
            this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
            requestAnimationFrame(() => {
                if (this.chatContainer) {
                    this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
                    this.chatContainer.style.scrollBehavior = 'smooth';
                }
            });
        }
    }

    scrollToBottom = () => {
        if (this.chatContainer) {
            requestAnimationFrame(() => {
                if (!this.chatContainer) return;

                const images = this.chatContainer.querySelectorAll('img, video');
                let loadedCount = 0;
                const totalMedia = images.length;

                const doScroll = () => {
                    if (!this.chatContainer) return;
                    // 这条路径会等容器里所有 img/video 加载完才滚 —— 中间可能过了好几秒，
                    // 用户早就滚上去看历史了。滚之前再确认一次意图仍然成立，否则作废。
                    if (!this._isAutoFollowing() && !this._isStuckToBottom()) return;
                    this.chatContainer.style.scrollBehavior = 'smooth';
                    this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
                };

                if (totalMedia === 0) {
                    doScroll();
                    return;
                }

                const checkAllLoaded = () => {
                    loadedCount++;
                    if (loadedCount === totalMedia) {
                        doScroll();
                    }
                };

                images.forEach((media) => {
                    if (media.complete || media.readyState === 4) {
                        checkAllLoaded();
                    } else {
                        media.addEventListener('load', checkAllLoaded, { once: true });
                        media.addEventListener('loadeddata', checkAllLoaded, { once: true });
                        media.addEventListener('error', checkAllLoaded, { once: true });
                    }
                });

                // Fallback timeout in case media events don't fire
                setTimeout(() => {
                    doScroll();
                }, 1500);
            });
        }
    }

    // 空态：还没有任何消息时，展示客服头像 + 欢迎语，避免打开后一片惨白。
    //
    // 身份三件套**不在这里现算**——现算就等于每次 setState 都重解一遍兜底链，
    // 数据分步到达时肉眼可见地变脸（用户实测三次）。它们由 _settleEmptyIdentity
    // 在闸门打开的那一刻解析一次并冻结；本方法只有在 emptyIdentity 非空时才会被
    // 调用（见 render() 的空态分支），所以这里不再需要任何兜底。
    renderChatEmptyState = () => {
        const conf = this.props.conf || {};
        // 闸门之外调用是调用方的错，但解构 null 会抛 TypeError 把整个 widget
        // 炸成白屏 —— 这一格宁可什么都不画。
        if (!this.state.emptyIdentity) return null;
        const { title, avatarUrl, welcome } = this.state.emptyIdentity;
        // 品牌色不冻结：它归 colorKnown 闸门管，且要跟随亮暗色切换。
        const mainColor = conf.mainColor || '#2563eb';

        return (
            <div className="lc-empty-state" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', textAlign: 'center',
                minHeight: '100%', padding: '24px 28px', gap: 'var(--lc-space-2)',
            }}>
                <div className="lc-empty-avatar" style={{
                    width: '56px', height: '56px', borderRadius: 'var(--lc-radius-full)',
                    overflow: 'hidden', flexShrink: 0,
                    background: avatarUrl ? 'transparent' : mainColor,
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '22px', fontWeight: 600,
                    boxShadow: 'var(--lc-shadow-2)',
                }}>
                    {avatarUrl
                        ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span>{(title || 'CS').charAt(0).toUpperCase()}</span>}
                </div>
                <div className="lc-empty-title" style={{ fontSize: '15px', fontWeight: 600, color: 'var(--lc-text, #1e293b)', marginTop: '2px' }}>{title}</div>
                <div className="lc-empty-welcome" style={{
                    fontSize: '13.5px', color: 'var(--lc-text-2, #475569)', lineHeight: 1.5,
                    background: 'var(--lc-surface, #fff)', border: '1px solid #eef2f7',
                    borderRadius: 'var(--lc-radius-md)', padding: '10px 14px', maxWidth: '260px',
                    boxShadow: 'var(--lc-shadow-1)', whiteSpace: 'pre-wrap',
                }}>{welcome}</div>
            </div>
        );
    }

    // 加载骨架：消息拉取中且尚无消息时占位，替代那个只存活一帧的转圈。
    renderChatSkeleton = () => {
        const row = (fromVisitor, w) => (
            <div style={{
                display: 'flex', gap: 'var(--lc-space-2)', marginBottom: '14px',
                justifyContent: fromVisitor ? 'flex-end' : 'flex-start',
                alignItems: 'flex-end',
            }}>
                {!fromVisitor && <div className="lc-skel-avatar" />}
                <div className="lc-skel-bubble" style={{ width: w, height: '38px' }} />
            </div>
        );
        return (
            <div style={{ padding: '18px 16px' }}>
                {row(false, '62%')}
                {row(true, '48%')}
                {row(false, '70%')}
            </div>
        );
    }

    render() {
        const { imagePreviewUrl, textareaRows, showEmojiPicker, isMobile } = this.state;
        let imageOrUploadComponent;

        if (imagePreviewUrl) {
            imageOrUploadComponent = (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={imagePreviewUrl} style={{ height: '30px', width: 'auto' }} alt="Preview" />
                    <button onClick={this.handleDeleteImage} className="delete-button" title="Delete image">
                        <LcIcon name="close" size={12} />
                    </button>
                </div>
            );
        } else {
            imageOrUploadComponent = (
                <div className="uploadimg">
                    <input type="file" id="file" className="fileInput" onChange={this.handleImageChange} accept="image/*" />
                    <label htmlFor="file" className="upload-label">
                        <svg
                            style={{
                                width: '24px',
                                height: '24px'
                            }}
                            t="1723989118670" className="icon" viewBox="0 0 1024 1024" version="1.1"
                            xmlns="http://www.w3.org/2000/svg" p-id="7695" id="mx_n_1723989118670" width="200"
                            height="200">
                            <path
                                d="M779.3 196.6c-94.2-94.2-247.6-94.2-341.7 0l-261 260.8c-1.7 1.7-2.6 4-2.6 6.4s0.9 4.7 2.6 6.4l36.9 36.9c3.5 3.5 9.2 3.5 12.7 0l261-260.8c32.4-32.4 75.5-50.2 121.3-50.2s88.9 17.8 121.2 50.2c32.4 32.4 50.2 75.5 50.2 121.2 0 45.8-17.8 88.8-50.2 121.2l-266 265.9-43.1 43.1c-40.3 40.3-105.8 40.3-146.1 0-19.5-19.5-30.2-45.4-30.2-73s10.7-53.5 30.2-73l263.9-263.8c6.7-6.6 15.5-10.3 24.9-10.3h0.1c9.4 0 18.1 3.7 24.7 10.3 6.7 6.7 10.3 15.5 10.3 24.9 0 9.3-3.7 18.1-10.3 24.7L372.4 653c-1.7 1.7-2.6 4-2.6 6.4s0.9 4.7 2.6 6.4l36.9 36.9c3.5 3.5 9.2 3.5 12.7 0l215.6-215.6c19.9-19.9 30.8-46.3 30.8-74.4s-11-54.6-30.8-74.4c-41.1-41.1-107.9-41-149 0L463 364 224.8 602.1C192 634.9 174 678.4 174 724.8c0 46.3 18.1 89.8 50.8 122.5 33.9 33.8 78.3 50.7 122.7 50.7 44.4 0 88.8-16.9 122.6-50.7l309.2-309C824.8 492.7 850 432 850 367.5c0.1-64.6-25.1-125.3-70.7-170.9z"
                                p-id="7696" fill="#8a8a8a"></path>
                        </svg>
                    </label>
                </div>
            );
        }

        return (
            <div ref={(el) => { this.mainContainer = el; }} style={{height: '100%'}}>
                {/* 会话列表视图 - show/hide via display；上层视图 → pop（从左入） */}
                <div style={{
                    display: this.state.viewMode === 'chatList' ? 'block' : 'none',
                    height: '100%',
                    ...this._navAnimStyle('list', 'lcViewPopA'),
                }}>
                    {this.renderChatList()}
                </div>

                {/* 主聊天视图 - show/hide via display；下层视图 → push（从右入） */}
                <div style={{
                    display: this.state.viewMode === 'chatList' ? 'none' : 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    ...this._navAnimStyle('chat', 'lcViewPushA'),
                }}>
                    {/* Realtime 通话界面 */}
                    {this.state.showCallUI && this.currentCallData && (
                        <CallErrorBoundary key={'call-eb-' + (this.currentCallData.meetingId || '')} onFatal={this.handleCallUiFatal}>
                        <React.Suspense fallback={
                            <div style={{
                                position: 'fixed', inset: 0, zIndex: 100000,
                                background: 'rgba(15,15,20,0.85)', backdropFilter: 'blur(4px)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--lc-space-4)',
                            }}>
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: 'var(--lc-radius-full)',
                                    border: '3px solid rgba(255,255,255,0.25)',
                                    borderTopColor: '#fff', animation: 'spin 0.8s linear infinite',
                                }} />
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 500, letterSpacing: '0.01em' }}>
                                    {t('call.connecting') || 'Connecting…'}
                                </div>
                            </div>
                        }>
                            <RealtimeCall
                                chatId={this.chat_id}
                                clientId={this.client_id}
                                appId={this.props.appId}
                                authToken={this.currentCallData.authToken}
                                sessionId={this.currentCallData.sessionId}
                                meetingId={this.currentCallData.meetingId}
                                callType={this.currentCallData.callType}
                                isInitiator={this.currentCallData.isInitiator}
                                peerAccepted={this.state.callPeerAccepted}
                                onClose={() => {
                                    this.currentCallData = null;
                                    this.setState({ showCallUI: false, callPeerAccepted: false });
                                }}
                            />
                        </React.Suspense>
                        </CallErrorBoundary>
                    )}

                    {/* 来电通知 */}
                    {this.state.incomingCall && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.85)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10000,
                            animation: 'fadeIn var(--lc-dur-slow) var(--lc-ease-out)'
                        }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                                borderRadius: 'var(--lc-radius-lg)',
                                padding: 'var(--lc-space-8)',
                                maxWidth: '400px',
                                width: '90%',
                                boxShadow: 'var(--lc-shadow-3)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                animation: 'slideUp var(--lc-dur-slow) var(--lc-ease-out)'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: 'var(--lc-radius-full)',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '20px',
                                    animation: 'pulse 2s ease-in-out infinite'
                                }}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="#fff">
                                        <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                                    </svg>
                                </div>
                                <div style={{textAlign: 'center', marginBottom: 'var(--lc-space-8)'}}>
                                    <div style={{fontSize: '24px', fontWeight: 600, color: '#fff', marginBottom: 'var(--lc-space-2)'}}>
                                        {this.state.incomingCall.fromName}
                                    </div>
                                    <div style={{fontSize: '16px', color: '#999'}}>
                                        {this.state.incomingCall.type === 'audio' && t('chat.voice_call')}
                                        {this.state.incomingCall.type === 'video' && t('chat.video_call')}
                                        {this.state.incomingCall.type === 'screen' && t('chat.screen_share')}
                                    </div>
                                </div>
                                <div style={{display: 'flex', gap: 'var(--lc-space-4)', width: '100%'}}>
                                    <button style={{
                                        flex: 1,
                                        padding: '14px 24px',
                                        border: 'none',
                                        borderRadius: 'var(--lc-radius-sm)',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: '#fff',
                                        transition: 'all var(--lc-dur-base) var(--lc-ease-out)'
                                    }} onClick={this.acceptCall}>
                                        {t('call.accept')}
                                    </button>
                                    <button style={{
                                        flex: 1,
                                        padding: '14px 24px',
                                        border: 'none',
                                        borderRadius: 'var(--lc-radius-sm)',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        color: '#fff',
                                        transition: 'all var(--lc-dur-base) var(--lc-ease-out)'
                                    }} onClick={this.rejectCall}>
                                        {t('call.reject')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Emoji picker rendered via portal (outside Shadow DOM) to fix Web Components error */}
                    {this.state.showEmojiPicker && this.emojiPortalContainer && (() => {
                        if (!this.Picker) {
                            return createPortal(
                                <div style={{padding: '20px', background: 'var(--lc-surface, white)', textAlign: 'center', zIndex: 99999}}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        border: '3px solid var(--lc-surface-2, #f3f3f3)',
                                        borderTop: '3px solid #1972F5',
                                        borderRadius: 'var(--lc-radius-full)',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto'
                                    }}></div>
                                </div>,
                                this.emojiPortalContainer
                            );
                        }

                        const Picker = this.Picker;
                        return createPortal(
                            <div className="emoji-pick" ref={this.emojiPickerRef}>
                                <Picker
                                    data={this.data}
                                    onEmojiSelect={this.handleEmojiSelect}
                                    previewPosition="none"
                                    dynamicWidth={true}
                                />
                            </div>,
                            this.emojiPortalContainer
                        );
                    })()}
                {this.state.showGiphy && <div className="giphy emoji-pick" ref={this.gifPickerRef}>
                    {this.GiphySearch && <this.GiphySearch
                        status={this.state.gifStatus}
                        onRetry={this.sendGifSearch}
                        loadingMore={this.state.gifLoadingMore}
                        hasMore={this.state.gifHasMore}
                        onLoadMore={this.loadMoreGifs}
                        mockGifs={this.state.mockGifs}
                        onGifSelect={this.handleGifSelect}
                        onSearchTermChange={this.handleGiphySearchTermChange}
                    />}
                </div>}
                <div className={`chat-container ${this.state.replyToMessage ? 'has-reply' : ''}`} ref={(el) => { this.chatContainer = el; }}>
                    {/* 空态闸门（与原生 chatEmptyStateVisible 同一判定）：身份未定时
                        画中性骨架，绝不用兜底值先画一张会变的卡；定成"没有卡"时骨架
                        也撤掉，这一格空着（终态，不是永远转圈）。骨架 → 卡片这一次
                        切换是允许的，卡片一旦出现，标题/头像/欢迎语不再变。 */}
                    {this.state.messages.length === 0
                        && (this.state.loadingHistory || !this.state.emptySettled)
                        && this.renderChatSkeleton()}
                    {chatEmptyStateVisible({
                        messagesEmpty: this.state.messages.length === 0,
                        initialHistoryPending: this.state.loadingHistory,
                        identityKnown: !!this.state.emptyIdentity,
                    }) && this.renderChatEmptyState()}
                    <MessageArea
                        messages={this._messagesWithStream()}
                        conf={this.getMessageAreaConf()}
                        chatId={this.chat_id}
                        onReplyMessage={this.handleReplyMessage}
                        onButtonClick={this.handleButtonClick}
                        interactionLocked={!!(this.state.chatClosed || this.state.banned)}
                        scrollToMessageId={this.state.scrollToMessageId}
                        onImageLoad={this.handleImageLoad}
                        loadingHistory={this.state.loadingHistory}
                        hasMoreMessages={this.state.hasMoreMessages}
                        onServiceAction={this.handleServiceAction}
                        chat={this}
                        clientEmail={this.clientEmail}
                        bindingStatus={this.state.bindingStatus}
                        onEmailSubmit={this.handleEmailSubmit}
                        onFormSubmit={this.handleFormSubmit}
                        onResendMessage={this.handleResendMessage}
                        embedBridge={this._ensureEmbedBridge()}
                        embedTheme={this._currentEmbedTheme()}
                        embedMainColor={this.getMessageAreaConf().mainColor || '#2563eb'}
                        embedLang={getLang()}
                    />
                    {/* 客服正在输入提示 —— 与客服气泡同构的小气泡 + 三点跳动 */}
                    {this.state.agentTyping && (
                        <div style={{ display: 'flex', alignItems: 'flex-end', padding: '2px 16px 10px' }}>
                            <div className="typing-indicator" style={{
                                display: 'inline-flex', alignItems: 'center', gap: 'var(--lc-space-1)',
                                background: 'var(--lc-bubble-agent-bg, #f1f5f9)', borderRadius: 'var(--lc-radius-bubble-in)',
                                padding: '11px 14px', boxShadow: 'var(--lc-shadow-1)',
                            }} aria-label={t('chat.typing')}>
                                <span className="dot"></span><span className="dot"></span><span className="dot"></span>
                            </div>
                        </div>
                    )}
                    {/* 聊天空态建议 pill（Crisp 式开口引导）：访客从没发过话时，
                        composer 上方给 3-4 个知识库问题，点击即发送。浅灰底、
                        hover 主色描边（.lc-suggest-pill CSS）。 */}
                    {this.state.suggestPills.length > 0
                        && !this.state.banned && !this.state.chatClosed && !this.state.emailGateLocked
                        && !this.state.messages.some(m => m && m.from === 'visitor') && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            gap: 'var(--lc-space-2)',
                            padding: '8px 16px 10px',
                        }}>
                            {this.state.suggestPills.map((text, i) => (
                                <button
                                    key={i}
                                    className="lc-suggest-pill"
                                    onClick={() => this.handleSuggestPillClick(text)}
                                    style={{
                                        border: 'var(--lc-hairline)',
                                        background: 'var(--lc-surface-2, #f1f5f9)',
                                        color: 'var(--lc-text, #334155)',
                                        fontSize: '13px',
                                        lineHeight: 1.35,
                                        padding: '8px 14px',
                                        borderRadius: 'var(--lc-radius-full)',
                                        cursor: 'pointer',
                                        maxWidth: '100%',
                                        textAlign: 'left',
                                    }}
                                >
                                    {text}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* 「滚动到底部」浮动控件：sticky 钉在滚动容器可视区右下角。
                        - 离底 >120px：纯箭头圆钮
                        - 期间有新客服/AI 消息（unreadCount>0）：升级为计数胶囊
                        - 点击/手动滚回底部：清零并隐藏
                        进出场 opacity+translateY 150ms（transform/opacity only）。 */}
                    {(() => {
                        // 三道闸：距底阈值 + 非加载态 + 初始滚动已落位（防加载期误判）
                        const visible = this.state.farFromBottom
                            && !this.state.loadingHistory
                            && this._scrollWatchReady === true;
                        const count = this.state.unreadCount;
                        const mainColor = 'var(--widget-main-color, #2563eb)';
                        let reducedMotion = false;
                        try { reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
                        const label = count > 0
                            ? (count === 1 ? t('chat.new_msg_one') : t('chat.new_msgs', { count }))
                            : null;
                        return (
                            <div style={{
                                position: 'sticky',
                                bottom: '10px',
                                height: 0,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingRight: 'var(--lc-space-3)',
                                pointerEvents: 'none',
                                zIndex: 120,
                            }}>
                                <button
                                    onClick={this.handleJumpToBottom}
                                    aria-label={label || t('chat.jump_to_latest')}
                                    style={{
                                        pointerEvents: visible ? 'auto' : 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: count > 0 ? '6px' : '0',
                                        border: 'var(--lc-hairline)',
                                        background: 'var(--lc-surface, #ffffff)',
                                        color: count > 0 ? mainColor : 'var(--lc-text-2, #475569)',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        borderRadius: 'var(--lc-radius-full)',
                                        padding: count > 0 ? '8px 12px' : '8px',
                                        boxShadow: 'var(--lc-shadow-2)',
                                        cursor: 'pointer',
                                        opacity: visible ? 1 : 0,
                                        transform: visible ? 'translateY(-100%)' : 'translateY(calc(-100% + 8px))',
                                        transition: reducedMotion ? 'none' : 'opacity var(--lc-dur-fast) var(--lc-ease-out), transform var(--lc-dur-fast) var(--lc-ease-out)',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {label && <span>{label}</span>}
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
                                        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                                    </svg>
                                </button>
                            </div>
                        );
                    })()}
                </div>
                <div ref={this._bindComposer} className={
                    this.isMobile === true ? 'mobile-area':'pc-area'
                }>
                    <div className="toolbar">
                        {/* 左箭头 - 只在有溢出时显示 */}
                        {this.state.showLeftArrow && (
                            <button
                                className="toolbar-scroll-btn toolbar-scroll-left"
                                onClick={() => this.scrollToolbar('left')}
                                title={t('chat.scroll_left')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                                </svg>
                            </button>
                        )}

                        {/* 滚动容器 */}
                        <div className="toolbar-scroll-container" ref={this.toolbarRef}>
                        {/* 音视频通话按钮 - 折叠成一个"通话"触发按钮，点击向右展开语音/视频/共享 */}
                        {(() => {
                            const voiceOn = !this.appConfig || (this.appConfig.enable_voice !== false && this.appConfig.enable_voice !== 0 && this.appConfig.enable_voice !== '0');
                            const videoOn = !this.appConfig || (this.appConfig.enable_video !== false && this.appConfig.enable_video !== 0 && this.appConfig.enable_video !== '0');
                            const screenOn = !this.appConfig || (this.appConfig.enable_remote !== false && this.appConfig.enable_remote !== 0 && this.appConfig.enable_remote !== '0');
                            if (!voiceOn && !videoOn && !screenOn) return null;
                            return (
                                <div className={`call-group ${this.state.showCallActions ? 'expanded' : ''}`}>
                                    {/* 触发按钮用「带信号波的听筒」,和展开后第一项(纯听筒=语音通话)
                                        区分开 —— 之前两者是同一个 path,点开像什么都没发生。 */}
                                    <a onClick={this.toggleCallActions} className={`call-button call-trigger ${this.state.showCallActions ? 'active' : ''}`} title={t('chat.call_menu') || t('chat.voice_call')}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M15.05 5A5 5 0 0 1 19 8.95"/>
                                            <path d="M15.05 1A9 9 0 0 1 23 8.94"/>
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                    </a>
                                    <div className="call-actions">
                                        {voiceOn && (
                                            <a onClick={() => this.runCallAction(this.startAudioCall)} className="call-button" title={t('chat.voice_call')}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {videoOn && (
                                            <a onClick={() => this.runCallAction(this.startVideoCall)} className="call-button" title={t('chat.video_call')}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                                    <polygon points="23 7 16 12 23 17 23 7"/>
                                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                                </svg>
                                            </a>
                                        )}
                                        {screenOn && (
                                            <a onClick={() => this.runCallAction(this.startScreenShare)} className="call-button" title={t('chat.screen_share')}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="4" width="20" height="13" rx="2"/>
                                                    <line x1="8" y1="21" x2="16" y2="21"/>
                                                    <line x1="12" y1="17" x2="12" y2="21"/>
                                                    <path d="M9.5 10.5 12 8l2.5 2.5"/>
                                                    <line x1="12" y1="8" x2="12" y2="13"/>
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        <a ref={this.emojiButtonRef} onClick={this.toggleEmojiPicker} className="emoji-button">
                            {!this.state.showEmojiPicker &&
                                <svg
                                    style={{
                                        width: '24px',
                                        height: '24px'
                                    }}
                                    t="1723988896608" className="icon" viewBox="0 0 1024 1024" version="1.1"
                                     xmlns="http://www.w3.org/2000/svg" p-id="2752" width="200" height="200">
                                    <path
                                        d="M964.296223 320.242651c-24.661679-58.328453-59.975976-110.701265-104.950284-155.675572s-97.347119-80.288604-155.675572-104.960517c-60.395532-25.541723-124.536364-38.496779-190.652178-38.496779s-130.256646 12.955056-190.652178 38.496779c-58.328453 24.671912-110.701265 59.986209-155.675572 104.960517s-80.288604 97.347119-104.950284 155.675572c-25.551956 60.395532-38.496779 124.536364-38.496779 190.652178 0 66.10558 12.944823 130.256646 38.496779 190.641945 24.661679 58.328453 59.975976 110.701265 104.950284 155.675572s97.347119 80.288604 155.675572 104.950284c60.395532 25.551956 124.536364 38.496779 190.652178 38.496779s130.256646-12.944823 190.652178-38.496779c58.328453-24.661679 110.701265-59.975976 155.675572-104.950284s80.288604-97.347119 104.950284-155.675572c25.551956-60.385299 38.507012-124.536364 38.507012-190.641945C1002.803235 444.779016 989.848179 380.638183 964.296223 320.242651zM926.597623 685.593663c-22.604834 53.447283-54.972009 101.450577-96.190783 142.679583-41.229007 41.218774-89.232301 73.585949-142.679583 96.190783-55.330166 23.403013-114.108874 35.263132-174.709067 35.263132-60.600193 0-119.378901-11.860119-174.709067-35.263132-53.447283-22.604834-101.450577-54.972009-142.679583-96.190783-41.218774-41.229007-73.585949-89.232301-96.190783-142.679583-23.403013-55.319933-35.263132-114.098641-35.263132-174.698834 0-60.610426 11.860119-119.389134 35.263132-174.709067 22.604834-53.447283 54.972009-101.450577 96.201016-142.679583 41.218774-41.229007 89.222067-73.596182 142.66935-96.201016 55.330166-23.39278 114.108874-35.263132 174.709067-35.263132s119.378901 11.870352 174.709067 35.263132c53.447283 22.604834 101.450577 54.972009 142.679583 96.201016 41.218774 41.229007 73.585949 89.232301 96.190783 142.679583 23.403013 55.319933 35.273365 114.098641 35.273365 174.709067C961.870987 571.495022 950.000636 630.27373 926.597623 685.593663z"
                                        p-id="2753" fill="#8a8a8a"></path>
                                    <path
                                        d="M794.939047 645.214001c-1.115404 1.565658-27.792996 38.568411-75.509764 75.683726-28.110221 21.868053-57.827033 39.315424-88.301092 51.861158-38.455847 15.84078-78.190827 23.863501-118.110001 23.863501s-79.654154-8.022721-118.110001-23.863501c-30.474059-12.545734-60.190871-29.993105-88.301092-51.861158-47.716768-37.115316-74.394361-74.118068-75.509764-75.683726-6.569626-9.18929-4.441149-21.980617 4.758374-28.550243 9.18929-6.559393 21.960151-4.441149 28.54001 4.737908 0.112564 0.163729 6.364965 8.779967 17.999956 21.540595 11.624758 12.760628 28.652574 29.655414 50.326199 46.396703 25.111934 19.412119 51.513234 34.833343 78.456886 45.854351 33.267685 13.599739 67.527976 20.496823 101.839433 20.496823s68.571748-6.897084 101.839433-20.496823c26.943652-11.021008 53.344952-26.442232 78.467119-45.854351 43.337018-33.482579 68.080561-67.599607 68.326155-67.937299 6.579859-9.168824 19.360953-11.276834 28.54001-4.707209C799.369963 623.274316 801.49844 636.024711 794.939047 645.214001z"
                                        p-id="2754" fill="#8a8a8a"></path>
                                    <path
                                        d="M737.449705 449.680653c0 33.799804-27.424606 61.214177-61.203944 61.214177s-61.203944-27.414373-61.203944-61.214177c0-33.810037 27.424606-61.214177 61.203944-61.214177S737.449705 415.870616 737.449705 449.680653z"
                                        p-id="2755" fill="#8a8a8a"></path>
                                    <path
                                        d="M410.974096 449.680653c0 33.799804-27.424606 61.214177-61.203944 61.214177s-61.203944-27.414373-61.203944-61.214177c0-33.810037 27.424606-61.214177 61.203944-61.214177S410.974096 415.870616 410.974096 449.680653z"
                                        p-id="2756" fill="#8a8a8a"></path>
                                </svg>
                                    }

                            {this.state.showEmojiPicker &&
                                <svg
                                    style={{
                                        width: '24px',
                                        height: '24px'
                                    }}
                                    t="1723988896608" className="icon" viewBox="0 0 1024 1024" version="1.1"
                                 xmlns="http://www.w3.org/2000/svg" p-id="2752" width="200" height="200">
                                <path
                                    d="M964.296223 320.242651c-24.661679-58.328453-59.975976-110.701265-104.950284-155.675572s-97.347119-80.288604-155.675572-104.960517c-60.395532-25.541723-124.536364-38.496779-190.652178-38.496779s-130.256646 12.955056-190.652178 38.496779c-58.328453 24.671912-110.701265 59.986209-155.675572 104.960517s-80.288604 97.347119-104.950284 155.675572c-25.551956 60.395532-38.496779 124.536364-38.496779 190.652178 0 66.10558 12.944823 130.256646 38.496779 190.641945 24.661679 58.328453 59.975976 110.701265 104.950284 155.675572s97.347119 80.288604 155.675572 104.950284c60.395532 25.551956 124.536364 38.496779 190.652178 38.496779s130.256646-12.944823 190.652178-38.496779c58.328453-24.661679 110.701265-59.975976 155.675572-104.950284s80.288604-97.347119 104.950284-155.675572c25.551956-60.385299 38.507012-124.536364 38.507012-190.641945C1002.803235 444.779016 989.848179 380.638183 964.296223 320.242651zM926.597623 685.593663c-22.604834 53.447283-54.972009 101.450577-96.190783 142.679583-41.229007 41.218774-89.232301 73.585949-142.679583 96.190783-55.330166 23.403013-114.108874 35.263132-174.709067 35.263132-60.600193 0-119.378901-11.860119-174.709067-35.263132-53.447283-22.604834-101.450577-54.972009-142.679583-96.190783-41.218774-41.229007-73.585949-89.232301-96.190783-142.679583-23.403013-55.319933-35.263132-114.098641-35.263132-174.698834 0-60.610426 11.860119-119.389134 35.263132-174.709067 22.604834-53.447283 54.972009-101.450577 96.201016-142.679583 41.218774-41.229007 89.222067-73.596182 142.66935-96.201016 55.330166-23.39278 114.108874-35.263132 174.709067-35.263132s119.378901 11.870352 174.709067 35.263132c53.447283 22.604834 101.450577 54.972009 142.679583 96.201016 41.218774 41.229007 73.585949 89.232301 96.190783 142.679583 23.403013 55.319933 35.273365 114.098641 35.273365 174.709067C961.870987 571.495022 950.000636 630.27373 926.597623 685.593663z"
                                    p-id="2753" fill="#1296db"></path>
                                <path
                                    d="M794.939047 645.214001c-1.115404 1.565658-27.792996 38.568411-75.509764 75.683726-28.110221 21.868053-57.827033 39.315424-88.301092 51.861158-38.455847 15.84078-78.190827 23.863501-118.110001 23.863501s-79.654154-8.022721-118.110001-23.863501c-30.474059-12.545734-60.190871-29.993105-88.301092-51.861158-47.716768-37.115316-74.394361-74.118068-75.509764-75.683726-6.569626-9.18929-4.441149-21.980617 4.758374-28.550243 9.18929-6.559393 21.960151-4.441149 28.54001 4.737908 0.112564 0.163729 6.364965 8.779967 17.999956 21.540595 11.624758 12.760628 28.652574 29.655414 50.326199 46.396703 25.111934 19.412119 51.513234 34.833343 78.456886 45.854351 33.267685 13.599739 67.527976 20.496823 101.839433 20.496823s68.571748-6.897084 101.839433-20.496823c26.943652-11.021008 53.344952-26.442232 78.467119-45.854351 43.337018-33.482579 68.080561-67.599607 68.326155-67.937299 6.579859-9.168824 19.360953-11.276834 28.54001-4.707209C799.369963 623.274316 801.49844 636.024711 794.939047 645.214001z"
                                    p-id="2754" fill="#1296db"></path>
                                <path
                                    d="M737.449705 449.680653c0 33.799804-27.424606 61.214177-61.203944 61.214177s-61.203944-27.414373-61.203944-61.214177c0-33.810037 27.424606-61.214177 61.203944-61.214177S737.449705 415.870616 737.449705 449.680653z"
                                    p-id="2755" fill="#1296db"></path>
                                <path
                                    d="M410.974096 449.680653c0 33.799804-27.424606 61.214177-61.203944 61.214177s-61.203944-27.414373-61.203944-61.214177c0-33.810037 27.424606-61.214177 61.203944-61.214177S410.974096 415.870616 410.974096 449.680653z"
                                    p-id="2756" fill="#1296db"></path>
                            </svg>}
                        </a>
                        
                        <a onClick={this.toggleGiphy} className="giphy-button">
                            {!this.state.showGiphy &&
                            <svg
                                style={{
                                    width: '24px',
                                    height: '24px'
                                }}
                                t="1723989019566" className="icon" viewBox="0 0 1024 1024" version="1.1"
                                 xmlns="http://www.w3.org/2000/svg" p-id="4310" width="200" height="200">
                                <path
                                    d="M1024 170.666667v682.666666H0V170.666667h1024zM85.333333 256v512h853.333334V256H85.333333z m354.133334 280.106667c0 82.730667-51.477333 133.333333-136.469334 133.333333-92.501333 0-148.117333-59.776-148.117333-158.72 0-97.706667 55.829333-158.144 146.026667-158.144 73.344 0 127.936 42.709333 135.424 106.24h-75.413334c-7.509333-26.453333-29.994667-42.474667-60.010666-42.474667-42.688 0-68.117333 34.986667-68.117334 93.525334 0 59.584 28.117333 95.850667 71.893334 95.850666 34.773333 0 58.944-20.416 60.8-50.837333l0.213333-5.013333h-54.997333v-52.48H439.466667v38.741333zM573.013333 661.333333h-76.48V360.704h76.458667V661.333333z m147.285334 0H643.84V360.704h204.586667v61.461333h-128.128v68.544h116.245333v58.538667h-116.266667V661.333333z"
                                    fill="currentColor" p-id="4311"></path>
                            </svg>}
                            {this.state.showGiphy &&
                            <svg
                                style={{
                                    width: '24px',
                                    height: '24px'
                                }}
                                t="1723988568469" className="icon" viewBox="0 0 1024 1024" version="1.1"
                                 xmlns="http://www.w3.org/2000/svg" p-id="1516" width="200" height="200">
                                <path
                                    d="M1024 170.666667v682.666666H0V170.666667h1024zM85.333333 256v512h853.333334V256H85.333333z m354.133334 280.106667c0 82.730667-51.477333 133.333333-136.469334 133.333333-92.501333 0-148.117333-59.776-148.117333-158.72 0-97.706667 55.829333-158.144 146.026667-158.144 73.344 0 127.936 42.709333 135.424 106.24h-75.413334c-7.509333-26.453333-29.994667-42.474667-60.010666-42.474667-42.688 0-68.117333 34.986667-68.117334 93.525334 0 59.584 28.117333 95.850667 71.893334 95.850666 34.773333 0 58.944-20.416 60.8-50.837333l0.213333-5.013333h-54.997333v-52.48H439.466667v38.741333zM573.013333 661.333333h-76.48V360.704h76.458667V661.333333z m147.285334 0H643.84V360.704h204.586667v61.461333h-128.128v68.544h116.245333v58.538667h-116.266667V661.333333z"
                                    fill="#1296db" p-id="1517"></path>
                            </svg>}
                        </a>

                            {imageOrUploadComponent}

                        {/* 语音消息录制入口——录音/预览态时隐藏，composer 已经切成录音条 */}
                        {!this.state.banned && !this.state.chatClosed && !this.state.emailGateLocked
                            && !this.state.voiceRecording && !this.state.voicePreview && (
                            <a onClick={this.startVoiceRecording} className="mic-button" title={t('voice.record')} aria-label={t('voice.record')}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="2" width="6" height="12" rx="3"/>
                                    <path d="M5 10a7 7 0 0 0 14 0"/>
                                    <line x1="12" y1="19" x2="12" y2="22"/>
                                    <line x1="8" y1="22" x2="16" y2="22"/>
                                </svg>
                            </a>
                        )}

                        {this.state.uploading &&
                            <div className="progress-bar-container">
                                <div className="progress-bar" style={{ width: `${this.state.uploadProgress}%` }}>
                                    {this.state.uploadProgress}%
                                </div>
                            </div>
                        }
                        </div>

                        {/* 右箭头 - 只在有溢出时显示 */}
                        {this.state.showRightArrow && (
                            <button
                                className="toolbar-scroll-btn toolbar-scroll-right"
                                onClick={() => this.scrollToolbar('right')}
                                title={t('chat.scroll_right')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                                </svg>
                            </button>
                        )}
                    </div>
                    {/* required ask_email 未提交：锁定输入区提示条 */}
                    {!this.state.banned && this.state.emailGateLocked && (
                        <div className="email-gate-bar" style={{
                            padding: 'var(--lc-space-2) var(--lc-space-4)',
                            fontSize: '13px',
                            color: 'var(--lc-text-2, #475569)',
                            background: 'var(--lc-bg, #f8fafc)',
                            borderTop: 'var(--lc-hairline)',
                            textAlign: 'center',
                        }}>
                            {t('chat.email_gate_notice')}
                        </div>
                    )}
                    {/* 会话已关闭提示条。
                        `chatClosed` 此前是**只写不读**:四处写入、零处读取 ——
                        客服关闭会话后,访客这边输入框照常可用,发出去的消息进入
                        一个已关闭的会话。iOS 早就锁了(`locked:` 里带 chatClosed),
                        web 与 Android 没锁,三端不一致。
                        锁必须配一条出路:只锁不给路是把访客困住,所以这里复用了
                        widget-index.js 里那段一直没被渲染过的 `.chat-closed-banner`
                        样式 —— 它自带一个按钮,说明设计上本来就是"提示 + 开新会话"。 */}
                    {this.state.chatClosed && !this.state.banned && (
                        <div className="chat-closed-banner">
                            <span>{t('chat.closed_notice')}</span>
                            <button onClick={this.createNewChat}>{t('chat.new_chat')}</button>
                        </div>
                    )}
                    {/* 封禁提示条 */}
                    {this.state.banned && (
                        <div className="banned-bar" style={{
                            padding: 'var(--lc-space-2) var(--lc-space-4)',
                            fontSize: '13px',
                            color: '#b91c1c',
                            background: '#fef2f2',
                            borderTop: '1px solid #fecaca',
                            textAlign: 'center',
                        }}>
                            {t('chat.banned_notice')}
                        </div>
                    )}
                    {/* 原生容器里麦克风/摄像头权限被拒——通话发起前的预检结果 */}
                    {!this.state.banned && !this.state.emailGateLocked && this.state.permissionGuidance && (
                        <PermissionGuidanceBar kind={this.state.permissionGuidance.kind} />
                    )}
                    {(this.state.voiceRecording || this.state.voicePreview) ? (
                        <div className="msg-input lc-voice-composer" style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', padding: '0 14px',
                        }}>
                            {this.state.voiceRecording && (
                                <>
                                    <button type="button" onClick={this.cancelVoiceRecording}
                                        aria-label={t('voice.cancel')} title={t('voice.cancel')}
                                        style={{
                                            flexShrink: 0, width: '30px', height: '30px', borderRadius: 'var(--lc-radius-full)',
                                            border: 'none', background: 'transparent', color: 'var(--lc-text-2, #475569)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                    </button>
                                    <span className="lc-voice-rec-dot" aria-hidden="true" style={{
                                        flexShrink: 0, width: '10px', height: '10px', borderRadius: 'var(--lc-radius-full)',
                                        background: '#ef4444', animation: 'lcVoiceRecPulse 1.4s ease-in-out infinite',
                                    }} />
                                    <span style={{ flex: 1, fontVariantNumeric: 'tabular-nums', color: 'var(--lc-text, #1e293b)', fontSize: '14px' }}>
                                        {formatVoiceDuration(this.state.voiceRecording.seconds)}
                                    </span>
                                    <button type="button" onClick={this.stopVoiceRecording}
                                        aria-label={t('voice.stop')} title={t('voice.stop')}
                                        style={{
                                            flexShrink: 0, width: '32px', height: '32px', borderRadius: 'var(--lc-radius-full)',
                                            border: 'none', background: 'var(--widget-main-color, #2563eb)', color: '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
                                    </button>
                                </>
                            )}
                            {this.state.voicePreview && (
                                <>
                                    <button type="button" onClick={this.discardVoicePreview}
                                        aria-label={t('voice.discard')} title={t('voice.discard')}
                                        disabled={this.state.voiceSending}
                                        style={{
                                            flexShrink: 0, width: '30px', height: '30px', borderRadius: 'var(--lc-radius-full)',
                                            border: 'none', background: 'transparent', color: 'var(--lc-text-2, #475569)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: this.state.voiceSending ? 'not-allowed' : 'pointer', padding: 0,
                                            opacity: this.state.voiceSending ? 0.5 : 1,
                                        }}
                                    >
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                    </button>
                                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                        <VoicePlayer src={this.state.voicePreview.url} durationHint={this.state.voicePreview.seconds} compact />
                                    </div>
                                    <button type="button" onClick={this.sendVoicePreview}
                                        aria-label={t('voice.send')} title={t('voice.send')}
                                        disabled={this.state.voiceSending}
                                        style={{
                                            flexShrink: 0, width: '32px', height: '32px', borderRadius: 'var(--lc-radius-full)',
                                            border: 'none', background: 'var(--widget-main-color, #2563eb)', color: '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: this.state.voiceSending ? 'not-allowed' : 'pointer', padding: 0,
                                            opacity: this.state.voiceSending ? 0.7 : 1,
                                        }}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" stroke="none"/></svg>
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                    <div className="msg-input"
                         ref={(el) => { this.texContainerRef.current = el; }}
                    >
                        <textarea
                            rows={1}
                                            className="textarea"
                            /* type 是 <input> 的属性，放在 <textarea> 上是无效的 —— 删掉。
                               留着可能在喂 Edge/Android 的自动填充启发式，让它把这个框
                               当成普通文本输入框，从而在键盘上方多插一条填充建议栏
                               （那条栏是浏览器 chrome，会占掉约 89px 高度）。 */
                            // 聊天输入框绝不能触发浏览器自动填充。
                            //
                            // 没有这几个属性时，Edge/Chrome Android 把它当作可能需要
                            // 填充的字段，在键盘上方额外弹一条「密码/银行卡/身份」建议栏
                            // （实测约 65px）。那条栏是浏览器 chrome 的一部分：
                            // env(keyboard-inset-height) 只报键盘本体（287），不含它，
                            // 于是任何按键盘高度算面板的方案都会短一截 —— composer 和
                            // 键盘之间那道怎么都消不掉的空隙就是它。
                            // 用户实测佐证：点输入框时浏览器底部黑区变大，而访问其它
                            // 网站不会。
                            //
                            // data-form-type / data-lpignore 是给 1Password、LastPass
                            // 这类扩展看的（它们不认 autocomplete="off"）。
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="sentences"
                            spellCheck={false}
                            data-form-type="other"
                            data-lpignore="true"
                            placeholder={this.state.banned ? t('chat.banned_placeholder') : (this.state.emailGateLocked ? t('chat.email_gate_placeholder') : this._composerPlaceholder())}
                            disabled={this.state.banned || this.state.emailGateLocked}
                            style={{
                                flex: 1,
                                // 高度由 adjustTextareaHeight 按真实 scrollHeight 设置，React 不再插手
                                ...((this.state.banned || this.state.emailGateLocked) ? { background: 'var(--lc-bg, #f8fafc)', cursor: 'not-allowed' } : {})
                            }}
                            ref={(input) => {
                                this.input = input;
                                this.textareaRef.current = input;
                            }}
                            onKeyDown={this.handleKeyDown}
                            onInput={(e) => {
                                this.handleInputChange(e);
                            }}
                        />
                        {this.state.showSendButton && !this.state.banned && !this.state.emailGateLocked &&
                            <div className="send-container" onClick={this.simulateEnterPress}>
                                <span className="send-btn" tabIndex="0" role="button"
                                                            aria-label="Send">
                                    <span
                                className="send-img"></span></span>
                            </div>
                                        }
                    </div>
                    )}
                    {/* 显示回复提示 */}
                    {this.state.replyToMessage && (
                        <div className="reply-bar" onClick={this.scrollToReplyMessage}>
                            <div className="reply-bar-content">
                                <div className="reply-bar-icon" aria-label="reply">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 17 4 12 9 7"/>
                                        <path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
                                    </svg>
                                </div>
                                <div className="reply-bar-text">
                                    <div className="reply-bar-from">
                                        {this.state.replyToMessage.from === 'visitor' ? t('chat.reply_self') : t('chat.reply_agent')}
                                    </div>
                                    <div className="reply-bar-message">
                                        {this.state.replyToMessage.text || t('chat.reply_image_placeholder')}
                                    </div>
                                </div>
                                <button className="reply-bar-cancel" onClick={(e) => {
                                    e.stopPropagation();
                                    this.cancelReply();
                                }}><LcIcon name="close" size={12} /></button>
                            </div>
                        </div>
                    )}
                    {/* "Powered by Lane.Chat" — free plans always; paid plans may hide (config.show_branding). */}
                    {this.state.appConfig?.show_branding !== false && (
                        <a
                            className="lc-powered-by"
                            href={this._poweredByHref()}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                if (nativeBridge.isActive() && nativeBridge.supports('openUrl')) {
                                    e.preventDefault();
                                    openLink(this._poweredByHref(), { source: 'powered_by' });
                                }
                            }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--lc-space-1)', fontSize: '11px', color: 'var(--lc-text-3, #9ca3af)', textDecoration: 'none', padding: '6px 0 0', margin: 0, flexShrink: 0, order: 99 }}
                        >
                            <span>{t('chat.powered_by')}</span>
                            <svg width="13" height="13" viewBox="0 0 64 64" style={{ display: 'block' }} aria-hidden="true">
                                <rect width="64" height="64" rx="14" fill="#E85F3A" />
                                <rect x="16" y="10" width="10" height="38" rx="2" fill="#FFF6EA" />
                                <circle cx="42" cy="42" r="7" fill="#FFF6EA" opacity="0.9" />
                            </svg>
                            <span style={{ fontWeight: 600, color: 'var(--lc-text-2, #6b7280)' }}>Lane.Chat</span>
                        </a>
                    )}
                </div>
                </div>
            </div>
        );
    }

    simulateEnterPress = () => {
        const event = { key: 'Enter', shiftKey: false, preventDefault: () => {} };
        this.handleKeyDown(event);
    }

    uploadImage = async (file) => {
        try {
            const uploadResult = await ApiService.uploadFile(
                file,
                this.props.appId,
                this.state.chat_id, // 传递 chat_id
                (progress) => {
                    this.setState({ uploadProgress: Math.round(progress) });
                }
            );

            if (uploadResult.code === 0) {
                return uploadResult.data.url;
            } else {
                throw new Error(uploadResult.msg || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload failed:', error);
            throw error;
        }
    }

    // 发送消息的统一方法
    sendMessage = async (text = '', attachment = null) => {
        if (!text && !attachment) return;
        if (this.state.banned) return; // 被封禁后禁止发送
        if (this.state.emailGateLocked) return; // required ask_email 未提交前禁止发送

        // 发消息即视为停止输入，立即通知客服清除"正在输入"
        this.stopTyping();

        // 「用户刚自己发了消息」是自动跟随到底的唯一一个非贴底放行条件。
        // 在写入临时气泡之前 arm，writeToMessages / ack 回调都会认这张一次性通行证。
        this._armFollowOnce();

        // 先添加到本地消息列表，提供即时反馈
        const tempId = 'temp-' + Date.now();

        const message = {
            type: 'message',
            text: text || '',
            from: 'visitor',
            attachment: attachment,
            appId: this.props.appId,
            userId: this.client_id || this.fp_visitor,
            chatId: this.chat_id,
            clientId: this.client_id,
            visitorName: this.props.conf?.visitorName,
            reply_to_id: this.state.replyToMessage ? this.state.replyToMessage.id : null,
            timestamp: new Date().toISOString(),
            // client_temp_id 若被服务端原样存回 metadata 并广播回来，ack 就能精确认领
            // 这一条临时气泡，而不是"随便清掉所有 sending 态"（那样会把并发中的另一条
            // 临时消息一起清掉，见 handleWebSocketMessage 里的说明）。
            metadata: { client_temp_id: tempId },
        };

        const tempMessage = {
            ...message,
            id: tempId,
            status: 'sending',
            time: message.timestamp
        };

        // 如果是回复消息，添加 reply_to 数据以便立即渲染
        if (this.state.replyToMessage) {
            tempMessage.type = 'reply';
            tempMessage.reply_to = {
                id: this.state.replyToMessage.id,
                content: this.state.replyToMessage.text,
                from_user: this.state.replyToMessage.from,
                attachment: this.state.replyToMessage.attachment
            };
        }

        this.writeToMessages(tempMessage);

        // 使用WebSocketService发送
        const sent = WebSocketService.send(message);

        if (!sent) {
            // 连接断开，WebSocketService 已经将消息加入队列
            debugLog('WebSocket not ready, message queued by WebSocketService');

            // 记录待发送消息数量（用于UI状态）
            this.messageQueue.push(JSON.stringify(message));

            // 之前这里只是静默排队——WS 一直连不上时,气泡永远停在"发送中",和真的
            // 发出去了没有任何区别。直接标成 failed,给访客一个可点重发的入口。
            this._markMessageFailed(tempId);

            // 尝试重新连接
            if (!this.isReconnecting && !this.isConnecting) {
                debugLog('Initiating reconnection...');
                this.connectWebSocket();
            }
        } else {
            // 消息已发送，清空队列计数
            this.messageQueue = [];
            // send() 返回 true 只代表"帧已经写进 socket",不代表服务端已经收到并广播
            // ack。网络抖动/丢帧时 ack 可能永远不来,消息就会永久停在"发送中"却没有
            // 任何提示。这里定个兜底超时,到点还没被 ack 替换掉就转 failed。
            this._armSendTimeout(tempId);

            // 总线事件 message_sent —— fire-and-forget,和 teaser/preview 一样不等
            // 服务端 ack(ack 到达时会走 _enqueuePaced 的 message_received,那是"对方
            // 收到"的语义,这里只是"访客点了发送")。
            this._postToWidget({
                type: 'lane_msg_sent',
                message_type: attachment ? (attachment.type || 'attachment') : 'text',
                excerpt: String(text || '').slice(0, 80),
            });
        }

        // 清理状态
        this.setState({
            replyToMessage: null,
            unreadCount: 0
        });

        return tempId;
    };

    // 把某条临时(sending)消息标成 failed，驱动 message-area.js 里的可点重发提示。
    _markMessageFailed = (tempId) => {
        this.setState(prevState => ({
            messages: prevState.messages.map(m => (m.id === tempId ? { ...m, status: 'failed' } : m))
        }));
    };

    // ack 迟迟不来的兜底：到点了这条消息还停在 sending 态就判定失败，而不是让它
    // 无限期停在"发送中"。ack 到达时会把这条 temp 消息整体替换/移除掉（见
    // handleWebSocketMessage），所以到点先检查是否还在 sending，避免误伤已成功的消息。
    _armSendTimeout = (tempId) => {
        setTimeout(() => {
            if (!this._isMounted) return;
            this.setState(prevState => {
                const stillSending = prevState.messages.some(m => m.id === tempId && m.status === 'sending');
                if (!stillSending) return {};
                return { messages: prevState.messages.map(m => (m.id === tempId ? { ...m, status: 'failed' } : m)) };
            });
        }, 8000);
    };

    // 失败气泡点击重发：复用原文本/附件重新走一遍 sendMessage，并先移除旧的失败
    // 气泡（避免重发成功后界面里同时留着一条 failed 和一条新的 sending/成功消息）。
    handleResendMessage = (message) => {
        if (!message) return;
        this.setState(prevState => ({
            messages: prevState.messages.filter(m => m.id !== message.id)
        }), () => {
            this.sendMessage(message.text || '', message.attachment || null);
        });
    };

    handleKeyDown = async (e) => {
        if (this.state.banned) return; // 被封禁后禁止发送
        if (this.state.emailGateLocked) return; // required ask_email 未提交前禁止发送
        // !e.isComposing / keyCode!==229:输入法(中文/日文等)组字中按回车是"选词/确认候选",
        // 不应触发发送。漏判会导致中文用户打字时回车把半成品消息发出去。
        if (this.input && e.key === 'Enter' && !e.shiftKey && !e.isComposing && e.keyCode !== 229 && !this.state.uploading && (this.input.value || this.state.imageFile)) {
            e.preventDefault();
            let text = this.input.value;
            
            // 检查是否是命令
            if (text && text.startsWith('/')) {
                const command = text.trim().toLowerCase();
                
                if (command === '/email') {
                    // 统一走通知渠道卡片（原来弹的是另一张风格不同的 EmailBinding 卡）
                    this.showNotificationCard(['email']);
                    this.input.value = '';
                    return;
                }
                // 其他命令继续通过WebSocket发送
            }
            
            let uploadResult;
            if (this.state.imagePreviewUrl) {
                try {
                    this.setState({ uploading: true });
                    const file = this.state.imageFile;
                    uploadResult = await this.uploadImage(file);
                } catch (error) {
                    console.error('Failed to upload image:', error);
                    this.setState({ uploading: false });
                    // 之前这里静默失败——选了图、点了发送，界面什么反应都没有。
                    // 给一条看得懂的系统提示，图片预览留着方便直接重试。
                    this.writeToMessages({ from: 'system', type: 'system', text: t('chat.image_upload_failed'), time: new Date() });
                    return;
                }
            }

            // 使用统一的sendMessage方法发送消息
            await this.sendMessage(text, uploadResult);

            this.input.value = '';
            this.setState({
                showEmojiPicker: false,
                showGiphy: false,
                replyToMessage: null,
                showSendButton: false,
                unreadCount: 0
            }, () => {
                this.adjustTextareaHeight();
            });
            this.setState({ uploading: false, uploadProgress: 0, imagePreviewUrl: null, imageFile: null });

            // 自动回复逻辑已移至后端WebSocket处理
        }
    }

    incomingMessage = (msg) => {
        this.writeToMessages(msg);
        // 提示音统一由外层 widget.js 处理（lane_msg_received → 面板关闭且
        // 开关打开时用 Web Audio 响一声）。旧的 #messageSound 元素从不存在，
        // 且 closed shadow 里 getElementById 也够不到 —— 死代码已移除。
    }

    writeToMessages = (msg) => {
        msg.time = new Date();
        if (msg.type === 'init' || msg.type === 'register') {
            return;
        }
        
        // 处理服务消息 - 不要return，让消息能够被添加到列表中
        if (msg.type === 'service' && msg.service_data) {
            // 处理特定的服务动作，但仍然添加消息到列表
            this.handleServiceMessage(msg);
        }
        
        // 处理绑定/评分消息类型（不需要text字段）
        if (msg.type === 'binding' && msg.binding_data) {
            // 绑定消息不需要text字段
        } else if (msg.type === 'rating') {
            // 评分卡片消息不需要text字段
        } else if (msg.type === 'service' && msg.service_data?.type === 'rate_chat') {
            // 评分服务消息不需要text字段
        } else if (msg.type === 'card' && msg.cardType) {
            // 卡片消息（email-binding / service-invitation）正文在卡片里，没有 text。
            // 少了这一条，下面的 !msg.text 会把整条消息 return 掉 —— 卡片永远进不了
            // 列表，点按钮"没反应"的真正原因。
        } else if (!msg.text && !msg.attachment) {
            return;
        }
        
        this.setState(prevState => {
            // 如果收到visitor的消息，移除临时状态消息
            if (msg.from === 'visitor') {
                const filteredMessages = prevState.messages.filter(m => m.type !== 'temp-status');
                return { messages: [...filteredMessages, msg] };
            }
            return { messages: [...prevState.messages, msg] };
        });

        // 不要存储临时消息到localStorage
        if (store.enabled && msg.type !== 'temp-status' && msg.type !== 'connection-status') {
            try {
                store.transact(this.messagesKey, function (messages) {
                    messages.push(msg);
                });
            } catch (e) {
                debugLog('failed to add new message to local storage', e);
                store.set(this.messagesKey, []);
            }
        }
        // 滚动裁决走统一闸门：用户自己发的消息 arm 一次性放行，其余一律"贴底才跟随"。
        if (msg.from === 'visitor') this._armFollowOnce();

        requestAnimationFrame(() => {
            if (this._maybeAutoScroll()) return;
            // 用户正在看历史 → 不动 scrollTop，只累加未读（驱动「回到底部」计数胶囊）
            // 之前这里的条件是 msg.from !== 'system' —— 本地插入的交互卡片
            // （通知渠道卡 / 绑定卡 / 邮箱卡）from 全是 'system'，于是整类卡片
            // 插进对话流却从不累加未读，访客滚在历史里时底部「n 条新消息」不亮，
            // 完全不知道有东西出现了。改成按「消息类型」判定：只有瞬时状态条和
            // 本地系统提示（都是访客自己动作的回声）不计数，交互卡片计数。
            const SILENT_TYPES = ['temp-status', 'connection-status', 'system'];
            if (SILENT_TYPES.indexOf(msg.type) === -1) {
                this.setState(prevState => ({
                    unreadCount: prevState.unreadCount + 1
                }));
            }
        });
    }
    
    handleServiceMessage = async (msg) => {
        const { service_data } = msg;
        
        if (!service_data || !service_data.action) {
            return;
        }
        
        switch (service_data.action) {
            case 'binding_invitation':
                await this.handleBindingInvitation(service_data);
                break;
            default:
                debugLog('Unknown service action:', service_data.action);
        }
    }
    
    handleBindingInvitation = async (data) => {
        const { binding_type, message } = data;
        
        // 显示服务消息
        const serviceMessage = {
            id: 'service-' + Date.now(),
            from: 'system',
            text: message,
            type: 'card',
            cardType: 'service-invitation',
            serviceData: data,
            time: new Date()
        };
        
        // 添加到消息列表
        this.setState(prevState => ({
            messages: [...prevState.messages, serviceMessage]
        }));
        
        // 三种绑定类型统一收敛到同一张 NotificationCard，不再各弹各的卡。
        const METHOD_OF = { email: 'email', tg: 'telegram', telegram: 'telegram', web: 'webpush', webpush: 'webpush' };
        const method = METHOD_OF[binding_type];
        if (method) setTimeout(() => this.showNotificationCard([method]), 500);

        this._maybeAutoScroll();
    }
}
